import { createContext, useContext, useState, useEffect } from 'react';
import { extraTranslations } from './extraTranslations';

// Available languages with their display names and flags
export const availableLanguages = [
  { code: 'en', name: 'English', flag: '🇺🇸', nativeName: 'English' },
  { code: 'es', name: 'Spanish', flag: '🇪🇸', nativeName: 'Español' },
  { code: 'fr', name: 'French', flag: '🇫🇷', nativeName: 'Français' },
  { code: 'pt', name: 'Portuguese', flag: '🇧🇷', nativeName: 'Português' },
  { code: 'de', name: 'German', flag: '🇩🇪', nativeName: 'Deutsch' },
  { code: 'it', name: 'Italian', flag: '🇮🇹', nativeName: 'Italiano' },
  { code: 'zh', name: 'Chinese', flag: '🇨🇳', nativeName: '中文' },
];

const translations = {
  en: {
    // Navigation
    dashboard: 'Dashboard',
    planner: 'Lesson Planner',
    attendance: 'Attendance',
    gradebook: 'Gradebook',
    classes: 'Classes',
    settings: 'Settings',
    logout: 'Logout',
    
    // Auth
    login: 'Login',
    register: 'Register',
    email: 'Email',
    password: 'Password',
    name: 'Name',
    signInWithGoogle: 'Sign in with Google',
    orContinueWith: 'or continue with',
    dontHaveAccount: "Don't have an account?",
    alreadyHaveAccount: 'Already have an account?',
    
    // Dashboard
    welcome: 'Welcome',
    todayOverview: "Today's Overview",
    quickActions: 'Quick Actions',
    attendancePending: 'Attendance Pending',
    upcomingAssignments: 'Upcoming Assignments',
    recentPlans: 'Recent Plans',
    totalClasses: 'Total Classes',
    totalStudents: 'Total Students',
    totalPlans: 'Total Plans',
    
    // Classes
    addClass: 'Add Class',
    className: 'Class Name',
    grade: 'Grade',
    section: 'Section',
    subject: 'Subject',
    yearTerm: 'Year/Term',
    students: 'Students',
    addStudent: 'Add Student',
    firstName: 'First Name',
    lastName: 'Last Name',
    studentNumber: 'Student Number',
    parentEmail: 'Parent Email',
    notes: 'Notes',
    accommodations: 'Accommodations',
    
    // Planner
    weeklyPlan: 'Weekly Plan',
    createPlan: 'Create Plan',
    weekOf: 'Week of',
    unit: 'Unit',
    story: 'Story/Title',
    objectiveOfWeek: 'Objective of the Week',
    skillsOfWeek: 'Skills of the Week',
    dayTheme: 'Day Theme',
    activities: 'Activities',
    materials: 'Materials',
    standards: 'Standards',
    expectations: 'Expectations',
    firstWeek: 'First Week',
    secondWeek: 'Second Week',
    saveAsTemplate: 'Save as Template',
    duplicateLastWeek: 'Duplicate Last Week',
    exportPdf: 'Export PDF',
    
    // Taxonomy levels
    level1Memory: 'Level 1: Memory',
    level2Processing: 'Level 2: Processing',
    level3Strategic: 'Level 3: Strategic Thinking',
    level4Extended: 'Level 4: Extended Thinking',
    
    // Activities
    brainstorming: 'Brainstorming',
    buildingBackground: 'Building Background',
    vocabularyDevelopment: 'Vocabulary Development',
    readPages: 'Read Pages',
    guidedReading: 'Guided & Choral Reading',
    oralQuestions: 'Oral Questions',
    comprehensionQuestions: 'Comprehension Questions',
    exercisePractice: 'Exercise Practice',
    other: 'Other',
    
    // Materials
    book: 'Book',
    notebook: 'Notebook',
    teachersGuide: "Teacher's Guide",
    testQuiz: 'Test/Quiz',
    dictionary: 'Dictionary',
    handouts: 'Handouts',
    
    // Standards domains
    listeningAndSpeaking: 'Listening/Speaking',
    foundationalSkills: 'Foundational Skills',
    reading: 'Reading',
    writing: 'Writing',
    language: 'Language',
    
    // Subject integration
    mathematics: 'Mathematics',
    spanish: 'Spanish',
    socialStudies: 'Social Studies',
    science: 'Science',
    health: 'Health',
    art: 'Art',
    physicalEducation: 'Physical Education',
    religion: 'Religion',
    
    // Attendance
    takeAttendance: 'Take Attendance',
    present: 'Present',
    absent: 'Absent',
    tardy: 'Tardy',
    excused: 'Excused',
    markAllPresent: 'Mark All Present',
    submitAttendance: 'Submit Attendance',
    attendanceReport: 'Attendance Report',
    
    // Gradebook
    assignments: 'Assignments',
    createAssignment: 'Create Assignment',
    title: 'Title',
    description: 'Description',
    points: 'Points',
    dueDate: 'Due Date',
    category: 'Category',
    homework: 'Homework',
    quiz: 'Quiz',
    test: 'Test',
    project: 'Project',
    gradeEntry: 'Grade Entry',
    missing: 'Missing',
    late: 'Late',
    
    // Common
    save: 'Save',
    cancel: 'Cancel',
    delete: 'Delete',
    edit: 'Edit',
    view: 'View',
    search: 'Search',
    filter: 'Filter',
    loading: 'Loading...',
    noData: 'No data available',
    confirmDelete: 'Are you sure you want to delete this?',
    success: 'Success',
    error: 'Error',
    from: 'From',
    to: 'To',
    date: 'Date',
    teacher: 'Teacher',
    selectLanguage: 'Select Language',
    
    // Days
    monday: 'Monday',
    tuesday: 'Tuesday',
    wednesday: 'Wednesday',
    thursday: 'Thursday',
    friday: 'Friday',
    
    // Games
    educationalGames: 'Educational Games',
    myGames: 'My Games',
    createGame: 'Create Game',
    play: 'Play',
    copyLink: 'Copy Link',
    shareToClassroom: 'Share to Classroom',
    deleteGame: 'Delete Game',
    questions: 'Questions',
    gameType: 'Game Type',
    gradeLevel: 'Grade Level',
    
    // Landing Page
    landingHeroTitle: 'Your complete classroom in one platform',
    landingHeroSubtitle: 'Lesson planning, attendance, gradebook, and AI assistant. Everything a teacher needs, in one place.',
    landingBuiltForTeachers: 'Built for teachers',
    landingStartFree: 'Start free',
    landingViewPricing: 'View pricing',
    landingUsedBySchools: 'Used by schools in Puerto Rico and beyond',
    landingEverythingYouNeed: 'Everything you need',
    landingEverythingDesc: 'A complete platform designed specifically for teachers.',
    landingFeatures: 'Features',
    landingAiAssistant: 'AI Assistant',
    landingPoweredBy: 'Powered by Anthropic',
    landingActiveTeachers: 'active teachers',
    landingHomeschool: 'Homeschool',
    landingIntegrations: 'Integrations',
    landingCustomization: 'Customization',
    landingPricing: 'Pricing',
    landingGetStarted: 'Get Started - Log In',
    landingStart: 'Start',
    landingAiLessonPlan: 'AI Lesson Plan',
    landingPresentations: 'Presentations',
    landingGrades: 'Grades',
    landingPlanFaster: 'Plan faster',
    landingPlanFasterDesc: 'Create weekly lesson plans in seconds with our intuitive format.',
    landingWeek1: 'Week 1',
    landingYourBrand: 'Your brand, your style',
    landingYourBrandDesc: "Customize with your school's logo and colors on all documents.",
    landingYourSchool: 'Your School',
    landingCustomBranding: 'Custom branding',
    landingExportPro: 'Export professionally',
    landingExportProDesc: 'Generate PDFs ready to print: plans, report cards, substitute packets.',
    landingDownloadPdf: 'Download PDF',
    landingAiAssistantTitle: 'AI Assistant',
    landingAiAssistantDesc: 'Generate lesson plans, activities, questions and more with AI.',
    landingLessonPlans: 'Lesson plans',
    landingQuizzesTests: 'Quizzes & tests',
    landingDigitalClassroom: 'Your digital classroom',
    landingTryFree: 'Try free for 30 days',
    landingNoCard: 'No credit card required',
    landingFullName: 'Full Name',
    landingCreateAccount: 'Create Free Account',
    landingOrLoginWith: 'Or login with',
    landingHaveAccount: 'Have an account?',
    landingLoginHere: 'Login here',
    landingNeedAccount: 'Need account?',
    landingRegisterHere: 'Register here',
    // AI Section
    landingAiTeachingAssistant: 'AI Teaching Assistant',
    landingYouAsked: 'You asked:',
    landingAiResponse: 'AI Response:',
    landingAiQuestion: 'How can I teach equivalent fractions to 4th grade?',
    landingAiAnswer: "Here's a lesson plan based on CCSS.MATH.4.NF.A.1 standard with hands-on activities...",
    landingQuizzes: 'Quizzes',
    landingWorksheets: 'Worksheets',
    landingIdeas: 'Ideas',
    landingNewAi: 'New: Artificial Intelligence',
    landingAiAssistantToTeach: 'Your AI assistant to teach better',
    landingAiAssistantDesc2: 'Generate lesson plans, quizzes, summaries, and activities based on Common Core and Puerto Rico standards, instantly.',
    landingAlignedStandards: 'Aligned with Common Core & PR CORE',
    landingGenerateSeconds: 'Generate content in seconds',
    landingBilingual: 'Bilingual: Spanish & English',
    landingPoweredBy: 'Powered by Claude Sonnet 4.5',
    landingTryItFree: 'Try it free',
    landingIncludedSubscription: 'Included in subscription',
    // Integrations Section
    landingConnectTools: 'Connect with your favorite tools',
    landingConnectToolsDesc: 'Integrate with platforms you already use to make your workflow even easier.',
    landingCalendarSync: 'Calendar Sync',
    landingCalendarSyncDesc: 'Export your lesson plans to Google Calendar, Outlook, or Apple Calendar.',
    landingGoogleClassroom: 'Google Classroom',
    landingGoogleClassroomDesc: 'Share activities and games directly with your students.',
    landingPdfExport: 'PDF Export',
    landingPdfExportDesc: 'Generate professional PDFs for plans, report cards, and more.',
    landingEmailNotifications: 'Email Notifications',
    landingEmailNotificationsDesc: 'Send automatic reports to parents and administrators.',
    landingViewIntegrations: 'View all integrations',
    // Customization Section
    landingYourBrandYourWay: 'Your brand, your way',
    landingCustomizeDesc: 'Personalize everything with your school colors, logo, and preferences.',
    landingSchoolLogo: 'School Logo',
    landingSchoolLogoDesc: 'Add your logo to all documents and reports.',
    landingColorThemes: 'Color Themes',
    landingColorThemesDesc: 'Choose colors that match your school identity.',
    landingCustomTemplates: 'Custom Templates',
    landingCustomTemplatesDesc: 'Create reusable templates for your plans.',
    landingMultiLanguage: 'Multi-Language',
    landingMultiLanguageDesc: 'Work in English, Spanish, or both languages.',
    // Homeschool Section
    landingHomeschoolTitle: 'Homeschool Education',
    landingHomeschoolSubtitle: 'Your complete homeschool companion',
    landingHomeschoolDesc: 'Everything you need for a successful homeschool experience in one integrated platform.',
    landingAdaptiveLearning: 'Adaptive Learning',
    landingAdaptiveLearningDesc: 'Learning paths that adapt to each child\'s pace.',
    landingFullVisibility: 'Full Visibility',
    landingFullVisibilityDesc: 'Track progress in real-time with detailed reports.',
    landingFlexibleCurriculum: 'Flexible Curriculum',
    landingFlexibleCurriculumDesc: 'Customize the curriculum to fit your family\'s needs.',
    landing100Customizable: '100% Customizable',
    landingDownloadLearn: 'Download & Learn',
    landingDownloadLearnDesc: 'Export materials for offline learning.',
    landingPdfExportFeature: 'PDF Export',
    landingTextToSpeech: 'Text-to-Speech',
    landingTextToSpeechDesc: 'Audio support for all reading content.',
    landingAudioLearning: 'Audio Learning',
    landingSafeContent: 'Safe Content',
    landingSafeContentDesc: 'Age-appropriate and reviewed materials.',
    landingParentalControl: 'Parental Control',
    landingSubjectModules: 'Subject-Specific Modules',
    landingMathematics: 'Mathematics',
    landingLanguageArts: 'Language Arts',
    landingScience: 'Science',
    landingAstronomy: 'Astronomy',
    landingGeography: 'Geography',
    landingHistory: 'History',
    landingArt: 'Art',
    landingPhysicalEd: 'Physical Ed',
    landingStartHomeschooling: 'Start Homeschooling',
    // CTA Section
    landing7DaysFree: '7 days free',
    landingGetStartedToday: 'Get started today',
    landingStartYourJourney: 'Start your digital classroom journey. No credit card required.',
    landingNoCreditCard: 'No credit card required',
    landingSetup2Min: 'Setup in 2 minutes',
    landingCancelAnytime: 'Cancel anytime',
    landingSupportBilingual: 'Support in Spanish & English',
    landingActiveTeachers: 'active teachers',
    landingSignIn: 'Sign in',
    landingCreateAccountTab: 'Create account',
    landingContinueGoogle: 'Continue with Google',
    landingOrWithEmail: 'or with email',
    landingEmail: 'Email',
    landingPassword: 'Password',
    landingFullNameLabel: 'Full name',
    landingCreateFreeAccount: 'Create free account',
    // Footer
    landingContact: 'Contact',
    landingPrivacyPolicy: 'Privacy Policy',
    landingHelp: 'Help',
    landingTermsOfUse: 'Terms of Use',
    landingCookiesPolicy: 'Cookies Policy',
    landingAccessibility: 'Accessibility',
    landingAllRightsReserved: 'All rights reserved.',
    
    // AI Features Section
    landingAdvancedAiFeatures: 'Advanced AI Features',
    landingIntelligentPlanning: 'Intelligent planning with AI',
    landingAiPlanningDesc: 'From generating a complete plan to daily suggestions, AI helps you every step of the way.',
    landingGenerateFullWeek: 'Generate Full Week',
    landingGenerateFullWeekDesc: 'With one click, generate activities for all 5 days with pedagogical progression: intro → practice → mastery → assessment.',
    landingDays: 'days',
    landingProgression: 'Progression',
    landingDailySuggestions: 'Daily Suggestions',
    landingDailySuggestionsDesc: 'Each day shows its pedagogical phase (Introduction, Practice, Mastery). Get specific suggestions for each stage.',
    landingMonFri: 'Mon → Fri',
    landingContextual: 'Contextual',
    landingReusableTemplates: 'Reusable Templates',
    landingReusableTemplatesDesc: 'Save your best plans as templates. Clone and customize for new topics with one click.',
    landingSave: 'Save',
    landingClone: 'Clone',
    landing5StarterTemplates: '5 Starter Templates',
    landing5StarterTemplatesDesc: 'Start immediately with pro templates: Fractions, Reading Comprehension, Scientific Method, Writing, and Multiplication.',
    landingMath: 'Math',
    landingELA: 'ELA',
    landingAiCustomize: 'AI Customize',
    landingAiCustomizeDesc: 'Take any template and adapt it to a new topic. AI keeps the successful structure while changing the content.',
    landingNewTopic: 'New topic',
    landingSameStructure: 'Same structure',
    landingTemplateOfWeek: 'Template of the Week',
    landingTemplateOfWeekDesc: 'Each week we spotlight a different template on your dashboard with specific customization tips.',
    landingWeeklyRotation: 'Weekly rotation',
    landingTips: 'Tips',
    landingTryAiFree7Days: 'Try AI free for 7 days',
    
    // AI Presentations Section
    landingNewAiPresentations: 'NEW! AI Presentations',
    landingCreatePresentations: 'Create presentations that captivate',
    landingPresentationsDesc: 'Generate complete educational presentations with AI. Choose visual themes, add images, apply transitions, and save for later use.',
    landingAiGenerate: 'AI Generate',
    landingAiGenerateDesc: '6+ slides in seconds',
    landing8VisualThemes: '8 Visual Themes',
    landing8VisualThemesDesc: 'Ocean, Galaxy, Blackboard...',
    landingSaveAndLoad: 'Save & Load',
    landingSaveAndLoadDesc: 'Personal library',
    landingDownloadHtml: 'Download HTML',
    landingDownloadHtmlDesc: 'Use offline',
    landingCreateFirstPresentation: 'Create my first presentation',
    landingTheSolarSystem: 'The Solar System',
    landingScienceGrade: 'Science - 4th Grade',
    landingTransitionsEnabled: '✨ Transitions enabled',
    
    // Customization Section  
    landingFullCustomization: 'Full customization',
    landingSchoolBrandingTitle: 'Your school\'s identity on every document',
    landingSchoolBrandingDesc: 'From your logo to your colors, every PDF and report reflects your institution\'s branding.',
    landingSchoolLogoAll: 'Your school logo on all documents',
    landingCustomColorsMatch: 'Custom colors that match your brand',
    landingPdfFormats: 'Your school\'s exact PDF formats',
    landingCentralizedAdmin: 'Centralized admin dashboard',
    landingTeachersPlanning: "Teacher's Planning",
    landingYourLogoHere: 'Your logo here',
    landingYourColors: 'Your colors',
    
    // Integrations Section
    landingConnectFavorite: 'Connect with your favorite platforms',
    landingConnectFavoriteDesc: 'Sync with the tools you already use for a seamless workflow.',
    landingAvailable: 'Available',
    landingMostPopular: 'Most popular',
    landingImportClasses: 'Import Classes',
    landingImportClassesDesc: 'Bring your classes and student rosters automatically',
    landingSyncGrades: 'Sync Grades',
    landingSyncGradesDesc: 'Send grades to Google Classroom with one click',
    landingSecurePrivate: 'Secure & Private',
    landingSecurePrivateDesc: 'Secure OAuth authentication, your data protected',
    landingSetupMinutes: 'Setup in minutes',
    landingNoExtraCost: 'No extra cost',
    landingSupportIncluded: 'Support included',
    landingComingSoon: 'Coming Soon',
    landingNeedIntegration: 'Need another integration?',
    landingNeedIntegrationDesc: 'We\'re constantly adding new integrations. Let us know what platforms you use and we\'ll prioritize them.',
    landingRequestIntegration: 'Request Integration',
    
    // Homeschool Section
    landingPerfectHomeschool: 'Perfect for Homeschool Educators',
    landingHomeschoolCompleteDesc: 'Everything you need to create a personalized learning experience, from lesson planning to progress tracking.',
    landingAiPowered: 'AI-Powered',
    landingParentDashboard: 'Parent Dashboard',
    landingParentDashboardDesc: 'Complete overview of each child\'s progress, grades, and upcoming work in one unified dashboard.',
    landingFlexibleCurriculumDesc2: 'Customize subjects, pacing, and teaching methods to match each child\'s unique learning style.',
    landing100Customizable: '100% Customizable',
    landingDownloadAndLearn: 'Download & Learn',
    landingDownloadAndLearnDesc: 'Export lessons and materials as PDFs. Continue learning even when offline or on the go.',
    landingAudioLearning: 'Audio Learning',
    landingTextToSpeechDesc2: 'Built-in text-to-speech for all content. Perfect for auditory learners or reading assistance.',
    landingSafeContentDesc2: 'All content is age-appropriate and educationally vetted. Full parental control over what children can access.',
    
    // New Landing Page Copy
    landingSave5Hours: 'Save 5+ Hours Every Week',
    landingSave5HoursTitle: 'Save 5+ Hours Every Week on Planning, Grading, and Classroom Admin',
    landingSave5HoursSubtitle: 'TeacherHubPro helps busy educators create lesson plans, manage grades, track attendance, and use AI to simplify daily classroom work — all in one place.',
    landingBuiltForBusyTeachers: 'Built for busy teachers',
    landingStartFreeNoCreditCard: 'Start Free — No Credit Card Required',
    landingSeeHowItWorks: 'See How It Works',
    landingTeachingIsMore: 'Teaching Is More Than Teaching',
    landingTeachingIsMoreDesc: 'Between lesson planning, grading, attendance, reporting, and communication — most teachers spend hours every week on tasks that steal time from students and personal life.',
    landingYouBecameTeacher: 'You became a teacher to educate.',
    landingNotToDrown: 'Not to drown in paperwork.',
    landingEverythingSimple: 'Everything You Need. One Simple Platform.',
    landingLessBusywork: 'Less busywork. More teaching.',
    landingGenerateLessons: 'Generate structured lesson plans in minutes',
    landingTrackGrades: 'Track grades with an easy-to-use digital gradebook',
    landingRecordAttendance: 'Record attendance quickly',
    landingOrganizeData: 'Organize class data in one dashboard',
    landingUseAiTools: 'Use AI tools to speed up prep work',
    landingWhatTeachersExperience: 'What Teachers Experience',
    landingFasterPlanning: 'Faster weekly lesson planning',
    landingLessTimeGrading: 'Less time grading',
    landingBetterOrganization: 'Better organization',
    landingMoreConfidence: 'More classroom confidence',
    landingMorePersonalTime: 'More personal time after school',
    landing3SimpleSteps: 'Get Started in 3 Simple Steps',
    landingValueInMinutes: 'You\'ll see value in minutes — not hours.',
    landingCreateClasses: 'Create Your Classes',
    landingCreateClassesDesc: 'Set up your classes and add students in seconds',
    landingGenerateWithAi: 'Generate Lessons with AI',
    landingGenerateWithAiDesc: 'Create comprehensive lesson plans with AI assistance',
    landingManageGradesAttendance: 'Manage Grades and Attendance Effortlessly',
    landingManageGradesAttendanceDesc: 'Track everything from one intuitive dashboard',
    landingBuiltForEducators: 'Built Specifically for Educators',
    landingElementaryTeachers: 'Elementary Teachers',
    landingMiddleSchoolEducators: 'Middle School Educators',
    landingHighSchoolTeachers: 'High School Teachers',
    landingHomeschoolEducators: 'Homeschool Educators',
    landingInstructionalCoaches: 'Instructional Coaches',
    landingJoinThousands: 'Join Thousands of Teachers',
    landingJoinThousandsDesc: 'Educators across the country are already using TeacherHubPro to streamline their workflow, spend more time with students, and reclaim their evenings.',
    landingReadyToSimplify: 'Ready to Simplify Your Teaching Life?',
    landingStartFreeToday: 'Start Free Today',
    landingExperienceTimeSaved: 'Start your free account today and experience how much time you can save this week.',
    landingNoCreditCardRequired: 'No credit card required',
    landingSetupIn2Minutes: 'Set up in under 2 minutes',
    landingCancelAnytime: 'Cancel anytime',
    landingFullAccess: 'Full access to all features',
    landingTeachersUsing: 'teachers already using TeacherHubPro',
    landingSimplifyingTeaching: 'Simplifying teaching, one classroom at a time.',
    landingLogIn: 'Log In',
    landingStartFreeCta: 'Start Free',
    landingBackToHome: 'Back to Home',
    landingWelcomeBack: 'Welcome Back',
    landingSignInAccess: 'Sign in to access your dashboard',
    landingStartFreeTrial: 'Start Your Free Trial',
    landingJoinThousandsTeachers: 'Join thousands of teachers saving time every week',
    landingCreateFreeAccount: 'Create Free Account',
    landingSignInTab: 'Sign In',
    landingContinueWithGoogle: 'Continue with Google',
    landingOrWithEmail: 'or with email',
    landingFullName: 'Full Name',
    landingYourName: 'Your name',
    landingYourEmail: 'you@school.edu',
    landingCreatePassword: 'Create a password',
    landingCreatingAccount: 'Creating account...',
    landingSigningIn: 'Signing in...',
    
    // Feature Sections
    landingEngageStudents: 'Engage Students Like Never Before',
    landingEngageStudentsDesc: 'Transform your classroom with interactive games, adaptive learning, and live quizzes that keep students motivated and learning.',
    landingPlayToLearnTitle: 'Play to Learn',
    landingPlayToLearnSubtitle: 'Kahoot-Style Live Games',
    landingPlayToLearnDesc: 'Host live quiz games or assign self-paced activities. Students join with a PIN and compete in real-time with instant feedback.',
    landingPlayToLearnFeature1: 'Live multiplayer quizzes',
    landingPlayToLearnFeature2: 'Self-paced assignments',
    landingPlayToLearnFeature3: 'Real-time leaderboards',
    landingPlayToLearnFeature4: '9+ game modes',
    landingEducationalGamesTitle: 'Educational Games',
    landingEducationalGamesSubtitle: 'Fun Learning Activities',
    landingEducationalGamesDesc: 'Create custom games for any subject. Share with students via link or Google Classroom. Track engagement and progress.',
    landingEducationalGamesFeature1: 'Customizable game templates',
    landingEducationalGamesFeature2: 'Share via link or QR code',
    landingEducationalGamesFeature3: 'Student progress tracking',
    landingEducationalGamesFeature4: 'Works on any device',
    landingAdaptiveLearningTitle: 'Adaptive Learning',
    landingAdaptiveLearningSubtitle: 'Personalized Learning Paths',
    landingAdaptiveLearningDesc: 'AI-powered learning paths that adapt to each student\'s pace and level. Identify gaps and provide targeted practice.',
    landingAdaptiveLearningFeature1: 'AI-powered recommendations',
    landingAdaptiveLearningFeature2: 'Personalized practice',
    landingAdaptiveLearningFeature3: 'Progress analytics',
    landingAdaptiveLearningFeature4: 'Automatic difficulty adjustment',
    landingExploreFeature: 'Explore',
    landingProductLabel: 'Product',
    landingCompanyLabel: 'Company',
    landingLegalLabel: 'Legal',
    landingHelpCenter: 'Help Center',
    landingPrivacyPolicyLink: 'Privacy Policy',
    landingTermsOfServiceLink: 'Terms of Service',
    
    // Platform Integrations (English)
    landingPlatformIntegrations: 'Platform Integrations',
    landingPlatformIntegrationsDesc: 'Connect with the tools you already use',
    landingAiAssistantTitle: 'AI Assistant',
    landingStudentProgressTitle: 'Student Progress',
    landingPresentationsTitle: 'Presentations',
    
    // Google Classroom Section (English)
    landingConnectPlatforms: 'Connect with Your Favorite Platforms',
    landingConnectPlatformsDesc: 'Seamlessly integrate with the tools your school already uses',
    landingIntegrated: 'INTEGRATED',
    landingImportClasses: 'Import classes and rosters automatically',
    landingSyncGrades: 'Sync grades back to Google Classroom',
    landingSecurePrivate: 'Secure & private - OAuth 2.0 protected',
    landingConnectGoogleClassroom: 'Connect Google Classroom',
    landingComingSoon: 'Coming Soon',
    landingComingSoonBadge: 'Coming Soon',
    landingMoreIntegrations: 'More integrations are on the way. Let us know what you need!',
    landingTrustedBySchools: 'Trusted by schools using Google Classroom worldwide',
    
    // Homeschool Section (English)
    landingForHomeschool: 'FOR HOMESCHOOL',
    landingHomeschoolFriendly: 'Homeschool Friendly',
    landingLearnMoreHomeschool: 'Learn More About Homeschool Features',
    landingHomeschoolAdaptiveDesc: 'AI-powered paths that adapt to your child\'s unique pace',
    landingFlexibleCurriculum: 'Flexible Curriculum',
    landingFlexibleCurriculumDesc: 'Customize subjects and pacing to match learning styles',
    landingTextToSpeechDesc: 'Built-in text-to-speech for auditory learners',
    landingSafeContent: 'Safe Content',
    landingSafeContentDesc: 'Age-appropriate, educationally vetted materials',
    
    // Pricing Page (English)
    pricingTitle: 'Choose Your Plan',
    pricingSubtitle: 'Start with a 7-day free trial. Cancel anytime.',
    pricingBack: 'Back',
    pricingYourDigitalClassroom: 'Your digital classroom',
    pricingAllRightsReserved: 'All rights reserved.',
    pricingTrialExpired: 'Your free trial has expired. Please select a plan to continue using TeacherHubPro.',
    pricingAdminAccount: 'Admin Account - Full Access',
    pricingFreeTrial: 'Free Trial',
    pricingDaysRemaining: 'days remaining',
    pricingActiveSubscription: 'Active Subscription',
    pricingSchoolDistrict: 'For Schools & Districts',
    pricingNumberOfTeachers: 'Number of teachers',
    pricingTeachers: 'teachers',
    pricingMostPopular: 'MOST POPULAR',
    pricingMonth: 'month',
    pricingYear: 'year',
    pricingPerTeacher: 'per teacher',
    pricingBilledAnnually: 'Billed annually',
    pricingGetStarted: 'Get Started',
    pricingSubscribe: 'Subscribe',
    pricingContactUs: 'Contact Us',
    pricingCurrentPlan: 'Current Plan',
    pricingStarterPlan: 'Starter',
    pricingStarterDesc: 'Perfect for individual teachers',
    pricingProPlan: 'Pro',
    pricingProDesc: 'Advanced features for power users',
    pricingSchoolPlan: 'School',
    pricingSchoolDesc: 'For entire schools with team features',
    pricingDistrictPlan: 'District',
    pricingDistrictDesc: 'For school districts with advanced admin',
    pricingFeatureUnlimitedClasses: 'Unlimited Classes',
    pricingFeatureUnlimitedStudents: 'Unlimited Students',
    pricingFeatureBasicLessonPlanning: 'Basic Lesson Planning',
    pricingFeatureGradebook: 'Gradebook',
    pricingFeatureAttendance: 'Attendance Tracking',
    pricingFeatureEverythingStarter: 'Everything in Starter',
    pricingFeatureAIAssistant: 'AI Assistant (Claude)',
    pricingFeatureAdvancedAnalytics: 'Advanced Analytics',
    pricingFeatureCustomTemplates: 'Custom Templates',
    pricingFeaturePrioritySupport: 'Priority Support',
    pricingFeatureEverythingPro: 'Everything in Pro',
    pricingFeatureSchoolDashboard: 'School Dashboard',
    pricingFeatureTeacherManagement: 'Teacher Management',
    pricingFeatureSharedResources: 'Shared Resources',
    pricingFeatureBulkImport: 'Bulk Import',
    pricingFeatureEverythingSchool: 'Everything in School',
    pricingFeatureDistrictAnalytics: 'District Analytics',
    pricingFeatureMultiSchool: 'Multi-School Management',
    pricingFeatureCustomTraining: 'Custom Training',
    pricingFeatureApiAccess: 'API Access',
    // Pricing Plans - Specific
    pricingIndividualMonthly: 'Individual Monthly',
    pricingIndividualMonthlyDesc: 'Full access for individual teachers',
    pricingIndividualYearly: 'Individual Yearly',
    pricingIndividualYearlyDesc: 'Best value for individual teachers',
    pricingSchoolPlanName: 'School Plan',
    pricingSchoolPlanDesc: 'Perfect for small to medium schools',
    pricingDistrictPlanName: 'District Plan',
    pricingDistrictPlanDesc: 'For large school districts',
    pricingSave: 'Save',
    pricingMinimum: 'Minimum',
    pricingForSchoolPlans: 'for school plans',
    pricingMinTeachers: 'Minimum 10 teachers for School Plan, 100 for District',
    pricingSignUpFree: 'Sign Up Free',
    // Pricing Features
    pricingFeatureLessonPlanner: 'Lesson Planner',
    pricingFeatureAttendanceTracker: 'Attendance Tracker',
    pricingFeaturePdfExport: 'PDF Export',
    pricingFeatureTemplates: 'Templates',
    pricingFeatureEmailSupport: 'Email Support',
    pricingFeatureEverythingMonthly: 'Everything in Monthly',
    pricingFeature2MonthsFree: '2 months free',
    pricingFeatureEarlyAccess: 'Early feature access',
    pricingFeatureAllIndividual: 'All Individual Features',
    pricingFeatureAdminDashboard: 'Admin Dashboard',
    pricingFeatureSchoolBranding: 'School Branding',
    pricingFeatureSchoolReports: 'School Reports',
    pricingFeatureUserManagement: 'User Management',
    pricingFeatureAllSchool: 'All School Features',
    pricingFeatureSsoIntegration: 'SSO Integration',
    pricingFeatureDedicatedSupport: 'Dedicated Support',
    
    // Footer & Navigation (Marketing Pages)
    navFeatures: 'Features',
    navPricing: 'Pricing',
    navTrust: 'Trust',
    navContact: 'Contact',
    navLogin: 'Log In',
    navStartFree: 'Start Free',
    footerProduct: 'Product',
    footerResources: 'Resources',
    footerTrust: 'Trust',
    footerLegal: 'Legal',
    footerFeatures: 'Features',
    footerPricing: 'Pricing',
    footerIntegrations: 'Integrations',
    footerHelpCenter: 'Help Center',
    footerContact: 'Contact',
    footerTrustCenter: 'Trust Center',
    footerPrivacy: 'Privacy',
    footerTerms: 'Terms',
    footerCookies: 'Cookies',
    footerAccessibility: 'Accessibility',
    footerTagline: 'The smarter workspace for teachers.',
    footerSupportNotice: 'Need help? Our support resources are here to help you get started and keep moving.',
    footerAllRightsReserved: 'All rights reserved.',
    startFreeTrial: 'Start Free Trial',
    viewPricing: 'View Pricing',
    featuresHeroTitle: 'Powerful tools built for teachers',
    featuresHeroSubtitle: 'TeacherHubPro brings together the essential tools educators need to plan, organize, create, and manage classroom work more efficiently.',
    featuresCTATitle: 'See how TeacherHubPro fits into your teaching day',
    featuresCTASubtitle: 'Explore the platform and discover a better way to plan, organize, and teach.',
    contactTitle: 'Contact Us',
    contactSendMessage: 'Send us a message',
    contactName: 'Name',
    contactEmail: 'Email',
    contactSubject: 'Subject',
    contactMessage: 'Message',
    contactEmailSupport: 'Email Support',
    contactHelpCenter: 'Help Center',
    landingCTATitle: 'Start simplifying your teaching workflow today',
    landingCTASubtitle: 'Join educators using TeacherHubPro to save time, stay organized, and work smarter with AI.',
    
    // Hero Section
    heroTitle: 'The Smarter Workspace for Teachers',
    heroSubtitle: 'Plan lessons, manage grades, track attendance, create classroom materials, and save hours every week with one AI-powered platform built for teachers.',
    seeHowItWorks: 'See How It Works',
    heroSupportingText: 'No extra AI fees. Built for teachers, tutors, homeschool educators, and schools.',
    dashboardLessons: 'Lessons',
    dashboardStudents: 'Students',
    dashboardAvgGrade: 'Avg Grade',
    hoursSavedPerWeek: 'hours saved/week',
    
    // Trust Bar
    trustBuiltForEducators: 'Built for modern educators',
    trustAIIncluded: 'AI included in every plan',
    trustSecureEasy: 'Secure and easy to use',
    trustReduceWorkload: 'Designed to reduce planning and admin workload',
    
    // Benefits Grid
    benefitSectionTitle: 'Everything teachers need, in one place',
    benefitSectionSubtitle: 'Stop switching between documents, spreadsheets, and disconnected tools. TeacherHubPro brings your core teaching workflow into one simple platform.',
    benefitLessonPlanningTitle: 'Lesson Planning',
    benefitLessonPlanningDesc: 'Create organized, reusable lesson plans in less time with built-in templates and AI support.',
    benefitGradebookTitle: 'Gradebook',
    benefitGradebookDesc: 'Track student progress with a clean, simple gradebook built for real classroom use.',
    benefitAttendanceTitle: 'Attendance Tracking',
    benefitAttendanceDesc: 'Manage attendance quickly and keep class records organized without extra paperwork.',
    benefitAIToolsTitle: 'AI Teaching Tools',
    benefitAIToolsDesc: 'Generate lesson ideas, activities, writing prompts, classroom materials, and more in seconds.',
    benefitPresentationsTitle: 'Presentations and Resources',
    benefitPresentationsDesc: 'Build materials for class faster and keep everything connected in one workflow.',
    benefitWorkflowTitle: 'Teacher-Friendly Workflow',
    benefitWorkflowDesc: 'Designed for educators who need practical tools, not complex software.',
    
    // Outcomes Section
    outcomeSectionTitle: 'Spend less time on admin and more time teaching',
    outcomePlanFaster: 'Plan faster',
    outcomePlanFasterDesc: 'Build lesson plans, classroom activities, and materials without starting from scratch every time.',
    outcomeStayOrganized: 'Stay organized',
    outcomeStayOrganizedDesc: 'Keep grades, attendance, and teaching resources in one place instead of scattered across multiple tools.',
    outcomeTeachConfidence: 'Teach with confidence',
    outcomeTeachConfidenceDesc: 'Use AI and streamlined workflows to reduce stress and keep your classroom work moving.',
    
    // Social Proof Section
    socialProofSectionTitle: 'Built to support real classroom work',
    socialProofEducators: 'Designed specifically for educators',
    socialProofSupport: 'Includes support resources and guided help',
    socialProofWorkflows: 'Built to simplify common teacher workflows',
    socialProofSuitable: 'Suitable for individual teachers, homeschool educators, and growing school use',
    socialProofHelpCenter: 'Support resources available • Help center included',
    
    // Use Cases Section
    useCaseSectionTitle: 'Made for different teaching environments',
    useCaseClassroomTitle: 'Classroom Teachers',
    useCaseClassroomDesc: 'Manage planning, grading, and daily classroom organization in one place.',
    useCaseHomeschoolTitle: 'Homeschool Educators',
    useCaseHomeschoolDesc: 'Create flexible lesson workflows and keep learning plans organized.',
    useCaseTutorsTitle: 'Tutors and Small Learning Programs',
    useCaseTutorsDesc: 'Track student work, plan sessions, and create resources efficiently.',
    useCaseSchoolsTitle: 'Schools and Teams',
    useCaseSchoolsDesc: 'Support educators with a platform that improves consistency and saves time.',
    
    // Feature Detail Pages - Common
    backToFeatures: 'Back to Features',
    keyBenefits: 'Key Benefits',
    howItWorks: 'How It Works',
    readyToGetStarted: 'Ready to get started?',
    featureCTASubtitle: 'Join thousands of educators who are already saving time and improving their teaching workflow with TeacherHubPro.',
    exploreOtherFeatures: 'Explore Other Features',
    learnMore: 'Learn more',
    
    // Lesson Planning Feature
    featureLessonPlanningTitle: 'Lesson Planning',
    featureLessonPlanningSubtitle: 'Create organized, reusable lesson plans in less time with built-in templates and AI support.',
    featureLessonPlanningDescription: 'TeacherHubPro\'s lesson planning tool helps you create comprehensive, standards-aligned lesson plans quickly and efficiently. Use our AI-powered suggestions to generate ideas, find resources, and structure your lessons. Save your plans as templates for future use and share them with colleagues.',
    featureLessonPlanningBenefit1: 'Create lesson plans up to 5x faster with AI-powered templates',
    featureLessonPlanningBenefit2: 'Align lessons to curriculum standards automatically',
    featureLessonPlanningBenefit3: 'Reuse and adapt plans for different classes or years',
    featureLessonPlanningBenefit4: 'Share plans with colleagues and collaborate in real-time',
    featureLessonPlanningHowItWorks1: 'Choose a template or start from scratch, then add your learning objectives',
    featureLessonPlanningHowItWorks2: 'Use AI suggestions to fill in activities, resources, and assessments',
    featureLessonPlanningHowItWorks3: 'Save, organize, and reuse your plans across classes and semesters',
    
    // Gradebook Feature
    featureGradebookTitle: 'Gradebook',
    featureGradebookSubtitle: 'Track student progress with a clean, simple gradebook built for real classroom use.',
    featureGradebookDescription: 'Our gradebook is designed to be intuitive and powerful. Track assignments, calculate grades automatically, and generate progress reports with ease. See individual student performance at a glance and identify students who may need additional support.',
    featureGradebookBenefit1: 'Automatic grade calculations with customizable weighting',
    featureGradebookBenefit2: 'Visual progress tracking for each student',
    featureGradebookBenefit3: 'Generate report cards and progress reports instantly',
    featureGradebookBenefit4: 'AI-powered insights to identify struggling students',
    featureGradebookHowItWorks1: 'Set up your classes and add students',
    featureGradebookHowItWorks2: 'Create assignments and enter grades as you go',
    featureGradebookHowItWorks3: 'Review analytics and generate reports with one click',
    
    // Attendance Feature
    featureAttendanceTitle: 'Attendance Tracking',
    featureAttendanceSubtitle: 'Manage attendance quickly and keep class records organized without extra paperwork.',
    featureAttendanceDescription: 'Say goodbye to paper attendance sheets. Our digital attendance system lets you take attendance in seconds, track patterns, and generate reports for administration. Set up notifications for chronic absenteeism and keep parents informed automatically.',
    featureAttendanceBenefit1: 'Take attendance in under 30 seconds per class',
    featureAttendanceBenefit2: 'Automatic pattern detection for chronic absenteeism',
    featureAttendanceBenefit3: 'Generate attendance reports for administration',
    featureAttendanceBenefit4: 'Optional parent notifications for absences',
    featureAttendanceHowItWorks1: 'Open your class and tap to mark present, absent, or tardy',
    featureAttendanceHowItWorks2: 'Review attendance patterns and trends over time',
    featureAttendanceHowItWorks3: 'Export reports or share with parents through the portal',
    
    // AI Tools Feature
    featureAIToolsTitle: 'AI Teaching Tools',
    featureAIToolsSubtitle: 'Generate lesson ideas, activities, writing prompts, classroom materials, and more in seconds.',
    featureAIToolsDescription: 'Harness the power of AI to supercharge your teaching. Generate lesson ideas tailored to your curriculum, create differentiated worksheets, write engaging discussion questions, and build assessments in minutes instead of hours. Our AI understands educational best practices and grade-level appropriateness.',
    featureAIToolsBenefit1: 'Generate lesson ideas and activities tailored to your subject',
    featureAIToolsBenefit2: 'Create differentiated materials for diverse learners',
    featureAIToolsBenefit3: 'Build assessments and rubrics automatically',
    featureAIToolsBenefit4: 'No additional AI fees - included in every plan',
    featureAIToolsHowItWorks1: 'Describe what you need - lesson idea, worksheet, quiz, etc.',
    featureAIToolsHowItWorks2: 'AI generates options based on your grade level and subject',
    featureAIToolsHowItWorks3: 'Edit, customize, and save to your resource library',
    
    // Presentations Feature
    featurePresentationsTitle: 'Presentations & Resources',
    featurePresentationsSubtitle: 'Build materials for class faster and keep everything connected in one workflow.',
    featurePresentationsDescription: 'Create engaging presentations, worksheets, and classroom resources all in one place. Our presentation builder integrates with your lesson plans so everything stays connected. Use templates designed for education, import existing materials, and share resources with students easily.',
    featurePresentationsBenefit1: 'Education-focused templates for various subjects',
    featurePresentationsBenefit2: 'Integrates with lesson plans and gradebook',
    featurePresentationsBenefit3: 'Easy sharing with students via link or LMS',
    featurePresentationsBenefit4: 'Import and organize existing resources',
    featurePresentationsHowItWorks1: 'Choose a template or create from scratch',
    featurePresentationsHowItWorks2: 'Add content, images, and interactive elements',
    featurePresentationsHowItWorks3: 'Link to your lesson plan and share with your class',
    
    // Workflow Feature
    featureWorkflowTitle: 'Teacher-Friendly Workflow',
    featureWorkflowSubtitle: 'Designed for educators who need practical tools, not complex software.',
    featureWorkflowDescription: 'We built TeacherHubPro specifically for how teachers actually work. Everything is designed to be intuitive, with minimal setup required. Move seamlessly between planning, grading, and communication. Your data syncs automatically, so you can work from any device.',
    featureWorkflowBenefit1: 'Intuitive interface designed by educators',
    featureWorkflowBenefit2: 'Works seamlessly across desktop and mobile',
    featureWorkflowBenefit3: 'Quick setup - start using in minutes',
    featureWorkflowBenefit4: 'Integrates with existing tools you already use',
    featureWorkflowHowItWorks1: 'Sign up and set up your classes in minutes',
    featureWorkflowHowItWorks2: 'Access all tools from one unified dashboard',
    featureWorkflowHowItWorks3: 'Your data syncs automatically across all your devices',
    
    // Classroom Teachers Use Case
    featureClassroomTeachersTitle: 'For Classroom Teachers',
    featureClassroomTeachersSubtitle: 'Manage planning, grading, and daily classroom organization in one place.',
    featureClassroomTeachersDescription: 'As a classroom teacher, you juggle multiple classes, dozens of students, and countless responsibilities. TeacherHubPro brings all your essential tools together so you can spend less time on administration and more time doing what you love - teaching.',
    featureClassroomTeachersBenefit1: 'Plan lessons for multiple classes in one organized workspace',
    featureClassroomTeachersBenefit2: 'Track grades and attendance across all your students',
    featureClassroomTeachersBenefit3: 'Generate parent communication and progress reports',
    featureClassroomTeachersBenefit4: 'Access your materials from classroom or home',
    featureClassroomTeachersHowItWorks1: 'Set up your classes and import your student roster',
    featureClassroomTeachersHowItWorks2: 'Plan lessons, take attendance, and record grades daily',
    featureClassroomTeachersHowItWorks3: 'Generate reports and communicate with parents through the portal',
    
    // Homeschool Use Case
    featureHomeschoolTitle: 'For Homeschool Educators',
    featureHomeschoolSubtitle: 'Create flexible lesson workflows and keep learning plans organized.',
    featureHomeschoolDescription: 'Homeschooling requires flexibility, adaptability, and excellent organization. TeacherHubPro helps you create personalized learning paths for each child, track their progress, and maintain records for compliance. Our AI tools help generate fresh ideas and materials when you need inspiration.',
    featureHomeschoolBenefit1: 'Create personalized learning paths for each child',
    featureHomeschoolBenefit2: 'Maintain organized records for compliance requirements',
    featureHomeschoolBenefit3: 'AI-generated activities and lesson ideas',
    featureHomeschoolBenefit4: 'Flexible scheduling that adapts to your family\'s needs',
    featureHomeschoolHowItWorks1: 'Set up profiles for each child you\'re teaching',
    featureHomeschoolHowItWorks2: 'Create and track progress on personalized learning plans',
    featureHomeschoolHowItWorks3: 'Generate reports for portfolio reviews or assessments',
    
    // Tutors Use Case
    featureTutorsTitle: 'For Tutors & Small Programs',
    featureTutorsSubtitle: 'Track student work, plan sessions, and create resources efficiently.',
    featureTutorsDescription: 'Whether you\'re an independent tutor or running a small learning program, TeacherHubPro helps you stay organized and professional. Track individual student progress, plan effective sessions, and communicate with parents. Scale your operation without losing the personal touch.',
    featureTutorsBenefit1: 'Track progress for multiple students individually',
    featureTutorsBenefit2: 'Plan sessions and maintain session notes',
    featureTutorsBenefit3: 'Generate progress reports for parents',
    featureTutorsBenefit4: 'Create custom materials for each student\'s needs',
    featureTutorsHowItWorks1: 'Add your students and their learning goals',
    featureTutorsHowItWorks2: 'Plan sessions and track progress over time',
    featureTutorsHowItWorks3: 'Share progress with parents through the parent portal',
    
    // Schools Use Case
    featureSchoolsTitle: 'For Schools & Teams',
    featureSchoolsSubtitle: 'Support educators with a platform that improves consistency and saves time.',
    featureSchoolsDescription: 'TeacherHubPro is designed to scale with your school or district. Provide your teachers with consistent, powerful tools that save time and improve outcomes. Administrators can monitor adoption, share resources across the team, and ensure curriculum alignment.',
    featureSchoolsBenefit1: 'Consistent tools across all teachers in your school',
    featureSchoolsBenefit2: 'Shared resource library for curriculum materials',
    featureSchoolsBenefit3: 'Admin dashboard for monitoring and support',
    featureSchoolsBenefit4: 'Volume pricing for schools and districts',
    featureSchoolsHowItWorks1: 'Contact us for school or district pricing',
    featureSchoolsHowItWorks2: 'Onboard teachers with guided setup and training',
    featureSchoolsHowItWorks3: 'Monitor adoption and provide ongoing support'
  },
  es: {
    // Navigation
    dashboard: 'Panel',
    planner: 'Planificador',
    attendance: 'Asistencia',
    gradebook: 'Notas',
    classes: 'Clases',
    settings: 'Configuración',
    logout: 'Cerrar Sesión',
    
    // Auth
    login: 'Iniciar Sesión',
    register: 'Registrarse',
    email: 'Correo Electrónico',
    password: 'Contraseña',
    name: 'Nombre',
    signInWithGoogle: 'Iniciar sesión con Google',
    orContinueWith: 'o continuar con',
    dontHaveAccount: '¿No tienes cuenta?',
    alreadyHaveAccount: '¿Ya tienes cuenta?',
    
    // Dashboard
    welcome: 'Bienvenido',
    todayOverview: 'Resumen de Hoy',
    quickActions: 'Acciones Rápidas',
    attendancePending: 'Asistencia Pendiente',
    upcomingAssignments: 'Próximas Tareas',
    recentPlans: 'Planes Recientes',
    totalClasses: 'Total de Clases',
    totalStudents: 'Total de Estudiantes',
    totalPlans: 'Total de Planes',
    
    // Classes
    addClass: 'Agregar Clase',
    className: 'Nombre de Clase',
    grade: 'Grado',
    section: 'Sección',
    subject: 'Materia',
    yearTerm: 'Año/Período',
    students: 'Estudiantes',
    addStudent: 'Agregar Estudiante',
    firstName: 'Nombre',
    lastName: 'Apellido',
    studentNumber: 'Número de Estudiante',
    parentEmail: 'Correo del Padre',
    notes: 'Notas',
    accommodations: 'Acomodaciones',
    
    // Planner
    weeklyPlan: 'Plan Semanal',
    createPlan: 'Crear Plan',
    weekOf: 'Semana del',
    unit: 'Unidad',
    story: 'Historia/Título',
    objectiveOfWeek: 'Objetivo de la Semana',
    skillsOfWeek: 'Destrezas de la Semana',
    dayTheme: 'Tema del Día',
    activities: 'Actividades',
    materials: 'Materiales',
    standards: 'Estándares',
    expectations: 'Expectativas',
    firstWeek: 'Primera Semana',
    secondWeek: 'Segunda Semana',
    saveAsTemplate: 'Guardar como Plantilla',
    duplicateLastWeek: 'Duplicar Última Semana',
    exportPdf: 'Exportar PDF',
    
    // Taxonomy levels
    level1Memory: 'Nivel 1: Memoria',
    level2Processing: 'Nivel 2: Procesamiento',
    level3Strategic: 'Nivel 3: Pensamiento Estratégico',
    level4Extended: 'Nivel 4: Pensamiento Extendido',
    
    // Activities
    brainstorming: 'Lluvia de Ideas',
    buildingBackground: 'Construir Conocimiento Previo',
    vocabularyDevelopment: 'Desarrollo de Vocabulario',
    readPages: 'Leer Páginas',
    guidedReading: 'Lectura Guiada y Coral',
    oralQuestions: 'Preguntas Orales',
    comprehensionQuestions: 'Preguntas de Comprensión',
    exercisePractice: 'Práctica de Ejercicios',
    other: 'Otro',
    
    // Materials
    book: 'Libro',
    notebook: 'Cuaderno',
    teachersGuide: 'Guía del Maestro',
    testQuiz: 'Prueba/Examen',
    dictionary: 'Diccionario',
    handouts: 'Hojas de Trabajo',
    
    // Standards domains
    listeningAndSpeaking: 'Escuchar/Hablar',
    foundationalSkills: 'Destrezas Fundamentales',
    reading: 'Lectura',
    writing: 'Escritura',
    language: 'Lenguaje',
    
    // Subject integration
    mathematics: 'Matemáticas',
    spanish: 'Español',
    socialStudies: 'Estudios Sociales',
    science: 'Ciencias',
    health: 'Salud',
    art: 'Arte',
    physicalEducation: 'Educación Física',
    religion: 'Religión',
    
    // Attendance
    takeAttendance: 'Tomar Asistencia',
    present: 'Presente',
    absent: 'Ausente',
    tardy: 'Tardanza',
    excused: 'Excusado',
    markAllPresent: 'Marcar Todos Presente',
    submitAttendance: 'Enviar Asistencia',
    attendanceReport: 'Reporte de Asistencia',
    
    // Gradebook
    assignments: 'Tareas',
    createAssignment: 'Crear Tarea',
    title: 'Título',
    description: 'Descripción',
    points: 'Puntos',
    dueDate: 'Fecha de Entrega',
    category: 'Categoría',
    homework: 'Tarea',
    quiz: 'Prueba Corta',
    test: 'Examen',
    project: 'Proyecto',
    gradeEntry: 'Entrada de Notas',
    missing: 'Falta',
    late: 'Tarde',
    
    // Common
    save: 'Guardar',
    cancel: 'Cancelar',
    delete: 'Eliminar',
    edit: 'Editar',
    view: 'Ver',
    search: 'Buscar',
    filter: 'Filtrar',
    loading: 'Cargando...',
    noData: 'No hay datos disponibles',
    confirmDelete: '¿Estás seguro de que deseas eliminar esto?',
    success: 'Éxito',
    error: 'Error',
    from: 'Desde',
    to: 'Hasta',
    date: 'Fecha',
    teacher: 'Maestro',
    selectLanguage: 'Seleccionar Idioma',
    
    // Days
    monday: 'Lunes',
    tuesday: 'Martes',
    wednesday: 'Miércoles',
    thursday: 'Jueves',
    friday: 'Viernes',
    
    // Games
    educationalGames: 'Juegos Educativos',
    myGames: 'Mis Juegos',
    createGame: 'Crear Juego',
    play: 'Jugar',
    copyLink: 'Copiar Enlace',
    shareToClassroom: 'Compartir en Classroom',
    deleteGame: 'Eliminar Juego',
    questions: 'Preguntas',
    gameType: 'Tipo de Juego',
    gradeLevel: 'Nivel de Grado',
    
    // Landing Page
    landingHeroTitle: 'Tu aula completa en una plataforma',
    landingHeroSubtitle: 'Planificación, asistencia, notas y asistente IA. Todo lo que un maestro necesita, en un solo lugar.',
    landingBuiltForTeachers: 'Diseñado para maestros',
    landingStartFree: 'Comenzar gratis',
    landingViewPricing: 'Ver precios',
    landingUsedBySchools: 'Usado por escuelas en Puerto Rico y más',
    landingEverythingYouNeed: 'Todo lo que necesitas',
    landingEverythingDesc: 'Una plataforma completa diseñada específicamente para maestros.',
    landingFeatures: 'Funciones',
    landingAiAssistant: 'Asistente IA',
    landingPoweredBy: 'Impulsado por Anthropic',
    landingActiveTeachers: 'maestros activos',
    landingHomeschool: 'Educación en Casa',
    landingIntegrations: 'Integraciones',
    landingCustomization: 'Personalización',
    landingPricing: 'Precios',
    landingGetStarted: 'Comenzar - Iniciar sesión',
    landingStart: 'Comenzar',
    landingAiLessonPlan: 'Planificación IA',
    landingPresentations: 'Presentaciones',
    landingGrades: 'Calificaciones',
    landingPlanFaster: 'Planifica más rápido',
    landingPlanFasterDesc: 'Crea planes de lección semanales en segundos con nuestro formato intuitivo.',
    landingWeek1: 'Semana 1',
    landingYourBrand: 'Tu marca, tu estilo',
    landingYourBrandDesc: 'Personaliza con el logo y colores de tu escuela en todos los documentos.',
    landingYourSchool: 'Tu Escuela',
    landingCustomBranding: 'Marca personalizada',
    landingExportPro: 'Exporta profesionalmente',
    landingExportProDesc: 'Genera PDFs listos para imprimir: planes, boletas, paquetes para sustitutos.',
    landingDownloadPdf: 'Descargar PDF',
    landingAiAssistantTitle: 'Asistente de IA',
    landingAiAssistantDesc: 'Genera planes de lección, actividades, preguntas y más con IA.',
    landingLessonPlans: 'Planes de lección',
    landingQuizzesTests: 'Quizzes y exámenes',
    landingDigitalClassroom: 'Tu aula digital',
    landingTryFree: 'Prueba gratis por 30 días',
    landingNoCard: 'No requiere tarjeta de crédito',
    landingFullName: 'Nombre Completo',
    landingCreateAccount: 'Crear Cuenta Gratis',
    landingOrLoginWith: 'O inicia sesión con',
    landingHaveAccount: '¿Tienes cuenta?',
    landingLoginHere: 'Inicia sesión aquí',
    landingNeedAccount: '¿Necesitas cuenta?',
    landingRegisterHere: 'Regístrate aquí',
    // AI Section
    landingAiTeachingAssistant: 'Asistente de IA',
    landingYouAsked: 'Tú preguntaste:',
    landingAiResponse: 'Respuesta IA:',
    landingAiQuestion: '¿Cómo puedo enseñar fracciones equivalentes a 4to grado?',
    landingAiAnswer: 'Aquí tienes un plan de lección basado en el estándar CCSS.MATH.4.NF.A.1 con actividades manipulativas...',
    landingQuizzes: 'Quizzes',
    landingWorksheets: 'Hojas de Trabajo',
    landingIdeas: 'Ideas',
    landingNewAi: 'Nuevo: Inteligencia Artificial',
    landingAiAssistantToTeach: 'Tu asistente de IA para enseñar mejor',
    landingAiAssistantDesc2: 'Genera planes de lección, quizzes, resúmenes y actividades basadas en los estándares Common Core y Puerto Rico, al instante.',
    landingAlignedStandards: 'Alineado con Common Core y PR CORE',
    landingGenerateSeconds: 'Genera contenido en segundos',
    landingBilingual: 'Bilingüe: Español e Inglés',
    landingPoweredBy: 'Powered by Claude Sonnet 4.5',
    landingTryItFree: 'Probar gratis',
    landingIncludedSubscription: 'Incluido en suscripción',
    // Integrations Section
    landingConnectTools: 'Conecta con tus herramientas favoritas',
    landingConnectToolsDesc: 'Integra con las plataformas que ya usas para facilitar tu trabajo.',
    landingCalendarSync: 'Sincronización de Calendario',
    landingCalendarSyncDesc: 'Exporta tus planes a Google Calendar, Outlook o Apple Calendar.',
    landingGoogleClassroom: 'Google Classroom',
    landingGoogleClassroomDesc: 'Comparte actividades y juegos directamente con tus estudiantes.',
    landingPdfExport: 'Exportar PDF',
    landingPdfExportDesc: 'Genera PDFs profesionales para planes, boletas y más.',
    landingEmailNotifications: 'Notificaciones por Email',
    landingEmailNotificationsDesc: 'Envía reportes automáticos a padres y administradores.',
    landingViewIntegrations: 'Ver todas las integraciones',
    // Customization Section
    landingYourBrandYourWay: 'Tu marca, a tu manera',
    landingCustomizeDesc: 'Personaliza todo con los colores, logo y preferencias de tu escuela.',
    landingSchoolLogo: 'Logo de Escuela',
    landingSchoolLogoDesc: 'Añade tu logo a todos los documentos y reportes.',
    landingColorThemes: 'Temas de Color',
    landingColorThemesDesc: 'Elige colores que coincidan con la identidad de tu escuela.',
    landingCustomTemplates: 'Plantillas Personalizadas',
    landingCustomTemplatesDesc: 'Crea plantillas reutilizables para tus planes.',
    landingMultiLanguage: 'Multi-Idioma',
    landingMultiLanguageDesc: 'Trabaja en inglés, español o ambos idiomas.',
    // Homeschool Section
    landingHomeschoolTitle: 'Educación en Casa',
    landingHomeschoolSubtitle: 'Tu compañero completo de homeschool',
    landingHomeschoolDesc: 'Todo lo que necesitas para una experiencia exitosa de educación en casa en una plataforma integrada.',
    landingAdaptiveLearning: 'Aprendizaje Adaptativo',
    landingAdaptiveLearningDesc: 'Rutas de aprendizaje que se adaptan al ritmo de cada niño.',
    landingFullVisibility: 'Visibilidad Total',
    landingFullVisibilityDesc: 'Sigue el progreso en tiempo real con reportes detallados.',
    landingFlexibleCurriculum: 'Currículo Flexible',
    landingFlexibleCurriculumDesc: 'Personaliza el currículo según las necesidades de tu familia.',
    landing100Customizable: '100% Personalizable',
    landingDownloadLearn: 'Descarga y Aprende',
    landingDownloadLearnDesc: 'Exporta materiales para aprendizaje sin conexión.',
    landingPdfExportFeature: 'Exportar PDF',
    landingTextToSpeech: 'Texto a Voz',
    landingTextToSpeechDesc: 'Soporte de audio para todo el contenido de lectura.',
    landingAudioLearning: 'Aprendizaje Auditivo',
    landingSafeContent: 'Contenido Seguro',
    landingSafeContentDesc: 'Materiales apropiados para la edad y revisados.',
    landingParentalControl: 'Control Parental',
    landingSubjectModules: 'Módulos por Materia',
    landingMathematics: 'Matemáticas',
    landingLanguageArts: 'Lenguaje',
    landingScience: 'Ciencias',
    landingAstronomy: 'Astronomía',
    landingGeography: 'Geografía',
    landingHistory: 'Historia',
    landingArt: 'Arte',
    landingPhysicalEd: 'Educación Física',
    landingStartHomeschooling: 'Comenzar Educación en Casa',
    // CTA Section
    landing7DaysFree: '7 días gratis',
    landingGetStartedToday: 'Comienza hoy',
    landingStartYourJourney: 'Comienza tu viaje de aula digital. No requiere tarjeta de crédito.',
    landingNoCreditCard: 'Sin tarjeta de crédito requerida',
    landingSetup2Min: 'Configuración en 2 minutos',
    landingCancelAnytime: 'Cancela en cualquier momento',
    landingSupportBilingual: 'Soporte en español e inglés',
    landingActiveTeachers: 'maestros activos',
    landingSignIn: 'Iniciar sesión',
    landingCreateAccountTab: 'Crear cuenta',
    landingContinueGoogle: 'Continuar con Google',
    landingOrWithEmail: 'o con email',
    landingEmail: 'Correo electrónico',
    landingPassword: 'Contraseña',
    landingFullNameLabel: 'Nombre completo',
    landingCreateFreeAccount: 'Crear cuenta gratis',
    // Footer
    landingContact: 'Contacto',
    landingPrivacyPolicy: 'Política de Privacidad',
    landingHelp: 'Ayuda',
    landingTermsOfUse: 'Términos de Uso',
    landingCookiesPolicy: 'Política de Cookies',
    landingAccessibility: 'Accesibilidad',
    landingAllRightsReserved: 'Todos los derechos reservados.',
    
    // AI Features Section
    landingAdvancedAiFeatures: 'Funciones de IA Avanzadas',
    landingIntelligentPlanning: 'Planificación inteligente con IA',
    landingAiPlanningDesc: 'Desde generar un plan completo hasta sugerencias diarias, la IA te ayuda en cada paso.',
    landingGenerateFullWeek: 'Genera Semana Completa',
    landingGenerateFullWeekDesc: 'Con un clic, genera actividades para los 5 días con progresión pedagógica: introducción → práctica → dominio → evaluación.',
    landingDays: 'días',
    landingProgression: 'Progresión',
    landingDailySuggestions: 'Sugerencias por Día',
    landingDailySuggestionsDesc: 'Cada día muestra su fase pedagógica (Introducción, Práctica, Dominio). Obtén sugerencias específicas para cada etapa.',
    landingMonFri: 'Lunes → Viernes',
    landingContextual: 'Contextual',
    landingReusableTemplates: 'Plantillas Reutilizables',
    landingReusableTemplatesDesc: 'Guarda tus mejores planes como plantillas. Clona y personaliza para nuevos temas con un clic.',
    landingSave: 'Guardar',
    landingClone: 'Clonar',
    landing5StarterTemplates: '5 Plantillas Iniciales',
    landing5StarterTemplatesDesc: 'Comienza inmediatamente con plantillas profesionales: Fracciones, Comprensión Lectora, Método Científico, Escritura y Multiplicación.',
    landingMath: 'Matemáticas',
    landingELA: 'Lectura',
    landingAiCustomize: 'Adapta con IA',
    landingAiCustomizeDesc: 'Toma cualquier plantilla y adáptala a un nuevo tema. La IA mantiene la estructura exitosa mientras cambia el contenido.',
    landingNewTopic: 'Nuevo tema',
    landingSameStructure: 'Misma estructura',
    landingTemplateOfWeek: 'Plantilla de la Semana',
    landingTemplateOfWeekDesc: 'Cada semana destacamos una plantilla diferente en tu dashboard con tips de personalización específicos.',
    landingWeeklyRotation: 'Rotación semanal',
    landingTips: 'Tips',
    landingTryAiFree7Days: 'Prueba la IA gratis por 7 días',
    
    // AI Presentations Section
    landingNewAiPresentations: '¡NUEVO! Presentaciones con IA',
    landingCreatePresentations: 'Crea presentaciones que cautivan',
    landingPresentationsDesc: 'Genera presentaciones educativas completas con IA. Elige temas visuales, agrega imágenes, aplica transiciones y guarda para usar después.',
    landingAiGenerate: 'Genera con IA',
    landingAiGenerateDesc: '6+ diapositivas en segundos',
    landing8VisualThemes: '8 Temas Visuales',
    landing8VisualThemesDesc: 'Océano, Galaxia, Pizarra...',
    landingSaveAndLoad: 'Guardar y Cargar',
    landingSaveAndLoadDesc: 'Biblioteca personal',
    landingDownloadHtml: 'Descargar HTML',
    landingDownloadHtmlDesc: 'Usa sin internet',
    landingCreateFirstPresentation: 'Crear mi primera presentación',
    landingTheSolarSystem: 'El Sistema Solar',
    landingScienceGrade: 'Ciencias - 4to Grado',
    landingTransitionsEnabled: '✨ Transiciones activadas',
    
    // Customization Section
    landingFullCustomization: 'Personalización total',
    landingSchoolBrandingTitle: 'La identidad de tu escuela en cada documento',
    landingSchoolBrandingDesc: 'Desde tu logo hasta tus colores, cada PDF y reporte refleja la marca de tu institución.',
    landingSchoolLogoAll: 'Logo de tu escuela en todos los documentos',
    landingCustomColorsMatch: 'Colores personalizados que coinciden con tu marca',
    landingPdfFormats: 'Formatos de PDF exactos de tu escuela',
    landingCentralizedAdmin: 'Panel de administración centralizado',
    landingTeachersPlanning: 'Planificación del Maestro',
    landingYourLogoHere: 'Tu logo aquí',
    landingYourColors: 'Tus colores',
    
    // Integrations Section
    landingConnectFavorite: 'Conecta con tus plataformas favoritas',
    landingConnectFavoriteDesc: 'Sincroniza con las herramientas que ya usas para un flujo de trabajo perfecto.',
    landingAvailable: 'Disponible',
    landingMostPopular: 'Más popular',
    landingImportClasses: 'Importar Clases',
    landingImportClassesDesc: 'Trae tus clases y listas de estudiantes automáticamente',
    landingSyncGrades: 'Sincronizar Calificaciones',
    landingSyncGradesDesc: 'Envía calificaciones a Google Classroom con un clic',
    landingSecurePrivate: 'Seguro y Privado',
    landingSecurePrivateDesc: 'Autenticación OAuth segura, tus datos protegidos',
    landingSetupMinutes: 'Configuración en minutos',
    landingNoExtraCost: 'Sin costo adicional',
    landingSupportIncluded: 'Soporte incluido',
    landingComingSoon: 'Próximamente',
    landingNeedIntegration: '¿Necesitas otra integración?',
    landingNeedIntegrationDesc: 'Estamos agregando nuevas integraciones constantemente. Cuéntanos qué plataformas usas y las priorizaremos.',
    landingRequestIntegration: 'Solicitar Integración',
    
    // Homeschool Section
    landingPerfectHomeschool: 'Perfecto para Educadores en Casa',
    landingHomeschoolCompleteDesc: 'Todo lo que necesitas para crear una experiencia de aprendizaje personalizada, desde planificación hasta seguimiento de progreso.',
    landingAiPowered: 'Impulsado por IA',
    landingParentDashboard: 'Panel de Padres',
    landingParentDashboardDesc: 'Vista completa del progreso, calificaciones y trabajo pendiente de cada niño en un panel unificado.',
    landingFlexibleCurriculumDesc2: 'Personaliza materias, ritmo y métodos de enseñanza para adaptarse al estilo de aprendizaje único de cada niño.',
    landing100Customizable: '100% Personalizable',
    landingDownloadAndLearn: 'Descarga y Aprende',
    landingDownloadAndLearnDesc: 'Exporta lecciones y materiales como PDFs. Continúa aprendiendo incluso sin conexión o en movimiento.',
    landingAudioLearning: 'Aprendizaje Auditivo',
    landingTextToSpeechDesc2: 'Texto a voz integrado para todo el contenido. Perfecto para aprendices auditivos o asistencia de lectura.',
    landingSafeContentDesc2: 'Todo el contenido es apropiado para la edad y verificado educativamente. Control parental total sobre lo que los niños pueden acceder.',
    
    // New Landing Page Copy (Spanish)
    landingSave5Hours: 'Ahorra 5+ Horas Cada Semana',
    landingSave5HoursTitle: 'Ahorra 5+ Horas Cada Semana en Planificación, Calificaciones y Administración',
    landingSave5HoursSubtitle: 'TeacherHubPro ayuda a educadores ocupados a crear planes de lecciones, gestionar calificaciones, registrar asistencia y usar IA para simplificar el trabajo diario — todo en un solo lugar.',
    landingBuiltForBusyTeachers: 'Diseñado para maestros ocupados',
    landingStartFreeNoCreditCard: 'Comenzar Gratis — Sin Tarjeta de Crédito',
    landingSeeHowItWorks: 'Ver Cómo Funciona',
    landingTeachingIsMore: 'Enseñar es Más que Enseñar',
    landingTeachingIsMoreDesc: 'Entre planificación, calificaciones, asistencia, reportes y comunicación — la mayoría de los maestros pasan horas cada semana en tareas que roban tiempo de los estudiantes y la vida personal.',
    landingYouBecameTeacher: 'Te convertiste en maestro para educar.',
    landingNotToDrown: 'No para ahogarte en papeleo.',
    landingEverythingSimple: 'Todo lo que Necesitas. Una Plataforma Simple.',
    landingLessBusywork: 'Menos trabajo administrativo. Más enseñanza.',
    landingGenerateLessons: 'Genera planes de lecciones estructurados en minutos',
    landingTrackGrades: 'Rastrea calificaciones con un libro de notas digital fácil de usar',
    landingRecordAttendance: 'Registra asistencia rápidamente',
    landingOrganizeData: 'Organiza datos de clase en un panel',
    landingUseAiTools: 'Usa herramientas de IA para acelerar la preparación',
    landingWhatTeachersExperience: 'Lo que Experimentan los Maestros',
    landingFasterPlanning: 'Planificación semanal más rápida',
    landingLessTimeGrading: 'Menos tiempo calificando',
    landingBetterOrganization: 'Mejor organización',
    landingMoreConfidence: 'Más confianza en el aula',
    landingMorePersonalTime: 'Más tiempo personal después de clases',
    landing3SimpleSteps: 'Comienza en 3 Pasos Simples',
    landingValueInMinutes: 'Verás valor en minutos — no horas.',
    landingCreateClasses: 'Crea Tus Clases',
    landingCreateClassesDesc: 'Configura tus clases y agrega estudiantes en segundos',
    landingGenerateWithAi: 'Genera Lecciones con IA',
    landingGenerateWithAiDesc: 'Crea planes de lecciones completos con asistencia de IA',
    landingManageGradesAttendance: 'Gestiona Calificaciones y Asistencia Fácilmente',
    landingManageGradesAttendanceDesc: 'Rastrea todo desde un panel intuitivo',
    landingBuiltForEducators: 'Diseñado Específicamente para Educadores',
    landingElementaryTeachers: 'Maestros de Primaria',
    landingMiddleSchoolEducators: 'Educadores de Escuela Intermedia',
    landingHighSchoolTeachers: 'Maestros de Escuela Superior',
    landingHomeschoolEducators: 'Educadores en el Hogar',
    landingInstructionalCoaches: 'Coaches Instruccionales',
    landingJoinThousands: 'Únete a Miles de Maestros',
    landingJoinThousandsDesc: 'Educadores en todo el país ya están usando TeacherHubPro para optimizar su flujo de trabajo, pasar más tiempo con estudiantes y recuperar sus noches.',
    landingReadyToSimplify: '¿Listo para Simplificar Tu Vida de Enseñanza?',
    landingStartFreeToday: 'Comenzar Gratis Hoy',
    landingExperienceTimeSaved: 'Comienza tu cuenta gratuita hoy y experimenta cuánto tiempo puedes ahorrar esta semana.',
    landingNoCreditCardRequired: 'Sin tarjeta de crédito requerida',
    landingSetupIn2Minutes: 'Configuración en menos de 2 minutos',
    landingCancelAnytime: 'Cancela cuando quieras',
    landingFullAccess: 'Acceso completo a todas las funciones',
    landingTeachersUsing: 'maestros ya usando TeacherHubPro',
    landingSimplifyingTeaching: 'Simplificando la enseñanza, un aula a la vez.',
    landingLogIn: 'Iniciar Sesión',
    landingStartFreeCta: 'Comenzar Gratis',
    landingBackToHome: 'Volver al Inicio',
    landingWelcomeBack: 'Bienvenido de Vuelta',
    landingSignInAccess: 'Inicia sesión para acceder a tu panel',
    landingStartFreeTrial: 'Comienza Tu Prueba Gratuita',
    landingJoinThousandsTeachers: 'Únete a miles de maestros ahorrando tiempo cada semana',
    landingCreateFreeAccount: 'Crear Cuenta Gratuita',
    landingSignInTab: 'Iniciar Sesión',
    landingContinueWithGoogle: 'Continuar con Google',
    landingOrWithEmail: 'o con correo electrónico',
    landingFullName: 'Nombre Completo',
    landingYourName: 'Tu nombre',
    landingYourEmail: 'tu@escuela.edu',
    landingCreatePassword: 'Crea una contraseña',
    landingCreatingAccount: 'Creando cuenta...',
    landingSigningIn: 'Iniciando sesión...',
    
    // Feature Sections (Spanish)
    landingEngageStudents: 'Involucra a los Estudiantes Como Nunca Antes',
    landingEngageStudentsDesc: 'Transforma tu aula con juegos interactivos, aprendizaje adaptativo y cuestionarios en vivo que mantienen a los estudiantes motivados y aprendiendo.',
    landingPlayToLearnTitle: 'Jugar para Aprender',
    landingPlayToLearnSubtitle: 'Juegos en Vivo Estilo Kahoot',
    landingPlayToLearnDesc: 'Organiza juegos de cuestionarios en vivo o asigna actividades a su propio ritmo. Los estudiantes se unen con un PIN y compiten en tiempo real con retroalimentación instantánea.',
    landingPlayToLearnFeature1: 'Cuestionarios multijugador en vivo',
    landingPlayToLearnFeature2: 'Tareas a su propio ritmo',
    landingPlayToLearnFeature3: 'Tablas de clasificación en tiempo real',
    landingPlayToLearnFeature4: '9+ modos de juego',
    landingEducationalGamesTitle: 'Juegos Educativos',
    landingEducationalGamesSubtitle: 'Actividades de Aprendizaje Divertidas',
    landingEducationalGamesDesc: 'Crea juegos personalizados para cualquier materia. Comparte con estudiantes mediante enlace o Google Classroom. Rastrea participación y progreso.',
    landingEducationalGamesFeature1: 'Plantillas de juegos personalizables',
    landingEducationalGamesFeature2: 'Comparte por enlace o código QR',
    landingEducationalGamesFeature3: 'Seguimiento del progreso estudiantil',
    landingEducationalGamesFeature4: 'Funciona en cualquier dispositivo',
    landingAdaptiveLearningTitle: 'Aprendizaje Adaptativo',
    landingAdaptiveLearningSubtitle: 'Rutas de Aprendizaje Personalizadas',
    landingAdaptiveLearningDesc: 'Rutas de aprendizaje impulsadas por IA que se adaptan al ritmo y nivel de cada estudiante. Identifica brechas y proporciona práctica dirigida.',
    landingAdaptiveLearningFeature1: 'Recomendaciones impulsadas por IA',
    landingAdaptiveLearningFeature2: 'Práctica personalizada',
    landingAdaptiveLearningFeature3: 'Análisis de progreso',
    landingAdaptiveLearningFeature4: 'Ajuste automático de dificultad',
    landingExploreFeature: 'Explorar',
    landingProductLabel: 'Producto',
    landingCompanyLabel: 'Empresa',
    landingLegalLabel: 'Legal',
    landingHelpCenter: 'Centro de Ayuda',
    landingPrivacyPolicyLink: 'Política de Privacidad',
    landingTermsOfServiceLink: 'Términos de Servicio',
    
    // Platform Integrations (Spanish)
    landingPlatformIntegrations: 'Integraciones de Plataforma',
    landingPlatformIntegrationsDesc: 'Conéctate con las herramientas que ya usas',
    landingAiAssistantTitle: 'Asistente IA',
    landingStudentProgressTitle: 'Progreso del Estudiante',
    landingPresentationsTitle: 'Presentaciones',
    
    // Google Classroom Section (Spanish)
    landingConnectPlatforms: 'Conecta con Tus Plataformas Favoritas',
    landingConnectPlatformsDesc: 'Integra sin problemas con las herramientas que tu escuela ya usa',
    landingIntegrated: 'INTEGRADO',
    landingImportClasses: 'Importa clases y listas automáticamente',
    landingSyncGrades: 'Sincroniza calificaciones con Google Classroom',
    landingSecurePrivate: 'Seguro y privado - protegido con OAuth 2.0',
    landingConnectGoogleClassroom: 'Conectar Google Classroom',
    landingComingSoon: 'Próximamente',
    landingComingSoonBadge: 'Próximamente',
    landingMoreIntegrations: '¡Más integraciones están en camino. Cuéntanos qué necesitas!',
    landingTrustedBySchools: 'Confiado por escuelas que usan Google Classroom en todo el mundo',
    
    // Homeschool Section (Spanish)
    landingForHomeschool: 'PARA EDUCACIÓN EN CASA',
    landingHomeschoolFriendly: 'Ideal para Homeschool',
    landingLearnMoreHomeschool: 'Más Sobre Funciones de Homeschool',
    landingHomeschoolAdaptiveDesc: 'Rutas impulsadas por IA que se adaptan al ritmo único de tu hijo',
    landingFlexibleCurriculum: 'Currículo Flexible',
    landingFlexibleCurriculumDesc: 'Personaliza materias y ritmo según estilos de aprendizaje',
    landingTextToSpeechDesc: 'Texto a voz integrado para aprendices auditivos',
    landingSafeContent: 'Contenido Seguro',
    landingSafeContentDesc: 'Materiales apropiados para la edad y verificados educativamente',
    
    // Pricing Page (Spanish)
    pricingTitle: 'Elige tu Plan',
    pricingSubtitle: 'Comienza con 7 días gratis. Cancela en cualquier momento.',
    pricingBack: 'Volver',
    pricingYourDigitalClassroom: 'Tu aula digital',
    pricingAllRightsReserved: 'Todos los derechos reservados.',
    pricingTrialExpired: 'Tu período de prueba ha expirado. Selecciona un plan para continuar usando TeacherHubPro.',
    pricingAdminAccount: 'Cuenta de Administrador - Acceso Completo',
    pricingFreeTrial: 'Período de prueba',
    pricingDaysRemaining: 'días restantes',
    pricingActiveSubscription: 'Suscripción Activa',
    pricingSchoolDistrict: 'Para Escuelas y Distritos',
    pricingNumberOfTeachers: 'Número de maestros',
    pricingTeachers: 'maestros',
    pricingMostPopular: 'MÁS POPULAR',
    pricingMonth: 'mes',
    pricingYear: 'año',
    pricingPerTeacher: 'por maestro',
    pricingBilledAnnually: 'Facturado anualmente',
    pricingGetStarted: 'Comenzar',
    pricingSubscribe: 'Suscribirse',
    pricingContactUs: 'Contáctanos',
    pricingCurrentPlan: 'Plan Actual',
    pricingStarterPlan: 'Inicial',
    pricingStarterDesc: 'Perfecto para maestros individuales',
    pricingProPlan: 'Pro',
    pricingProDesc: 'Funciones avanzadas para usuarios expertos',
    pricingSchoolPlan: 'Escuela',
    pricingSchoolDesc: 'Para escuelas completas con funciones de equipo',
    pricingDistrictPlan: 'Distrito',
    pricingDistrictDesc: 'Para distritos escolares con administración avanzada',
    pricingFeatureUnlimitedClasses: 'Clases Ilimitadas',
    pricingFeatureUnlimitedStudents: 'Estudiantes Ilimitados',
    pricingFeatureBasicLessonPlanning: 'Planificación Básica de Lecciones',
    pricingFeatureGradebook: 'Libro de Calificaciones',
    pricingFeatureAttendance: 'Seguimiento de Asistencia',
    pricingFeatureEverythingStarter: 'Todo en Inicial',
    pricingFeatureAIAssistant: 'Asistente IA (Claude)',
    pricingFeatureAdvancedAnalytics: 'Analíticas Avanzadas',
    pricingFeatureCustomTemplates: 'Plantillas Personalizadas',
    pricingFeaturePrioritySupport: 'Soporte Prioritario',
    pricingFeatureEverythingPro: 'Todo en Pro',
    pricingFeatureSchoolDashboard: 'Panel de Escuela',
    pricingFeatureTeacherManagement: 'Gestión de Maestros',
    pricingFeatureSharedResources: 'Recursos Compartidos',
    pricingFeatureBulkImport: 'Importación Masiva',
    pricingFeatureEverythingSchool: 'Todo en Escuela',
    pricingFeatureDistrictAnalytics: 'Analíticas de Distrito',
    pricingFeatureMultiSchool: 'Gestión Multi-Escuela',
    pricingFeatureCustomTraining: 'Capacitación Personalizada',
    pricingFeatureApiAccess: 'Acceso API',
    // Pricing Plans - Specific
    pricingIndividualMonthly: 'Mensual Individual',
    pricingIndividualMonthlyDesc: 'Acceso completo para maestros individuales',
    pricingIndividualYearly: 'Anual Individual',
    pricingIndividualYearlyDesc: 'El mejor valor para maestros individuales',
    pricingSchoolPlanName: 'Plan Escolar',
    pricingSchoolPlanDesc: 'Perfecto para escuelas pequeñas y medianas',
    pricingDistrictPlanName: 'Plan de Distrito',
    pricingDistrictPlanDesc: 'Para distritos escolares grandes',
    pricingSave: 'Ahorra',
    pricingMinimum: 'Mínimo',
    pricingForSchoolPlans: 'para planes escolares',
    pricingMinTeachers: 'Mínimo 10 maestros para Plan Escolar, 100 para Distrito',
    pricingSignUpFree: 'Regístrate Gratis',
    // Pricing Features
    pricingFeatureLessonPlanner: 'Planificador de lecciones',
    pricingFeatureAttendanceTracker: 'Seguimiento de asistencia',
    pricingFeaturePdfExport: 'Exportar a PDF',
    pricingFeatureTemplates: 'Plantillas',
    pricingFeatureEmailSupport: 'Soporte por email',
    pricingFeatureEverythingMonthly: 'Todo en Mensual',
    pricingFeature2MonthsFree: '2 meses gratis',
    pricingFeatureEarlyAccess: 'Actualizaciones anticipadas',
    pricingFeatureAllIndividual: 'Todo lo individual',
    pricingFeatureAdminDashboard: 'Panel de administrador',
    pricingFeatureSchoolBranding: 'Logo de la escuela',
    pricingFeatureSchoolReports: 'Reportes escolares',
    pricingFeatureUserManagement: 'Gestión de usuarios',
    pricingFeatureAllSchool: 'Todo lo escolar',
    pricingFeatureSsoIntegration: 'Integración SSO',
    pricingFeatureDedicatedSupport: 'Soporte dedicado',
    
    // Footer & Navigation (Marketing Pages)
    navFeatures: 'Funciones',
    navPricing: 'Precios',
    navTrust: 'Confianza',
    navContact: 'Contacto',
    navLogin: 'Iniciar Sesión',
    navStartFree: 'Comenzar Gratis',
    footerProduct: 'Producto',
    footerResources: 'Recursos',
    footerTrust: 'Confianza',
    footerLegal: 'Legal',
    footerFeatures: 'Funciones',
    footerPricing: 'Precios',
    footerIntegrations: 'Integraciones',
    footerHelpCenter: 'Centro de Ayuda',
    footerContact: 'Contacto',
    footerTrustCenter: 'Centro de Confianza',
    footerPrivacy: 'Privacidad',
    footerTerms: 'Términos',
    footerCookies: 'Cookies',
    footerAccessibility: 'Accesibilidad',
    footerTagline: 'El espacio de trabajo más inteligente para profesores.',
    footerSupportNotice: '¿Necesitas ayuda? Nuestros recursos de soporte están aquí para ayudarte a comenzar.',
    footerAllRightsReserved: 'Todos los derechos reservados.',
    startFreeTrial: 'Comenzar Prueba Gratis',
    viewPricing: 'Ver Precios',
    featuresHeroTitle: 'Herramientas poderosas diseñadas para profesores',
    featuresHeroSubtitle: 'TeacherHubPro reúne las herramientas esenciales que los educadores necesitan para planificar, organizar, crear y gestionar el trabajo del aula más eficientemente.',
    featuresCTATitle: 'Descubre cómo TeacherHubPro se adapta a tu día de enseñanza',
    featuresCTASubtitle: 'Explora la plataforma y descubre una mejor manera de planificar, organizar y enseñar.',
    contactTitle: 'Contáctanos',
    contactSendMessage: 'Envíanos un mensaje',
    contactName: 'Nombre',
    contactEmail: 'Correo Electrónico',
    contactSubject: 'Asunto',
    contactMessage: 'Mensaje',
    contactEmailSupport: 'Soporte por Email',
    contactHelpCenter: 'Centro de Ayuda',
    landingCTATitle: 'Comienza a simplificar tu flujo de trabajo de enseñanza hoy',
    landingCTASubtitle: 'Únete a los educadores que usan TeacherHubPro para ahorrar tiempo, mantenerse organizados y trabajar más inteligentemente con IA.',
    
    // Hero Section
    heroTitle: 'El Espacio de Trabajo Más Inteligente para Profesores',
    heroSubtitle: 'Planifica lecciones, gestiona calificaciones, registra asistencia, crea materiales de clase y ahorra horas cada semana con una plataforma impulsada por IA diseñada para profesores.',
    seeHowItWorks: 'Ver Cómo Funciona',
    heroSupportingText: 'Sin cargos extra por IA. Diseñado para profesores, tutores, educadores en casa y escuelas.',
    dashboardLessons: 'Lecciones',
    dashboardStudents: 'Estudiantes',
    dashboardAvgGrade: 'Promedio',
    hoursSavedPerWeek: 'horas ahorradas/semana',
    
    // Trust Bar
    trustBuiltForEducators: 'Diseñado para educadores modernos',
    trustAIIncluded: 'IA incluida en cada plan',
    trustSecureEasy: 'Seguro y fácil de usar',
    trustReduceWorkload: 'Reduce la carga administrativa',
    
    // Benefits Grid
    benefitSectionTitle: 'Todo lo que los profesores necesitan, en un solo lugar',
    benefitSectionSubtitle: 'Deja de cambiar entre documentos, hojas de cálculo y herramientas desconectadas. TeacherHubPro reúne tu flujo de trabajo principal en una plataforma simple.',
    benefitLessonPlanningTitle: 'Planificación de Lecciones',
    benefitLessonPlanningDesc: 'Crea planes de lección organizados y reutilizables en menos tiempo con plantillas integradas y soporte de IA.',
    benefitGradebookTitle: 'Libro de Calificaciones',
    benefitGradebookDesc: 'Registra el progreso de los estudiantes con un libro de calificaciones limpio y sencillo diseñado para el uso real en el aula.',
    benefitAttendanceTitle: 'Control de Asistencia',
    benefitAttendanceDesc: 'Gestiona la asistencia rápidamente y mantén los registros de clase organizados sin papeleo extra.',
    benefitAIToolsTitle: 'Herramientas de IA para Enseñanza',
    benefitAIToolsDesc: 'Genera ideas de lecciones, actividades, ejercicios de escritura, materiales de clase y más en segundos.',
    benefitPresentationsTitle: 'Presentaciones y Recursos',
    benefitPresentationsDesc: 'Crea materiales para clase más rápido y mantén todo conectado en un solo flujo de trabajo.',
    benefitWorkflowTitle: 'Flujo de Trabajo para Profesores',
    benefitWorkflowDesc: 'Diseñado para educadores que necesitan herramientas prácticas, no software complejo.',
    
    // Outcomes Section
    outcomeSectionTitle: 'Menos tiempo en administración, más tiempo enseñando',
    outcomePlanFaster: 'Planifica más rápido',
    outcomePlanFasterDesc: 'Crea planes de lecciones, actividades de clase y materiales sin empezar desde cero cada vez.',
    outcomeStayOrganized: 'Mantente organizado',
    outcomeStayOrganizedDesc: 'Mantén calificaciones, asistencia y recursos de enseñanza en un solo lugar en vez de dispersos en múltiples herramientas.',
    outcomeTeachConfidence: 'Enseña con confianza',
    outcomeTeachConfidenceDesc: 'Usa IA y flujos de trabajo optimizados para reducir el estrés y mantener tu trabajo de aula en movimiento.',
    
    // Social Proof Section
    socialProofSectionTitle: 'Construido para apoyar el trabajo real del aula',
    socialProofEducators: 'Diseñado específicamente para educadores',
    socialProofSupport: 'Incluye recursos de soporte y ayuda guiada',
    socialProofWorkflows: 'Construido para simplificar flujos de trabajo comunes de profesores',
    socialProofSuitable: 'Adecuado para profesores individuales, educadores en casa y uso escolar',
    socialProofHelpCenter: 'Recursos de ayuda disponibles • Centro de ayuda incluido',
    
    // Use Cases Section
    useCaseSectionTitle: 'Hecho para diferentes entornos de enseñanza',
    useCaseClassroomTitle: 'Profesores de Aula',
    useCaseClassroomDesc: 'Gestiona planificación, calificaciones y organización diaria del aula en un solo lugar.',
    useCaseHomeschoolTitle: 'Educadores en Casa',
    useCaseHomeschoolDesc: 'Crea flujos de trabajo flexibles para lecciones y mantén los planes de aprendizaje organizados.',
    useCaseTutorsTitle: 'Tutores y Programas Pequeños',
    useCaseTutorsDesc: 'Registra el trabajo de los estudiantes, planifica sesiones y crea recursos eficientemente.',
    useCaseSchoolsTitle: 'Escuelas y Equipos',
    useCaseSchoolsDesc: 'Apoya a los educadores con una plataforma que mejora la consistencia y ahorra tiempo.',
    
    // Feature Detail Pages - Common
    backToFeatures: 'Volver a Funciones',
    keyBenefits: 'Beneficios Clave',
    howItWorks: 'Cómo Funciona',
    readyToGetStarted: '¿Listo para comenzar?',
    featureCTASubtitle: 'Únete a miles de educadores que ya están ahorrando tiempo y mejorando su flujo de trabajo con TeacherHubPro.',
    exploreOtherFeatures: 'Explorar Otras Funciones',
    learnMore: 'Saber más',
    
    // Lesson Planning Feature
    featureLessonPlanningTitle: 'Planificación de Lecciones',
    featureLessonPlanningSubtitle: 'Crea planes de lección organizados y reutilizables en menos tiempo con plantillas integradas y soporte de IA.',
    featureLessonPlanningDescription: 'La herramienta de planificación de lecciones de TeacherHubPro te ayuda a crear planes de lección completos y alineados con los estándares de forma rápida y eficiente. Usa nuestras sugerencias impulsadas por IA para generar ideas, encontrar recursos y estructurar tus lecciones.',
    featureLessonPlanningBenefit1: 'Crea planes de lección hasta 5 veces más rápido con plantillas de IA',
    featureLessonPlanningBenefit2: 'Alinea las lecciones con los estándares curriculares automáticamente',
    featureLessonPlanningBenefit3: 'Reutiliza y adapta planes para diferentes clases o años',
    featureLessonPlanningBenefit4: 'Comparte planes con colegas y colabora en tiempo real',
    featureLessonPlanningHowItWorks1: 'Elige una plantilla o comienza desde cero, luego agrega tus objetivos de aprendizaje',
    featureLessonPlanningHowItWorks2: 'Usa sugerencias de IA para completar actividades, recursos y evaluaciones',
    featureLessonPlanningHowItWorks3: 'Guarda, organiza y reutiliza tus planes en clases y semestres',
    
    // Gradebook Feature
    featureGradebookTitle: 'Libro de Calificaciones',
    featureGradebookSubtitle: 'Registra el progreso de los estudiantes con un libro de calificaciones limpio y simple.',
    featureGradebookDescription: 'Nuestro libro de calificaciones está diseñado para ser intuitivo y poderoso. Registra tareas, calcula calificaciones automáticamente y genera informes de progreso con facilidad.',
    featureGradebookBenefit1: 'Cálculos automáticos de calificaciones con ponderación personalizable',
    featureGradebookBenefit2: 'Seguimiento visual del progreso de cada estudiante',
    featureGradebookBenefit3: 'Genera boletas e informes de progreso instantáneamente',
    featureGradebookBenefit4: 'Información impulsada por IA para identificar estudiantes con dificultades',
    featureGradebookHowItWorks1: 'Configura tus clases y agrega estudiantes',
    featureGradebookHowItWorks2: 'Crea tareas e ingresa calificaciones sobre la marcha',
    featureGradebookHowItWorks3: 'Revisa análisis y genera informes con un clic',
    
    // Attendance Feature
    featureAttendanceTitle: 'Control de Asistencia',
    featureAttendanceSubtitle: 'Gestiona la asistencia rápidamente y mantén los registros de clase organizados.',
    featureAttendanceDescription: 'Olvídate de las hojas de asistencia en papel. Nuestro sistema digital te permite tomar asistencia en segundos, rastrear patrones y generar informes.',
    featureAttendanceBenefit1: 'Toma asistencia en menos de 30 segundos por clase',
    featureAttendanceBenefit2: 'Detección automática de patrones de ausentismo crónico',
    featureAttendanceBenefit3: 'Genera informes de asistencia para administración',
    featureAttendanceBenefit4: 'Notificaciones opcionales a padres por ausencias',
    featureAttendanceHowItWorks1: 'Abre tu clase y toca para marcar presente, ausente o tardanza',
    featureAttendanceHowItWorks2: 'Revisa patrones y tendencias de asistencia a lo largo del tiempo',
    featureAttendanceHowItWorks3: 'Exporta informes o comparte con padres a través del portal',
    
    // AI Tools Feature
    featureAIToolsTitle: 'Herramientas de IA para Enseñanza',
    featureAIToolsSubtitle: 'Genera ideas de lecciones, actividades, ejercicios de escritura, materiales de clase y más en segundos.',
    featureAIToolsDescription: 'Aprovecha el poder de la IA para potenciar tu enseñanza. Genera ideas de lecciones adaptadas a tu currículo, crea hojas de trabajo diferenciadas, escribe preguntas de discusión atractivas y construye evaluaciones en minutos.',
    featureAIToolsBenefit1: 'Genera ideas de lecciones y actividades adaptadas a tu materia',
    featureAIToolsBenefit2: 'Crea materiales diferenciados para diversos aprendices',
    featureAIToolsBenefit3: 'Construye evaluaciones y rúbricas automáticamente',
    featureAIToolsBenefit4: 'Sin cargos adicionales de IA - incluido en cada plan',
    featureAIToolsHowItWorks1: 'Describe lo que necesitas - idea de lección, hoja de trabajo, examen, etc.',
    featureAIToolsHowItWorks2: 'La IA genera opciones basadas en tu nivel de grado y materia',
    featureAIToolsHowItWorks3: 'Edita, personaliza y guarda en tu biblioteca de recursos',
    
    // Presentations Feature
    featurePresentationsTitle: 'Presentaciones y Recursos',
    featurePresentationsSubtitle: 'Crea materiales para clase más rápido y mantén todo conectado en un flujo de trabajo.',
    featurePresentationsDescription: 'Crea presentaciones atractivas, hojas de trabajo y recursos de clase todo en un solo lugar. Nuestro creador de presentaciones se integra con tus planes de lección.',
    featurePresentationsBenefit1: 'Plantillas enfocadas en educación para varias materias',
    featurePresentationsBenefit2: 'Se integra con planes de lección y libro de calificaciones',
    featurePresentationsBenefit3: 'Comparte fácilmente con estudiantes vía enlace o LMS',
    featurePresentationsBenefit4: 'Importa y organiza recursos existentes',
    featurePresentationsHowItWorks1: 'Elige una plantilla o crea desde cero',
    featurePresentationsHowItWorks2: 'Agrega contenido, imágenes y elementos interactivos',
    featurePresentationsHowItWorks3: 'Vincula a tu plan de lección y comparte con tu clase',
    
    // Workflow Feature
    featureWorkflowTitle: 'Flujo de Trabajo para Profesores',
    featureWorkflowSubtitle: 'Diseñado para educadores que necesitan herramientas prácticas, no software complejo.',
    featureWorkflowDescription: 'Construimos TeacherHubPro específicamente para cómo trabajan realmente los profesores. Todo está diseñado para ser intuitivo, con una configuración mínima requerida.',
    featureWorkflowBenefit1: 'Interfaz intuitiva diseñada por educadores',
    featureWorkflowBenefit2: 'Funciona perfectamente en escritorio y móvil',
    featureWorkflowBenefit3: 'Configuración rápida - comienza a usar en minutos',
    featureWorkflowBenefit4: 'Se integra con herramientas existentes que ya usas',
    featureWorkflowHowItWorks1: 'Regístrate y configura tus clases en minutos',
    featureWorkflowHowItWorks2: 'Accede a todas las herramientas desde un panel unificado',
    featureWorkflowHowItWorks3: 'Tus datos se sincronizan automáticamente en todos tus dispositivos',
    
    // Classroom Teachers Use Case
    featureClassroomTeachersTitle: 'Para Profesores de Aula',
    featureClassroomTeachersSubtitle: 'Gestiona planificación, calificaciones y organización diaria del aula en un solo lugar.',
    featureClassroomTeachersDescription: 'Como profesor de aula, manejas múltiples clases, docenas de estudiantes e innumerables responsabilidades. TeacherHubPro reúne todas tus herramientas esenciales.',
    featureClassroomTeachersBenefit1: 'Planifica lecciones para múltiples clases en un espacio organizado',
    featureClassroomTeachersBenefit2: 'Registra calificaciones y asistencia de todos tus estudiantes',
    featureClassroomTeachersBenefit3: 'Genera comunicación con padres e informes de progreso',
    featureClassroomTeachersBenefit4: 'Accede a tus materiales desde el aula o casa',
    featureClassroomTeachersHowItWorks1: 'Configura tus clases e importa tu lista de estudiantes',
    featureClassroomTeachersHowItWorks2: 'Planifica lecciones, toma asistencia y registra calificaciones diariamente',
    featureClassroomTeachersHowItWorks3: 'Genera informes y comunícate con padres a través del portal',
    
    // Homeschool Use Case
    featureHomeschoolTitle: 'Para Educadores en Casa',
    featureHomeschoolSubtitle: 'Crea flujos de trabajo flexibles para lecciones y mantén los planes de aprendizaje organizados.',
    featureHomeschoolDescription: 'La educación en casa requiere flexibilidad, adaptabilidad y excelente organización. TeacherHubPro te ayuda a crear rutas de aprendizaje personalizadas para cada niño.',
    featureHomeschoolBenefit1: 'Crea rutas de aprendizaje personalizadas para cada niño',
    featureHomeschoolBenefit2: 'Mantén registros organizados para requisitos de cumplimiento',
    featureHomeschoolBenefit3: 'Actividades e ideas de lecciones generadas por IA',
    featureHomeschoolBenefit4: 'Horarios flexibles que se adaptan a las necesidades de tu familia',
    featureHomeschoolHowItWorks1: 'Configura perfiles para cada niño que enseñas',
    featureHomeschoolHowItWorks2: 'Crea y rastrea el progreso en planes de aprendizaje personalizados',
    featureHomeschoolHowItWorks3: 'Genera informes para revisiones de portafolio o evaluaciones',
    
    // Tutors Use Case
    featureTutorsTitle: 'Para Tutores y Programas Pequeños',
    featureTutorsSubtitle: 'Registra el trabajo de los estudiantes, planifica sesiones y crea recursos eficientemente.',
    featureTutorsDescription: 'Ya seas un tutor independiente o dirijas un pequeño programa de aprendizaje, TeacherHubPro te ayuda a mantenerte organizado y profesional.',
    featureTutorsBenefit1: 'Rastrea el progreso de múltiples estudiantes individualmente',
    featureTutorsBenefit2: 'Planifica sesiones y mantén notas de sesión',
    featureTutorsBenefit3: 'Genera informes de progreso para padres',
    featureTutorsBenefit4: 'Crea materiales personalizados para las necesidades de cada estudiante',
    featureTutorsHowItWorks1: 'Agrega tus estudiantes y sus objetivos de aprendizaje',
    featureTutorsHowItWorks2: 'Planifica sesiones y rastrea el progreso a lo largo del tiempo',
    featureTutorsHowItWorks3: 'Comparte el progreso con los padres a través del portal',
    
    // Schools Use Case
    featureSchoolsTitle: 'Para Escuelas y Equipos',
    featureSchoolsSubtitle: 'Apoya a los educadores con una plataforma que mejora la consistencia y ahorra tiempo.',
    featureSchoolsDescription: 'TeacherHubPro está diseñado para escalar con tu escuela o distrito. Proporciona a tus profesores herramientas consistentes y poderosas.',
    featureSchoolsBenefit1: 'Herramientas consistentes para todos los profesores de tu escuela',
    featureSchoolsBenefit2: 'Biblioteca de recursos compartida para materiales curriculares',
    featureSchoolsBenefit3: 'Panel de administración para monitoreo y soporte',
    featureSchoolsBenefit4: 'Precios por volumen para escuelas y distritos',
    featureSchoolsHowItWorks1: 'Contáctanos para precios de escuela o distrito',
    featureSchoolsHowItWorks2: 'Incorpora profesores con configuración guiada y capacitación',
    featureSchoolsHowItWorks3: 'Monitorea la adopción y proporciona soporte continuo'
  },
  fr: {
    art: "Arts plastiques",
    attendanceReport: "Rapport de présence",
    book: "Livre",
    brainstorming: "Remue-méninges",
    buildingBackground: "Mise en contexte",
    comprehensionQuestions: "Questions de compréhension",
    dictionary: "Dictionnaire",
    exercisePractice: "Exercices pratiques",
    foundationalSkills: "Compétences fondamentales",
    gradeEntry: "Saisie des notes",
    guidedReading: "Lecture guidée et chorale",
    handouts: "Documents",
    health: "Santé",
    language: "Langue",
    late: "En retard",
    level1Memory: "Niveau 1 : Mémoire",
    level2Processing: "Niveau 2 : Traitement",
    level3Strategic: "Niveau 3 : Pensée stratégique",
    level4Extended: "Niveau 4 : Pensée approfondie",
    listeningAndSpeaking: "Écoute/Expression orale",
    mathematics: "Mathématiques",
    missing: "Manquant",
    notebook: "Cahier",
    oralQuestions: "Questions orales",
    other: "Autre",
    physicalEducation: "Éducation physique",
    pricingDistrictDesc: "Pour les districts scolaires avec administration avancée",
    pricingDistrictPlan: "District",
    pricingFeatureAIAssistant: "Assistant IA (Claude)",
    pricingFeatureAdvancedAnalytics: "Analyses avancées",
    pricingFeatureAttendance: "Suivi des présences",
    pricingFeatureBasicLessonPlanning: "Planification de cours de base",
    pricingFeatureCustomTemplates: "Modèles personnalisés",
    pricingFeatureEverythingPro: "Tout ce qui est inclus dans Pro",
    pricingFeatureEverythingSchool: "Tout ce qui est inclus dans School",
    pricingFeatureEverythingStarter: "Tout ce qui est inclus dans Starter",
    pricingFeatureMultiSchool: "Gestion multi-écoles",
    pricingFeatureSchoolDashboard: "Tableau de bord de l'école",
    pricingFeatureSharedResources: "Ressources partagées",
    pricingFeatureTeacherManagement: "Gestion des enseignants",
    pricingFeatureUnlimitedClasses: "Classes illimitées",
    pricingFeatureUnlimitedStudents: "Élèves illimités",
    pricingProDesc: "Fonctionnalités avancées pour utilisateurs exigeants",
    pricingProPlan: "Pro",
    pricingSchoolDesc: "Pour les écoles entières avec fonctions d'équipe",
    pricingSchoolPlan: "École",
    pricingStarterDesc: "Parfait pour les enseignants individuels",
    pricingStarterPlan: "Starter",
    readPages: "Pages à lire",
    reading: "Lecture",
    religion: "Religion",
    science: "Sciences",
    socialStudies: "Sciences sociales",
    spanish: "Espagnol",
    teachersGuide: "Guide de l'enseignant",
    testQuiz: "Test/Quiz",
    vocabularyDevelopment: "Développement du vocabulaire",
    writing: "Écriture",

    // Navigation
    dashboard: 'Tableau de Bord',
    planner: 'Planificateur',
    attendance: 'Présence',
    gradebook: 'Carnet de Notes',
    classes: 'Classes',
    settings: 'Paramètres',
    logout: 'Déconnexion',
    
    // Auth
    login: 'Connexion',
    register: "S'inscrire",
    email: 'Email',
    password: 'Mot de Passe',
    name: 'Nom',
    signInWithGoogle: 'Se connecter avec Google',
    orContinueWith: 'ou continuer avec',
    dontHaveAccount: "Pas de compte?",
    alreadyHaveAccount: 'Déjà un compte?',
    
    // Dashboard
    welcome: 'Bienvenue',
    todayOverview: "Aperçu d'Aujourd'hui",
    quickActions: 'Actions Rapides',
    attendancePending: 'Présence en Attente',
    upcomingAssignments: 'Devoirs à Venir',
    recentPlans: 'Plans Récents',
    totalClasses: 'Total des Classes',
    totalStudents: 'Total des Élèves',
    totalPlans: 'Total des Plans',
    
    // Classes
    addClass: 'Ajouter Classe',
    className: 'Nom de Classe',
    grade: 'Niveau',
    section: 'Section',
    subject: 'Matière',
    yearTerm: 'Année/Période',
    students: 'Élèves',
    addStudent: 'Ajouter Élève',
    firstName: 'Prénom',
    lastName: 'Nom',
    studentNumber: "Numéro d'Élève",
    parentEmail: 'Email du Parent',
    notes: 'Notes',
    accommodations: 'Aménagements',
    
    // Planner
    weeklyPlan: 'Plan Hebdomadaire',
    createPlan: 'Créer Plan',
    weekOf: 'Semaine du',
    unit: 'Unité',
    story: 'Histoire/Titre',
    objectiveOfWeek: 'Objectif de la Semaine',
    skillsOfWeek: 'Compétences de la Semaine',
    dayTheme: 'Thème du Jour',
    activities: 'Activités',
    materials: 'Matériels',
    standards: 'Standards',
    expectations: 'Attentes',
    firstWeek: 'Première Semaine',
    secondWeek: 'Deuxième Semaine',
    saveAsTemplate: 'Enregistrer comme Modèle',
    duplicateLastWeek: 'Dupliquer Semaine Précédente',
    exportPdf: 'Exporter PDF',
    
    // Common
    save: 'Enregistrer',
    cancel: 'Annuler',
    delete: 'Supprimer',
    edit: 'Modifier',
    view: 'Voir',
    search: 'Rechercher',
    filter: 'Filtrer',
    loading: 'Chargement...',
    noData: 'Aucune donnée disponible',
    confirmDelete: 'Êtes-vous sûr de vouloir supprimer?',
    success: 'Succès',
    error: 'Erreur',
    from: 'De',
    to: 'À',
    date: 'Date',
    teacher: 'Enseignant',
    selectLanguage: 'Sélectionner la Langue',
    
    // Days
    monday: 'Lundi',
    tuesday: 'Mardi',
    wednesday: 'Mercredi',
    thursday: 'Jeudi',
    friday: 'Vendredi',
    
    // Attendance
    takeAttendance: 'Prendre la Présence',
    present: 'Présent',
    absent: 'Absent',
    tardy: 'En Retard',
    excused: 'Excusé',
    markAllPresent: 'Marquer Tous Présents',
    submitAttendance: 'Soumettre Présence',
    
    // Gradebook
    assignments: 'Devoirs',
    createAssignment: 'Créer Devoir',
    title: 'Titre',
    description: 'Description',
    points: 'Points',
    dueDate: 'Date Limite',
    category: 'Catégorie',
    homework: 'Devoir',
    quiz: 'Quiz',
    test: 'Examen',
    project: 'Projet',
    
    // Games
    educationalGames: 'Jeux Éducatifs',
    myGames: 'Mes Jeux',
    createGame: 'Créer Jeu',
    play: 'Jouer',
    copyLink: 'Copier Lien',
    shareToClassroom: 'Partager sur Classroom',
    deleteGame: 'Supprimer Jeu',
    questions: 'Questions',
    gameType: 'Type de Jeu',
    gradeLevel: 'Niveau Scolaire',
    
    // Landing Page
    landingHeroTitle: 'Votre classe complète en une plateforme',
    landingHeroSubtitle: 'Planification, présence, notes et assistant IA. Tout ce dont un enseignant a besoin.',
    landingBuiltForTeachers: 'Conçu pour les enseignants',
    landingStartFree: 'Commencer gratuitement',
    landingViewPricing: 'Voir les tarifs',
    landingUsedBySchools: 'Utilisé par des écoles partout dans le monde',
    landingEverythingYouNeed: 'Tout ce dont vous avez besoin',
    landingEverythingDesc: 'Une plateforme complète conçue spécialement pour les enseignants.',
    landingFeatures: 'Fonctionnalités',
    landingAiAssistant: 'Assistant IA',
    landingPoweredBy: 'Propulsé par Anthropic',
    landingActiveTeachers: 'enseignants actifs',
    landingHomeschool: 'École à la Maison',
    landingIntegrations: 'Intégrations',
    landingCustomization: 'Personnalisation',
    landingPricing: 'Tarifs',
    landingGetStarted: 'Commencer - Se Connecter',
    landingStart: 'Commencer',
    landingAiLessonPlan: 'Plan IA',
    landingPresentations: 'Présentations',
    landingGrades: 'Notes',
    landingPlanFaster: 'Planifiez plus vite',
    landingPlanFasterDesc: 'Créez des plans de cours hebdomadaires en quelques secondes.',
    landingWeek1: 'Semaine 1',
    landingYourBrand: 'Votre marque, votre style',
    landingYourBrandDesc: 'Personnalisez avec le logo et les couleurs de votre école.',
    landingYourSchool: 'Votre École',
    landingCustomBranding: 'Marque personnalisée',
    landingExportPro: 'Exportez professionnellement',
    landingExportProDesc: 'Générez des PDFs prêts à imprimer: plans, bulletins, dossiers.',
    landingDownloadPdf: 'Télécharger PDF',
    landingAiAssistantTitle: 'Assistant IA',
    landingAiAssistantDesc: 'Générez des plans de cours, activités et questions avec l\'IA.',
    landingLessonPlans: 'Plans de cours',
    landingQuizzesTests: 'Quiz et examens',
    landingDigitalClassroom: 'Votre classe numérique',
    landingTryFree: 'Essai gratuit de 30 jours',
    landingNoCard: 'Aucune carte de crédit requise',
    landingFullName: 'Nom Complet',
    landingCreateAccount: 'Créer un Compte Gratuit',
    landingOrLoginWith: 'Ou connectez-vous avec',
    landingHaveAccount: 'Avez-vous un compte?',
    landingLoginHere: 'Connectez-vous ici',
    landingNeedAccount: 'Besoin d\'un compte?',
    landingRegisterHere: 'Inscrivez-vous ici',
    // AI Section
    landingAiTeachingAssistant: 'Assistant IA pour l\'Enseignement',
    landingYouAsked: 'Vous avez demandé:',
    landingAiResponse: 'Réponse IA:',
    landingAiQuestion: 'Comment enseigner les fractions équivalentes en CM1?',
    landingAiAnswer: 'Voici un plan de cours basé sur les standards avec des activités pratiques...',
    landingQuizzes: 'Quiz',
    landingWorksheets: 'Fiches de travail',
    landingIdeas: 'Idées',
    landingNewAi: 'Nouveau: Intelligence Artificielle',
    landingAiAssistantToTeach: 'Votre assistant IA pour mieux enseigner',
    landingAiAssistantDesc2: 'Générez des plans de cours, quiz, résumés et activités basés sur les standards, instantanément.',
    landingAlignedStandards: 'Aligné avec les programmes officiels',
    landingGenerateSeconds: 'Générez du contenu en secondes',
    landingBilingual: 'Multilingue',
    landingPoweredBy: 'Propulsé par Claude Sonnet 4.5',
    landingTryItFree: 'Essayer gratuitement',
    landingIncludedSubscription: 'Inclus dans l\'abonnement',
    // Integrations Section
    landingConnectTools: 'Connectez vos outils favoris',
    landingConnectToolsDesc: 'Intégrez les plateformes que vous utilisez déjà.',
    landingCalendarSync: 'Sync Calendrier',
    landingCalendarSyncDesc: 'Exportez vos plans vers Google Calendar, Outlook ou Apple Calendar.',
    landingGoogleClassroom: 'Google Classroom',
    landingGoogleClassroomDesc: 'Partagez activités et jeux directement avec vos élèves.',
    landingPdfExport: 'Export PDF',
    landingPdfExportDesc: 'Générez des PDFs professionnels pour plans, bulletins et plus.',
    landingEmailNotifications: 'Notifications Email',
    landingEmailNotificationsDesc: 'Envoyez des rapports automatiques aux parents et administrateurs.',
    landingViewIntegrations: 'Voir toutes les intégrations',
    // Customization Section
    landingYourBrandYourWay: 'Votre marque, à votre façon',
    landingCustomizeDesc: 'Personnalisez tout avec les couleurs, logo et préférences de votre école.',
    landingSchoolLogo: 'Logo de l\'École',
    landingSchoolLogoDesc: 'Ajoutez votre logo à tous les documents et rapports.',
    landingColorThemes: 'Thèmes de Couleur',
    landingColorThemesDesc: 'Choisissez des couleurs qui correspondent à votre identité.',
    landingCustomTemplates: 'Modèles Personnalisés',
    landingCustomTemplatesDesc: 'Créez des modèles réutilisables pour vos plans.',
    landingMultiLanguage: 'Multi-Langue',
    landingMultiLanguageDesc: 'Travaillez en plusieurs langues.',
    // Homeschool Section
    landingHomeschoolTitle: 'École à la Maison',
    landingHomeschoolSubtitle: 'Votre compagnon complet pour l\'école à la maison',
    landingHomeschoolDesc: 'Tout ce dont vous avez besoin pour une expérience réussie d\'école à la maison.',
    landingAdaptiveLearning: 'Apprentissage Adaptatif',
    landingAdaptiveLearningDesc: 'Des parcours qui s\'adaptent au rythme de chaque enfant.',
    landingFullVisibility: 'Visibilité Totale',
    landingFullVisibilityDesc: 'Suivez les progrès en temps réel avec des rapports détaillés.',
    landingFlexibleCurriculum: 'Programme Flexible',
    landingFlexibleCurriculumDesc: 'Personnalisez le programme selon les besoins de votre famille.',
    landing100Customizable: '100% Personnalisable',
    landingDownloadLearn: 'Télécharger et Apprendre',
    landingDownloadLearnDesc: 'Exportez des matériaux pour l\'apprentissage hors ligne.',
    landingPdfExportFeature: 'Export PDF',
    landingTextToSpeech: 'Texte en Parole',
    landingTextToSpeechDesc: 'Support audio pour tout le contenu de lecture.',
    landingAudioLearning: 'Apprentissage Audio',
    landingSafeContent: 'Contenu Sécurisé',
    landingSafeContentDesc: 'Matériaux appropriés à l\'âge et vérifiés.',
    landingParentalControl: 'Contrôle Parental',
    landingSubjectModules: 'Modules par Matière',
    landingMathematics: 'Mathématiques',
    landingLanguageArts: 'Français',
    landingScience: 'Sciences',
    landingAstronomy: 'Astronomie',
    landingGeography: 'Géographie',
    landingHistory: 'Histoire',
    landingArt: 'Art',
    landingPhysicalEd: 'Éducation Physique',
    landingStartHomeschooling: 'Commencer l\'École à la Maison',
    // CTA Section
    landing7DaysFree: '7 jours gratuits',
    landingGetStartedToday: 'Commencez aujourd\'hui',
    landingStartYourJourney: 'Commencez votre parcours de classe numérique. Sans carte de crédit.',
    landingNoCreditCard: 'Sans carte de crédit requise',
    landingSetup2Min: 'Configuration en 2 minutes',
    landingCancelAnytime: 'Annulez à tout moment',
    landingSupportBilingual: 'Support multilingue',
    landingActiveTeachers: 'enseignants actifs',
    landingSignIn: 'Se connecter',
    landingCreateAccountTab: 'Créer un compte',
    landingContinueGoogle: 'Continuer avec Google',
    landingOrWithEmail: 'ou avec email',
    landingEmail: 'Email',
    landingPassword: 'Mot de passe',
    landingFullNameLabel: 'Nom complet',
    landingCreateFreeAccount: 'Créer un compte gratuit',
    // Footer
    landingContact: 'Contact',
    landingPrivacyPolicy: 'Politique de Confidentialité',
    landingHelp: 'Aide',
    landingTermsOfUse: 'Conditions d\'Utilisation',
    landingCookiesPolicy: 'Politique des Cookies',
    landingAccessibility: 'Accessibilité',
    landingAllRightsReserved: 'Tous droits réservés.',
    
    // AI Features Section
    landingAdvancedAiFeatures: 'Fonctionnalités IA Avancées',
    landingIntelligentPlanning: 'Planification intelligente avec IA',
    landingAiPlanningDesc: 'De la génération d\'un plan complet aux suggestions quotidiennes, l\'IA vous aide à chaque étape.',
    landingGenerateFullWeek: 'Générer Semaine Complète',
    landingGenerateFullWeekDesc: 'En un clic, générez des activités pour les 5 jours avec progression pédagogique : intro → pratique → maîtrise → évaluation.',
    landingDays: 'jours',
    landingProgression: 'Progression',
    landingDailySuggestions: 'Suggestions Quotidiennes',
    landingDailySuggestionsDesc: 'Chaque jour montre sa phase pédagogique (Introduction, Pratique, Maîtrise). Obtenez des suggestions spécifiques pour chaque étape.',
    landingMonFri: 'Lun → Ven',
    landingContextual: 'Contextuel',
    landingReusableTemplates: 'Modèles Réutilisables',
    landingReusableTemplatesDesc: 'Enregistrez vos meilleurs plans comme modèles. Clonez et personnalisez pour de nouveaux sujets en un clic.',
    landingSave: 'Enregistrer',
    landingClone: 'Cloner',
    landing5StarterTemplates: '5 Modèles de Départ',
    landing5StarterTemplatesDesc: 'Commencez immédiatement avec des modèles professionnels : Fractions, Compréhension de Lecture, Méthode Scientifique, Écriture et Multiplication.',
    landingMath: 'Maths',
    landingELA: 'Français',
    landingAiCustomize: 'Personnaliser avec IA',
    landingAiCustomizeDesc: 'Prenez n\'importe quel modèle et adaptez-le à un nouveau sujet. L\'IA garde la structure réussie tout en changeant le contenu.',
    landingNewTopic: 'Nouveau sujet',
    landingSameStructure: 'Même structure',
    landingTemplateOfWeek: 'Modèle de la Semaine',
    landingTemplateOfWeekDesc: 'Chaque semaine, nous mettons en avant un modèle différent sur votre tableau de bord avec des conseils de personnalisation.',
    landingWeeklyRotation: 'Rotation hebdomadaire',
    landingTips: 'Conseils',
    landingTryAiFree7Days: 'Essayez l\'IA gratuitement pendant 7 jours',
    
    // AI Presentations Section
    landingNewAiPresentations: 'NOUVEAU ! Présentations IA',
    landingCreatePresentations: 'Créez des présentations captivantes',
    landingPresentationsDesc: 'Générez des présentations éducatives complètes avec l\'IA. Choisissez des thèmes visuels, ajoutez des images, appliquez des transitions et sauvegardez.',
    landingAiGenerate: 'Générer avec IA',
    landingAiGenerateDesc: '6+ diapositives en secondes',
    landing8VisualThemes: '8 Thèmes Visuels',
    landing8VisualThemesDesc: 'Océan, Galaxie, Tableau noir...',
    landingSaveAndLoad: 'Sauvegarder et Charger',
    landingSaveAndLoadDesc: 'Bibliothèque personnelle',
    landingDownloadHtml: 'Télécharger HTML',
    landingDownloadHtmlDesc: 'Utiliser hors ligne',
    landingCreateFirstPresentation: 'Créer ma première présentation',
    landingTheSolarSystem: 'Le Système Solaire',
    landingScienceGrade: 'Sciences - CM1',
    landingTransitionsEnabled: '✨ Transitions activées',
    
    // Customization Section
    landingFullCustomization: 'Personnalisation complète',
    landingSchoolBrandingTitle: 'L\'identité de votre école sur chaque document',
    landingSchoolBrandingDesc: 'De votre logo à vos couleurs, chaque PDF et rapport reflète l\'image de votre établissement.',
    landingSchoolLogoAll: 'Le logo de votre école sur tous les documents',
    landingCustomColorsMatch: 'Couleurs personnalisées assorties à votre marque',
    landingPdfFormats: 'Les formats PDF exacts de votre école',
    landingCentralizedAdmin: 'Tableau de bord administrateur centralisé',
    landingTeachersPlanning: 'Planification de l\'Enseignant',
    landingYourLogoHere: 'Votre logo ici',
    landingYourColors: 'Vos couleurs',
    
    // Integrations Section
    landingConnectFavorite: 'Connectez-vous à vos plateformes préférées',
    landingConnectFavoriteDesc: 'Synchronisez avec les outils que vous utilisez déjà pour un flux de travail fluide.',
    landingAvailable: 'Disponible',
    landingMostPopular: 'Le plus populaire',
    landingImportClasses: 'Importer des Classes',
    landingImportClassesDesc: 'Importez vos classes et listes d\'élèves automatiquement',
    landingSyncGrades: 'Synchroniser les Notes',
    landingSyncGradesDesc: 'Envoyez les notes à Google Classroom en un clic',
    landingSecurePrivate: 'Sécurisé et Privé',
    landingSecurePrivateDesc: 'Authentification OAuth sécurisée, vos données protégées',
    landingSetupMinutes: 'Configuration en minutes',
    landingNoExtraCost: 'Sans frais supplémentaires',
    landingSupportIncluded: 'Support inclus',
    landingComingSoon: 'Bientôt Disponible',
    landingNeedIntegration: 'Besoin d\'une autre intégration ?',
    landingNeedIntegrationDesc: 'Nous ajoutons constamment de nouvelles intégrations. Dites-nous quelles plateformes vous utilisez et nous les prioriserons.',
    landingRequestIntegration: 'Demander une Intégration',
    
    // Homeschool Section
    landingPerfectHomeschool: 'Parfait pour les Éducateurs à Domicile',
    landingHomeschoolCompleteDesc: 'Tout ce dont vous avez besoin pour créer une expérience d\'apprentissage personnalisée, de la planification au suivi des progrès.',
    landingAiPowered: 'Propulsé par l\'IA',
    landingParentDashboard: 'Tableau de Bord Parent',
    landingParentDashboardDesc: 'Vue complète des progrès, notes et travaux à venir de chaque enfant dans un tableau de bord unifié.',
    landingFlexibleCurriculumDesc2: 'Personnalisez les matières, le rythme et les méthodes d\'enseignement selon le style d\'apprentissage unique de chaque enfant.',
    landing100Customizable: '100% Personnalisable',
    landingDownloadAndLearn: 'Télécharger et Apprendre',
    landingDownloadAndLearnDesc: 'Exportez les leçons et matériaux en PDF. Continuez à apprendre même hors ligne ou en déplacement.',
    landingAudioLearning: 'Apprentissage Audio',
    landingTextToSpeechDesc2: 'Synthèse vocale intégrée pour tout le contenu. Parfait pour les apprenants auditifs ou l\'aide à la lecture.',
    landingSafeContentDesc2: 'Tout le contenu est adapté à l\'âge et vérifié pédagogiquement. Contrôle parental total sur ce que les enfants peuvent accéder.',
    
    // New Landing Page Copy (French)
    landingSave5Hours: 'Économisez 5+ Heures Chaque Semaine',
    landingSave5HoursTitle: 'Économisez 5+ Heures Chaque Semaine sur la Planification, la Notation et l\'Administration',
    landingSave5HoursSubtitle: 'TeacherHubPro aide les éducateurs occupés à créer des plans de cours, gérer les notes, suivre la présence et utiliser l\'IA pour simplifier le travail quotidien — tout en un seul endroit.',
    landingBuiltForBusyTeachers: 'Conçu pour les enseignants occupés',
    landingStartFreeNoCreditCard: 'Commencer Gratuitement — Sans Carte de Crédit',
    landingSeeHowItWorks: 'Voir Comment Ça Marche',
    landingTeachingIsMore: 'Enseigner c\'est Plus qu\'Enseigner',
    landingTeachingIsMoreDesc: 'Entre la planification, la notation, la présence, les rapports et la communication — la plupart des enseignants passent des heures chaque semaine sur des tâches qui volent du temps aux élèves et à la vie personnelle.',
    landingYouBecameTeacher: 'Vous êtes devenu enseignant pour éduquer.',
    landingNotToDrown: 'Pas pour vous noyer dans la paperasse.',
    landingEverythingSimple: 'Tout ce dont vous avez besoin. Une Plateforme Simple.',
    landingLessBusywork: 'Moins de travail administratif. Plus d\'enseignement.',
    landingGenerateLessons: 'Générez des plans de cours structurés en minutes',
    landingTrackGrades: 'Suivez les notes avec un carnet de notes numérique facile à utiliser',
    landingRecordAttendance: 'Enregistrez la présence rapidement',
    landingOrganizeData: 'Organisez les données de classe dans un tableau de bord',
    landingUseAiTools: 'Utilisez des outils IA pour accélérer la préparation',
    landingWhatTeachersExperience: 'Ce que les Enseignants Expérimentent',
    landingFasterPlanning: 'Planification hebdomadaire plus rapide',
    landingLessTimeGrading: 'Moins de temps à noter',
    landingBetterOrganization: 'Meilleure organisation',
    landingMoreConfidence: 'Plus de confiance en classe',
    landingMorePersonalTime: 'Plus de temps personnel après l\'école',
    landing3SimpleSteps: 'Commencez en 3 Étapes Simples',
    landingValueInMinutes: 'Vous verrez la valeur en minutes — pas en heures.',
    landingCreateClasses: 'Créez Vos Classes',
    landingCreateClassesDesc: 'Configurez vos classes et ajoutez des élèves en secondes',
    landingGenerateWithAi: 'Générez des Cours avec l\'IA',
    landingGenerateWithAiDesc: 'Créez des plans de cours complets avec l\'assistance IA',
    landingManageGradesAttendance: 'Gérez les Notes et la Présence Facilement',
    landingManageGradesAttendanceDesc: 'Suivez tout depuis un tableau de bord intuitif',
    landingBuiltForEducators: 'Conçu Spécifiquement pour les Éducateurs',
    landingElementaryTeachers: 'Enseignants du Primaire',
    landingMiddleSchoolEducators: 'Éducateurs du Collège',
    landingHighSchoolTeachers: 'Enseignants du Lycée',
    landingHomeschoolEducators: 'Éducateurs à Domicile',
    landingInstructionalCoaches: 'Coachs Pédagogiques',
    landingJoinThousands: 'Rejoignez des Milliers d\'Enseignants',
    landingJoinThousandsDesc: 'Des éducateurs à travers le pays utilisent déjà TeacherHubPro pour optimiser leur flux de travail, passer plus de temps avec les élèves et récupérer leurs soirées.',
    landingReadyToSimplify: 'Prêt à Simplifier Votre Vie d\'Enseignant?',
    landingStartFreeToday: 'Commencer Gratuitement Aujourd\'hui',
    landingExperienceTimeSaved: 'Commencez votre compte gratuit aujourd\'hui et découvrez combien de temps vous pouvez économiser cette semaine.',
    landingNoCreditCardRequired: 'Sans carte de crédit requise',
    landingSetupIn2Minutes: 'Configuration en moins de 2 minutes',
    landingCancelAnytime: 'Annulez quand vous voulez',
    landingFullAccess: 'Accès complet à toutes les fonctionnalités',
    landingTeachersUsing: 'enseignants utilisent déjà TeacherHubPro',
    landingSimplifyingTeaching: 'Simplifier l\'enseignement, une classe à la fois.',
    landingLogIn: 'Se Connecter',
    landingStartFreeCta: 'Commencer Gratuitement',
    landingBackToHome: 'Retour à l\'Accueil',
    landingWelcomeBack: 'Bon Retour',
    landingSignInAccess: 'Connectez-vous pour accéder à votre tableau de bord',
    landingStartFreeTrial: 'Commencez Votre Essai Gratuit',
    landingJoinThousandsTeachers: 'Rejoignez des milliers d\'enseignants qui économisent du temps chaque semaine',
    landingCreateFreeAccount: 'Créer un Compte Gratuit',
    landingSignInTab: 'Se Connecter',
    landingContinueWithGoogle: 'Continuer avec Google',
    landingOrWithEmail: 'ou avec email',
    landingFullName: 'Nom Complet',
    landingYourName: 'Votre nom',
    landingYourEmail: 'vous@ecole.edu',
    landingCreatePassword: 'Créez un mot de passe',
    landingCreatingAccount: 'Création du compte...',
    landingSigningIn: 'Connexion...',
    
    // Feature Sections (French)
    landingEngageStudents: 'Engagez les Élèves Comme Jamais Auparavant',
    landingEngageStudentsDesc: 'Transformez votre classe avec des jeux interactifs, un apprentissage adaptatif et des quiz en direct qui maintiennent les élèves motivés et apprenant.',
    landingPlayToLearnTitle: 'Apprendre en Jouant',
    landingPlayToLearnSubtitle: 'Jeux en Direct Style Kahoot',
    landingPlayToLearnDesc: 'Organisez des jeux de quiz en direct ou assignez des activités à leur rythme. Les élèves rejoignent avec un PIN et rivalisent en temps réel avec un feedback instantané.',
    landingPlayToLearnFeature1: 'Quiz multijoueur en direct',
    landingPlayToLearnFeature2: 'Devoirs à leur rythme',
    landingPlayToLearnFeature3: 'Classements en temps réel',
    landingPlayToLearnFeature4: '9+ modes de jeu',
    landingEducationalGamesTitle: 'Jeux Éducatifs',
    landingEducationalGamesSubtitle: 'Activités d\'Apprentissage Amusantes',
    landingEducationalGamesDesc: 'Créez des jeux personnalisés pour n\'importe quelle matière. Partagez avec les élèves via lien ou Google Classroom. Suivez l\'engagement et les progrès.',
    landingEducationalGamesFeature1: 'Modèles de jeux personnalisables',
    landingEducationalGamesFeature2: 'Partagez par lien ou code QR',
    landingEducationalGamesFeature3: 'Suivi des progrès des élèves',
    landingEducationalGamesFeature4: 'Fonctionne sur tout appareil',
    landingAdaptiveLearningTitle: 'Apprentissage Adaptatif',
    landingAdaptiveLearningSubtitle: 'Parcours d\'Apprentissage Personnalisés',
    landingAdaptiveLearningDesc: 'Parcours d\'apprentissage alimentés par l\'IA qui s\'adaptent au rythme et au niveau de chaque élève. Identifiez les lacunes et fournissez une pratique ciblée.',
    landingAdaptiveLearningFeature1: 'Recommandations alimentées par l\'IA',
    landingAdaptiveLearningFeature2: 'Pratique personnalisée',
    landingAdaptiveLearningFeature3: 'Analytique des progrès',
    landingAdaptiveLearningFeature4: 'Ajustement automatique de la difficulté',
    landingExploreFeature: 'Explorer',
    landingProductLabel: 'Produit',
    landingCompanyLabel: 'Entreprise',
    landingLegalLabel: 'Juridique',
    landingHelpCenter: 'Centre d\'Aide',
    landingPrivacyPolicyLink: 'Politique de Confidentialité',
    landingTermsOfServiceLink: 'Conditions d\'Utilisation',
    
    // Platform Integrations (French)
    landingPlatformIntegrations: 'Intégrations de Plateforme',
    landingPlatformIntegrationsDesc: 'Connectez-vous avec les outils que vous utilisez déjà',
    landingAiAssistantTitle: 'Assistant IA',
    landingStudentProgressTitle: 'Progrès des Élèves',
    landingPresentationsTitle: 'Présentations',
    
    // Google Classroom Section (French)
    landingConnectPlatforms: 'Connectez Vos Plateformes Favorites',
    landingConnectPlatformsDesc: 'Intégrez facilement les outils que votre école utilise déjà',
    landingIntegrated: 'INTÉGRÉ',
    landingImportClasses: 'Importez classes et listes automatiquement',
    landingSyncGrades: 'Synchronisez les notes avec Google Classroom',
    landingSecurePrivate: 'Sécurisé et privé - protégé par OAuth 2.0',
    landingConnectGoogleClassroom: 'Connecter Google Classroom',
    landingComingSoon: 'Bientôt Disponible',
    landingComingSoonBadge: 'Bientôt',
    landingMoreIntegrations: 'Plus d\'intégrations arrivent. Dites-nous ce dont vous avez besoin!',
    landingTrustedBySchools: 'Approuvé par les écoles utilisant Google Classroom dans le monde entier',
    
    // Homeschool Section (French)
    landingForHomeschool: 'POUR L\'ÉCOLE À LA MAISON',
    landingHomeschoolFriendly: 'Adapté à l\'École à la Maison',
    landingLearnMoreHomeschool: 'En Savoir Plus sur les Fonctionnalités École à la Maison',
    landingHomeschoolAdaptiveDesc: 'Parcours alimentés par l\'IA qui s\'adaptent au rythme unique de votre enfant',
    landingFlexibleCurriculum: 'Programme Flexible',
    landingFlexibleCurriculumDesc: 'Personnalisez les matières et le rythme selon les styles d\'apprentissage',
    landingTextToSpeechDesc: 'Synthèse vocale intégrée pour les apprenants auditifs',
    landingSafeContent: 'Contenu Sécurisé',
    landingSafeContentDesc: 'Matériaux adaptés à l\'âge et vérifiés pédagogiquement',
    
    // Pricing Page (French)
    pricingTitle: 'Choisissez Votre Plan',
    pricingSubtitle: 'Commencez avec un essai gratuit de 7 jours. Annulez à tout moment.',
    pricingBack: 'Retour',
    pricingYourDigitalClassroom: 'Votre classe numérique',
    pricingAllRightsReserved: 'Tous droits réservés.',
    pricingTrialExpired: 'Votre période d\'essai a expiré. Veuillez sélectionner un plan pour continuer.',
    pricingAdminAccount: 'Compte Admin - Accès Complet',
    pricingFreeTrial: 'Essai Gratuit',
    pricingDaysRemaining: 'jours restants',
    pricingActiveSubscription: 'Abonnement Actif',
    pricingSchoolDistrict: 'Pour Écoles et Districts',
    pricingNumberOfTeachers: 'Nombre d\'enseignants',
    pricingTeachers: 'enseignants',
    pricingMostPopular: 'LE PLUS POPULAIRE',
    pricingMonth: 'mois',
    pricingYear: 'an',
    pricingPerTeacher: 'par enseignant',
    pricingBilledAnnually: 'Facturé annuellement',
    pricingGetStarted: 'Commencer',
    pricingSubscribe: 'S\'abonner',
    pricingContactUs: 'Nous Contacter',
    pricingCurrentPlan: 'Plan Actuel',
    // Pricing Plans - Specific
    pricingIndividualMonthly: 'Mensuel Individuel',
    pricingIndividualMonthlyDesc: 'Accès complet pour enseignants individuels',
    pricingIndividualYearly: 'Annuel Individuel',
    pricingIndividualYearlyDesc: 'Meilleure valeur pour enseignants individuels',
    pricingSchoolPlanName: 'Plan École',
    pricingSchoolPlanDesc: 'Parfait pour petites et moyennes écoles',
    pricingDistrictPlanName: 'Plan District',
    pricingDistrictPlanDesc: 'Pour grands districts scolaires',
    pricingSave: 'Économisez',
    pricingMinimum: 'Minimum',
    pricingForSchoolPlans: 'pour plans scolaires',
    pricingMinTeachers: 'Minimum 10 enseignants pour Plan École, 100 pour District',
    pricingSignUpFree: 'Inscription Gratuite',
    // Pricing Features
    pricingFeatureLessonPlanner: 'Planificateur de leçons',
    pricingFeatureAttendanceTracker: 'Suivi de présence',
    pricingFeatureGradebook: 'Carnet de notes',
    pricingFeaturePdfExport: 'Export PDF',
    pricingFeatureTemplates: 'Modèles',
    pricingFeatureEmailSupport: 'Support email',
    pricingFeatureEverythingMonthly: 'Tout du Mensuel',
    pricingFeaturePrioritySupport: 'Support prioritaire',
    pricingFeature2MonthsFree: '2 mois gratuits',
    pricingFeatureEarlyAccess: 'Accès anticipé',
    pricingFeatureAllIndividual: 'Toutes fonctions individuelles',
    pricingFeatureAdminDashboard: 'Tableau de bord admin',
    pricingFeatureSchoolBranding: 'Logo de l\'école',
    pricingFeatureBulkImport: 'Import en masse',
    pricingFeatureSchoolReports: 'Rapports scolaires',
    pricingFeatureUserManagement: 'Gestion des utilisateurs',
    pricingFeatureAllSchool: 'Toutes fonctions École',
    pricingFeatureDistrictAnalytics: 'Analytique district',
    pricingFeatureSsoIntegration: 'Intégration SSO',
    pricingFeatureDedicatedSupport: 'Support dédié',
    pricingFeatureCustomTraining: 'Formation personnalisée',
    pricingFeatureApiAccess: 'Accès API',
    
    // Footer & Navigation (Marketing Pages)
    navFeatures: 'Fonctionnalités',
    navPricing: 'Tarifs',
    navTrust: 'Confiance',
    navContact: 'Contact',
    navLogin: 'Connexion',
    navStartFree: 'Commencer Gratuitement',
    footerProduct: 'Produit',
    footerResources: 'Ressources',
    footerTrust: 'Confiance',
    footerLegal: 'Légal',
    footerFeatures: 'Fonctionnalités',
    footerPricing: 'Tarifs',
    footerIntegrations: 'Intégrations',
    footerHelpCenter: 'Centre d\'Aide',
    footerContact: 'Contact',
    footerTrustCenter: 'Centre de Confiance',
    footerPrivacy: 'Confidentialité',
    footerTerms: 'Conditions',
    footerCookies: 'Cookies',
    footerAccessibility: 'Accessibilité',
    footerTagline: 'L\'espace de travail intelligent pour les enseignants.',
    footerSupportNotice: 'Besoin d\'aide? Nos ressources de support sont là pour vous aider à démarrer.',
    footerAllRightsReserved: 'Tous droits réservés.',
    startFreeTrial: 'Commencer l\'Essai Gratuit',
    viewPricing: 'Voir les Tarifs',
    featuresHeroTitle: 'Des outils puissants conçus pour les enseignants',
    featuresHeroSubtitle: 'TeacherHubPro rassemble les outils essentiels dont les éducateurs ont besoin pour planifier, organiser, créer et gérer le travail en classe plus efficacement.',
    featuresCTATitle: 'Découvrez comment TeacherHubPro s\'intègre à votre journée d\'enseignement',
    featuresCTASubtitle: 'Explorez la plateforme et découvrez une meilleure façon de planifier, organiser et enseigner.',
    contactTitle: 'Contactez-nous',
    contactSendMessage: 'Envoyez-nous un message',
    contactName: 'Nom',
    contactEmail: 'Email',
    contactSubject: 'Sujet',
    contactMessage: 'Message',
    contactEmailSupport: 'Support par Email',
    contactHelpCenter: 'Centre d\'Aide',
    landingCTATitle: 'Commencez à simplifier votre flux de travail d\'enseignement dès aujourd\'hui',
    landingCTASubtitle: 'Rejoignez les éducateurs qui utilisent TeacherHubPro pour gagner du temps, rester organisés et travailler plus intelligemment avec l\'IA.',
    
    // Hero Section
    heroTitle: 'L\'Espace de Travail Intelligent pour les Enseignants',
    heroSubtitle: 'Planifiez des leçons, gérez les notes, suivez la présence, créez des supports de cours et gagnez des heures chaque semaine avec une plateforme IA conçue pour les enseignants.',
    seeHowItWorks: 'Voir Comment Ça Marche',
    heroSupportingText: 'Pas de frais IA supplémentaires. Conçu pour les enseignants, tuteurs, éducateurs à domicile et écoles.',
    dashboardLessons: 'Leçons',
    dashboardStudents: 'Élèves',
    dashboardAvgGrade: 'Moyenne',
    hoursSavedPerWeek: 'heures gagnées/semaine',
    
    // Trust Bar
    trustBuiltForEducators: 'Conçu pour les éducateurs modernes',
    trustAIIncluded: 'IA incluse dans chaque plan',
    trustSecureEasy: 'Sécurisé et facile à utiliser',
    trustReduceWorkload: 'Conçu pour réduire la charge administrative',
    
    // Benefits Grid
    benefitSectionTitle: 'Tout ce dont les enseignants ont besoin, en un seul endroit',
    benefitSectionSubtitle: 'Arrêtez de jongler entre documents, tableurs et outils déconnectés. TeacherHubPro rassemble votre flux de travail principal en une plateforme simple.',
    benefitLessonPlanningTitle: 'Planification de Cours',
    benefitLessonPlanningDesc: 'Créez des plans de cours organisés et réutilisables en moins de temps avec des modèles intégrés et le support IA.',
    benefitGradebookTitle: 'Carnet de Notes',
    benefitGradebookDesc: 'Suivez les progrès des élèves avec un carnet de notes clair et simple conçu pour l\'utilisation réelle en classe.',
    benefitAttendanceTitle: 'Suivi de Présence',
    benefitAttendanceDesc: 'Gérez la présence rapidement et gardez les dossiers de classe organisés sans paperasse supplémentaire.',
    benefitAIToolsTitle: 'Outils IA pour l\'Enseignement',
    benefitAIToolsDesc: 'Générez des idées de cours, activités, exercices d\'écriture, supports de classe et plus en quelques secondes.',
    benefitPresentationsTitle: 'Présentations et Ressources',
    benefitPresentationsDesc: 'Créez des supports de cours plus rapidement et gardez tout connecté dans un flux de travail unique.',
    benefitWorkflowTitle: 'Flux de Travail Adapté aux Enseignants',
    benefitWorkflowDesc: 'Conçu pour les éducateurs qui ont besoin d\'outils pratiques, pas de logiciels complexes.',
    
    // Outcomes Section
    outcomeSectionTitle: 'Moins de temps en administration, plus de temps à enseigner',
    outcomePlanFaster: 'Planifiez plus vite',
    outcomePlanFasterDesc: 'Créez des plans de cours, activités de classe et supports sans repartir de zéro à chaque fois.',
    outcomeStayOrganized: 'Restez organisé',
    outcomeStayOrganizedDesc: 'Gardez notes, présence et ressources pédagogiques en un seul endroit plutôt que dispersées dans plusieurs outils.',
    outcomeTeachConfidence: 'Enseignez avec confiance',
    outcomeTeachConfidenceDesc: 'Utilisez l\'IA et des flux de travail optimisés pour réduire le stress et maintenir votre travail de classe en mouvement.',
    
    // Social Proof Section
    socialProofSectionTitle: 'Conçu pour soutenir le vrai travail en classe',
    socialProofEducators: 'Conçu spécifiquement pour les éducateurs',
    socialProofSupport: 'Comprend des ressources de support et une aide guidée',
    socialProofWorkflows: 'Conçu pour simplifier les flux de travail courants des enseignants',
    socialProofSuitable: 'Adapté aux enseignants individuels, éducateurs à domicile et usage scolaire',
    socialProofHelpCenter: 'Ressources de support disponibles • Centre d\'aide inclus',
    
    // Use Cases Section
    useCaseSectionTitle: 'Fait pour différents environnements d\'enseignement',
    useCaseClassroomTitle: 'Enseignants en Classe',
    useCaseClassroomDesc: 'Gérez planification, notation et organisation quotidienne de la classe en un seul endroit.',
    useCaseHomeschoolTitle: 'Éducateurs à Domicile',
    useCaseHomeschoolDesc: 'Créez des flux de travail de cours flexibles et gardez les plans d\'apprentissage organisés.',
    useCaseTutorsTitle: 'Tuteurs et Petits Programmes',
    useCaseTutorsDesc: 'Suivez le travail des élèves, planifiez les sessions et créez des ressources efficacement.',
    useCaseSchoolsTitle: 'Écoles et Équipes',
    useCaseSchoolsDesc: 'Soutenez les éducateurs avec une plateforme qui améliore la cohérence et fait gagner du temps.',
    
    // Feature Detail Pages - Common
    backToFeatures: 'Retour aux Fonctionnalités',
    keyBenefits: 'Avantages Clés',
    howItWorks: 'Comment Ça Marche',
    readyToGetStarted: 'Prêt à commencer?',
    featureCTASubtitle: 'Rejoignez des milliers d\'éducateurs qui économisent déjà du temps et améliorent leur flux de travail avec TeacherHubPro.',
    exploreOtherFeatures: 'Explorer Autres Fonctionnalités',
    learnMore: 'En savoir plus',
    
    // Feature pages - French translations
    featureLessonPlanningTitle: 'Planification de Cours',
    featureLessonPlanningSubtitle: 'Créez des plans de cours organisés et réutilisables en moins de temps avec des modèles intégrés et le support IA.',
    featureLessonPlanningDescription: 'L\'outil de planification de cours de TeacherHubPro vous aide à créer des plans complets et alignés aux standards rapidement.',
    featureLessonPlanningBenefit1: 'Créez des plans jusqu\'à 5x plus vite avec des modèles IA',
    featureLessonPlanningBenefit2: 'Alignez les cours aux standards automatiquement',
    featureLessonPlanningBenefit3: 'Réutilisez et adaptez les plans pour différentes classes',
    featureLessonPlanningBenefit4: 'Partagez les plans avec vos collègues en temps réel',
    featureLessonPlanningHowItWorks1: 'Choisissez un modèle ou commencez de zéro',
    featureLessonPlanningHowItWorks2: 'Utilisez les suggestions IA pour les activités et ressources',
    featureLessonPlanningHowItWorks3: 'Enregistrez et réutilisez vos plans',
    featureGradebookTitle: 'Carnet de Notes',
    featureGradebookSubtitle: 'Suivez les progrès des élèves avec un carnet simple et intuitif.',
    featureGradebookDescription: 'Notre carnet de notes est conçu pour être intuitif et puissant.',
    featureGradebookBenefit1: 'Calculs automatiques avec pondération personnalisable',
    featureGradebookBenefit2: 'Suivi visuel des progrès de chaque élève',
    featureGradebookBenefit3: 'Générez des bulletins instantanément',
    featureGradebookBenefit4: 'Insights IA pour identifier les élèves en difficulté',
    featureGradebookHowItWorks1: 'Configurez vos classes et ajoutez les élèves',
    featureGradebookHowItWorks2: 'Créez des devoirs et entrez les notes',
    featureGradebookHowItWorks3: 'Consultez les analyses et générez des rapports',
    featureAttendanceTitle: 'Suivi de Présence',
    featureAttendanceSubtitle: 'Gérez la présence rapidement sans paperasse supplémentaire.',
    featureAttendanceDescription: 'Dites adieu aux feuilles de présence papier.',
    featureAttendanceBenefit1: 'Prenez la présence en moins de 30 secondes',
    featureAttendanceBenefit2: 'Détection automatique de l\'absentéisme',
    featureAttendanceBenefit3: 'Générez des rapports pour l\'administration',
    featureAttendanceBenefit4: 'Notifications optionnelles aux parents',
    featureAttendanceHowItWorks1: 'Ouvrez votre classe et marquez présent ou absent',
    featureAttendanceHowItWorks2: 'Consultez les tendances de présence',
    featureAttendanceHowItWorks3: 'Exportez ou partagez avec les parents',
    featureAIToolsTitle: 'Outils IA pour l\'Enseignement',
    featureAIToolsSubtitle: 'Générez des idées de cours, activités et matériels en secondes.',
    featureAIToolsDescription: 'Exploitez la puissance de l\'IA pour dynamiser votre enseignement.',
    featureAIToolsBenefit1: 'Générez des idées adaptées à votre matière',
    featureAIToolsBenefit2: 'Créez des matériels différenciés',
    featureAIToolsBenefit3: 'Construisez des évaluations automatiquement',
    featureAIToolsBenefit4: 'Pas de frais IA supplémentaires',
    featureAIToolsHowItWorks1: 'Décrivez ce dont vous avez besoin',
    featureAIToolsHowItWorks2: 'L\'IA génère des options selon votre niveau',
    featureAIToolsHowItWorks3: 'Modifiez et sauvegardez dans votre bibliothèque',
    featurePresentationsTitle: 'Présentations et Ressources',
    featurePresentationsSubtitle: 'Créez des matériels plus rapidement.',
    featurePresentationsDescription: 'Créez des présentations engageantes tout en un seul endroit.',
    featurePresentationsBenefit1: 'Modèles éducatifs pour diverses matières',
    featurePresentationsBenefit2: 'Intégration avec les plans de cours',
    featurePresentationsBenefit3: 'Partage facile avec les élèves',
    featurePresentationsBenefit4: 'Importez vos ressources existantes',
    featurePresentationsHowItWorks1: 'Choisissez un modèle ou créez de zéro',
    featurePresentationsHowItWorks2: 'Ajoutez du contenu et des éléments interactifs',
    featurePresentationsHowItWorks3: 'Liez à votre plan et partagez',
    featureWorkflowTitle: 'Flux de Travail Adapté',
    featureWorkflowSubtitle: 'Conçu pour les éducateurs qui ont besoin d\'outils pratiques.',
    featureWorkflowDescription: 'Nous avons construit TeacherHubPro pour comment les enseignants travaillent vraiment.',
    featureWorkflowBenefit1: 'Interface intuitive conçue par des éducateurs',
    featureWorkflowBenefit2: 'Fonctionne sur bureau et mobile',
    featureWorkflowBenefit3: 'Configuration rapide en quelques minutes',
    featureWorkflowBenefit4: 'S\'intègre avec vos outils existants',
    featureWorkflowHowItWorks1: 'Inscrivez-vous et configurez vos classes',
    featureWorkflowHowItWorks2: 'Accédez à tous les outils depuis un tableau de bord',
    featureWorkflowHowItWorks3: 'Vos données se synchronisent automatiquement',
    featureClassroomTeachersTitle: 'Pour les Enseignants',
    featureClassroomTeachersSubtitle: 'Gérez planification, notes et organisation en un seul endroit.',
    featureClassroomTeachersDescription: 'TeacherHubPro rassemble tous vos outils essentiels.',
    featureClassroomTeachersBenefit1: 'Planifiez pour plusieurs classes',
    featureClassroomTeachersBenefit2: 'Suivez notes et présence',
    featureClassroomTeachersBenefit3: 'Générez des rapports pour les parents',
    featureClassroomTeachersBenefit4: 'Accédez de partout',
    featureClassroomTeachersHowItWorks1: 'Configurez vos classes',
    featureClassroomTeachersHowItWorks2: 'Planifiez et enregistrez quotidiennement',
    featureClassroomTeachersHowItWorks3: 'Communiquez via le portail',
    featureHomeschoolTitle: 'Pour l\'École à la Maison',
    featureHomeschoolSubtitle: 'Créez des parcours d\'apprentissage flexibles.',
    featureHomeschoolDescription: 'TeacherHubPro aide à créer des parcours personnalisés.',
    featureHomeschoolBenefit1: 'Parcours personnalisés pour chaque enfant',
    featureHomeschoolBenefit2: 'Dossiers organisés pour la conformité',
    featureHomeschoolBenefit3: 'Idées générées par IA',
    featureHomeschoolBenefit4: 'Horaires flexibles',
    featureHomeschoolHowItWorks1: 'Créez des profils pour chaque enfant',
    featureHomeschoolHowItWorks2: 'Suivez les progrès sur les plans personnalisés',
    featureHomeschoolHowItWorks3: 'Générez des rapports',
    featureTutorsTitle: 'Pour les Tuteurs',
    featureTutorsSubtitle: 'Suivez le travail des élèves efficacement.',
    featureTutorsDescription: 'TeacherHubPro vous aide à rester organisé.',
    featureTutorsBenefit1: 'Suivez plusieurs élèves individuellement',
    featureTutorsBenefit2: 'Planifiez et gardez des notes',
    featureTutorsBenefit3: 'Rapports de progrès pour les parents',
    featureTutorsBenefit4: 'Matériels personnalisés',
    featureTutorsHowItWorks1: 'Ajoutez vos élèves',
    featureTutorsHowItWorks2: 'Planifiez et suivez',
    featureTutorsHowItWorks3: 'Partagez via le portail',
    featureSchoolsTitle: 'Pour les Écoles',
    featureSchoolsSubtitle: 'Soutenez les éducateurs avec une plateforme cohérente.',
    featureSchoolsDescription: 'TeacherHubPro est conçu pour évoluer avec votre école.',
    featureSchoolsBenefit1: 'Outils cohérents pour tous les enseignants',
    featureSchoolsBenefit2: 'Bibliothèque de ressources partagée',
    featureSchoolsBenefit3: 'Tableau de bord administrateur',
    featureSchoolsBenefit4: 'Tarifs de volume disponibles',
    featureSchoolsHowItWorks1: 'Contactez-nous pour les tarifs',
    featureSchoolsHowItWorks2: 'Intégrez les enseignants',
    featureSchoolsHowItWorks3: 'Surveillez et supportez'
  },
  pt: {
    absent: "Ausente",
    activities: "Atividades",
    art: "Artes",
    assignments: "Tarefas",
    attendanceReport: "Relatório de frequência",
    book: "Livro",
    brainstorming: "Chuva de ideias",
    buildingBackground: "Contextualização",
    category: "Categoria",
    comprehensionQuestions: "Perguntas de compreensão",
    createAssignment: "Criar tarefa",
    createPlan: "Criar plano",
    dayTheme: "Tema do dia",
    description: "Descrição",
    dictionary: "Dicionário",
    dueDate: "Data de entrega",
    duplicateLastWeek: "Duplicar semana passada",
    excused: "Justificado",
    exercisePractice: "Prática de exercícios",
    expectations: "Expectativas",
    exportPdf: "Exportar PDF",
    firstWeek: "Primeira semana",
    foundationalSkills: "Habilidades fundamentais",
    gradeEntry: "Lançamento de notas",
    guidedReading: "Leitura guiada e coral",
    handouts: "Apostilas",
    health: "Saúde",
    homework: "Lição de casa",
    language: "Idioma",
    late: "Atrasado",
    level1Memory: "Nível 1: Memória",
    level2Processing: "Nível 2: Processamento",
    level3Strategic: "Nível 3: Pensamento estratégico",
    level4Extended: "Nível 4: Pensamento estendido",
    listeningAndSpeaking: "Escuta/Fala",
    markAllPresent: "Marcar todos presentes",
    materials: "Materiais",
    mathematics: "Matemática",
    missing: "Faltando",
    notebook: "Caderno",
    objectiveOfWeek: "Objetivo da semana",
    oralQuestions: "Perguntas orais",
    other: "Outro",
    physicalEducation: "Educação Física",
    points: "Pontos",
    present: "Presente",
    pricingActiveSubscription: "Assinatura ativa",
    pricingAdminAccount: "Conta de administrador - Acesso total",
    pricingContactUs: "Fale conosco",
    pricingCurrentPlan: "Plano atual",
    pricingDaysRemaining: "dias restantes",
    pricingDistrictDesc: "Para distritos escolares com administração avançada",
    pricingDistrictPlan: "Distrito",
    pricingFeatureAIAssistant: "Assistente de IA (Claude)",
    pricingFeatureAdvancedAnalytics: "Análises avançadas",
    pricingFeatureAttendance: "Controle de frequência",
    pricingFeatureBasicLessonPlanning: "Planejamento de aulas básico",
    pricingFeatureCustomTemplates: "Modelos personalizados",
    pricingFeatureEverythingPro: "Tudo do Pro",
    pricingFeatureEverythingSchool: "Tudo do School",
    pricingFeatureEverythingStarter: "Tudo do Starter",
    pricingFeatureMultiSchool: "Gestão de várias escolas",
    pricingFeatureSchoolDashboard: "Painel da escola",
    pricingFeatureSharedResources: "Recursos compartilhados",
    pricingFeatureTeacherManagement: "Gestão de professores",
    pricingFeatureUnlimitedClasses: "Turmas ilimitadas",
    pricingFeatureUnlimitedStudents: "Alunos ilimitados",
    pricingForSchoolPlans: "para planos escolares",
    pricingFreeTrial: "Teste grátis",
    pricingGetStarted: "Começar",
    pricingNumberOfTeachers: "Número de professores",
    pricingProDesc: "Recursos avançados para usuários avançados",
    pricingProPlan: "Pro",
    pricingSchoolDesc: "Para escolas inteiras com recursos de equipe",
    pricingSchoolDistrict: "Para escolas e distritos",
    pricingSchoolPlan: "Escola",
    pricingStarterDesc: "Perfeito para professores individuais",
    pricingStarterPlan: "Starter",
    pricingSubscribe: "Assinar",
    pricingTeachers: "professores",
    pricingTrialExpired: "Seu teste grátis expirou. Selecione um plano para continuar usando o TeacherHubPro.",
    project: "Projeto",
    quiz: "Quiz",
    readPages: "Páginas de leitura",
    reading: "Leitura",
    religion: "Religião",
    saveAsTemplate: "Salvar como modelo",
    science: "Ciências",
    secondWeek: "Segunda semana",
    skillsOfWeek: "Habilidades da semana",
    socialStudies: "Estudos Sociais",
    spanish: "Espanhol",
    standards: "Padrões",
    story: "História/Título",
    submitAttendance: "Enviar frequência",
    takeAttendance: "Fazer chamada",
    tardy: "Atrasado",
    teachersGuide: "Guia do professor",
    test: "Teste",
    testQuiz: "Teste/Quiz",
    title: "Título",
    unit: "Unidade",
    vocabularyDevelopment: "Desenvolvimento de vocabulário",
    weekOf: "Semana de",
    weeklyPlan: "Plano semanal",
    writing: "Escrita",

    // Navigation
    dashboard: 'Painel',
    planner: 'Planejador',
    attendance: 'Presença',
    gradebook: 'Boletim',
    classes: 'Turmas',
    settings: 'Configurações',
    logout: 'Sair',
    
    // Auth
    login: 'Entrar',
    register: 'Cadastrar',
    email: 'Email',
    password: 'Senha',
    name: 'Nome',
    signInWithGoogle: 'Entrar com Google',
    orContinueWith: 'ou continuar com',
    dontHaveAccount: 'Não tem conta?',
    alreadyHaveAccount: 'Já tem conta?',
    
    // Dashboard
    welcome: 'Bem-vindo',
    todayOverview: 'Resumo de Hoje',
    quickActions: 'Ações Rápidas',
    attendancePending: 'Presença Pendente',
    upcomingAssignments: 'Próximas Tarefas',
    recentPlans: 'Planos Recentes',
    totalClasses: 'Total de Turmas',
    totalStudents: 'Total de Alunos',
    totalPlans: 'Total de Planos',
    
    // Classes
    addClass: 'Adicionar Turma',
    className: 'Nome da Turma',
    grade: 'Série',
    section: 'Seção',
    subject: 'Matéria',
    yearTerm: 'Ano/Período',
    students: 'Alunos',
    addStudent: 'Adicionar Aluno',
    firstName: 'Nome',
    lastName: 'Sobrenome',
    studentNumber: 'Número do Aluno',
    parentEmail: 'Email do Responsável',
    notes: 'Notas',
    accommodations: 'Adaptações',
    
    // Common
    save: 'Salvar',
    cancel: 'Cancelar',
    delete: 'Excluir',
    edit: 'Editar',
    view: 'Ver',
    search: 'Buscar',
    filter: 'Filtrar',
    loading: 'Carregando...',
    noData: 'Nenhum dado disponível',
    confirmDelete: 'Tem certeza que deseja excluir?',
    success: 'Sucesso',
    error: 'Erro',
    from: 'De',
    to: 'Até',
    date: 'Data',
    teacher: 'Professor',
    selectLanguage: 'Selecionar Idioma',
    
    // Days
    monday: 'Segunda',
    tuesday: 'Terça',
    wednesday: 'Quarta',
    thursday: 'Quinta',
    friday: 'Sexta',
    
    // Games
    educationalGames: 'Jogos Educativos',
    myGames: 'Meus Jogos',
    createGame: 'Criar Jogo',
    play: 'Jogar',
    copyLink: 'Copiar Link',
    shareToClassroom: 'Compartilhar no Classroom',
    deleteGame: 'Excluir Jogo',
    questions: 'Perguntas',
    gameType: 'Tipo de Jogo',
    gradeLevel: 'Nível Escolar',
    
    // Landing Page
    landingHeroTitle: 'Sua sala de aula completa em uma plataforma',
    landingHeroSubtitle: 'Planejamento, presença, notas e assistente IA. Tudo o que um professor precisa.',
    landingBuiltForTeachers: 'Feito para professores',
    landingStartFree: 'Começar grátis',
    landingViewPricing: 'Ver preços',
    landingUsedBySchools: 'Usado por escolas em todo o mundo',
    landingEverythingYouNeed: 'Tudo o que você precisa',
    landingEverythingDesc: 'Uma plataforma completa projetada especialmente para professores.',
    landingFeatures: 'Recursos',
    landingAiAssistant: 'Assistente IA',
    landingPoweredBy: 'Desenvolvido pela Anthropic',
    landingActiveTeachers: 'professores ativos',
    landingHomeschool: 'Ensino em Casa',
    landingIntegrations: 'Integrações',
    landingCustomization: 'Personalização',
    landingPricing: 'Preços',
    landingGetStarted: 'Começar - Entrar',
    landingStart: 'Começar',
    landingAiLessonPlan: 'Plano IA',
    landingPresentations: 'Apresentações',
    landingGrades: 'Notas',
    landingPlanFaster: 'Planeje mais rápido',
    landingPlanFasterDesc: 'Crie planos de aula semanais em segundos com nosso formato intuitivo.',
    landingWeek1: 'Semana 1',
    landingYourBrand: 'Sua marca, seu estilo',
    landingYourBrandDesc: 'Personalize com o logo e as cores da sua escola.',
    landingYourSchool: 'Sua Escola',
    landingCustomBranding: 'Marca personalizada',
    landingExportPro: 'Exporte profissionalmente',
    landingExportProDesc: 'Gere PDFs prontos para imprimir: planos, boletins, pacotes.',
    landingDownloadPdf: 'Baixar PDF',
    landingAiAssistantTitle: 'Assistente IA',
    landingAiAssistantDesc: 'Gere planos de aula, atividades e perguntas com IA.',
    landingLessonPlans: 'Planos de aula',
    landingQuizzesTests: 'Quizzes e provas',
    landingDigitalClassroom: 'Sua sala de aula digital',
    landingTryFree: 'Teste grátis por 30 dias',
    landingNoCard: 'Não requer cartão de crédito',
    landingFullName: 'Nome Completo',
    landingCreateAccount: 'Criar Conta Grátis',
    landingOrLoginWith: 'Ou entre com',
    landingHaveAccount: 'Tem uma conta?',
    landingLoginHere: 'Entre aqui',
    landingNeedAccount: 'Precisa de conta?',
    landingRegisterHere: 'Cadastre-se aqui',
    // AI Section
    landingAiTeachingAssistant: 'Assistente IA para Ensino',
    landingYouAsked: 'Você perguntou:',
    landingAiResponse: 'Resposta IA:',
    landingAiQuestion: 'Como posso ensinar frações equivalentes para o 4º ano?',
    landingAiAnswer: 'Aqui está um plano de aula baseado nos padrões com atividades práticas...',
    landingQuizzes: 'Quizzes',
    landingWorksheets: 'Folhas de Exercícios',
    landingIdeas: 'Ideias',
    landingNewAi: 'Novo: Inteligência Artificial',
    landingAiAssistantToTeach: 'Seu assistente IA para ensinar melhor',
    landingAiAssistantDesc2: 'Gere planos de aula, quizzes, resumos e atividades baseadas nos padrões, instantaneamente.',
    landingAlignedStandards: 'Alinhado com os padrões curriculares',
    landingGenerateSeconds: 'Gere conteúdo em segundos',
    landingBilingual: 'Multilíngue',
    landingPoweredBy: 'Desenvolvido com Claude Sonnet 4.5',
    landingTryItFree: 'Testar grátis',
    landingIncludedSubscription: 'Incluído na assinatura',
    // Integrations Section
    landingConnectTools: 'Conecte suas ferramentas favoritas',
    landingConnectToolsDesc: 'Integre as plataformas que você já usa.',
    landingCalendarSync: 'Sincronização de Calendário',
    landingCalendarSyncDesc: 'Exporte seus planos para Google Calendar, Outlook ou Apple Calendar.',
    landingGoogleClassroom: 'Google Classroom',
    landingGoogleClassroomDesc: 'Compartilhe atividades e jogos diretamente com seus alunos.',
    landingPdfExport: 'Exportar PDF',
    landingPdfExportDesc: 'Gere PDFs profissionais para planos, boletins e mais.',
    landingEmailNotifications: 'Notificações por Email',
    landingEmailNotificationsDesc: 'Envie relatórios automáticos para pais e administradores.',
    landingViewIntegrations: 'Ver todas as integrações',
    // Customization Section
    landingYourBrandYourWay: 'Sua marca, do seu jeito',
    landingCustomizeDesc: 'Personalize tudo com as cores, logo e preferências da sua escola.',
    landingSchoolLogo: 'Logo da Escola',
    landingSchoolLogoDesc: 'Adicione seu logo a todos os documentos e relatórios.',
    landingColorThemes: 'Temas de Cores',
    landingColorThemesDesc: 'Escolha cores que combinem com a identidade da sua escola.',
    landingCustomTemplates: 'Modelos Personalizados',
    landingCustomTemplatesDesc: 'Crie modelos reutilizáveis para seus planos.',
    landingMultiLanguage: 'Multi-Idioma',
    landingMultiLanguageDesc: 'Trabalhe em vários idiomas.',
    // Homeschool Section
    landingHomeschoolTitle: 'Ensino em Casa',
    landingHomeschoolSubtitle: 'Seu companheiro completo para homeschool',
    landingHomeschoolDesc: 'Tudo o que você precisa para uma experiência de ensino em casa bem-sucedida.',
    landingAdaptiveLearning: 'Aprendizado Adaptativo',
    landingAdaptiveLearningDesc: 'Caminhos de aprendizado que se adaptam ao ritmo de cada criança.',
    landingFullVisibility: 'Visibilidade Total',
    landingFullVisibilityDesc: 'Acompanhe o progresso em tempo real com relatórios detalhados.',
    landingFlexibleCurriculum: 'Currículo Flexível',
    landingFlexibleCurriculumDesc: 'Personalize o currículo de acordo com as necessidades da sua família.',
    landing100Customizable: '100% Personalizável',
    landingDownloadLearn: 'Baixar e Aprender',
    landingDownloadLearnDesc: 'Exporte materiais para aprendizado offline.',
    landingPdfExportFeature: 'Exportar PDF',
    landingTextToSpeech: 'Texto para Fala',
    landingTextToSpeechDesc: 'Suporte de áudio para todo o conteúdo de leitura.',
    landingAudioLearning: 'Aprendizado por Áudio',
    landingSafeContent: 'Conteúdo Seguro',
    landingSafeContentDesc: 'Materiais apropriados para a idade e revisados.',
    landingParentalControl: 'Controle Parental',
    landingSubjectModules: 'Módulos por Matéria',
    landingMathematics: 'Matemática',
    landingLanguageArts: 'Português',
    landingScience: 'Ciências',
    landingAstronomy: 'Astronomia',
    landingGeography: 'Geografia',
    landingHistory: 'História',
    landingArt: 'Arte',
    landingPhysicalEd: 'Educação Física',
    landingStartHomeschooling: 'Começar Ensino em Casa',
    // CTA Section
    landing7DaysFree: '7 dias grátis',
    landingGetStartedToday: 'Comece hoje',
    landingStartYourJourney: 'Comece sua jornada de sala de aula digital. Sem cartão de crédito.',
    landingNoCreditCard: 'Sem cartão de crédito necessário',
    landingSetup2Min: 'Configuração em 2 minutos',
    landingCancelAnytime: 'Cancele a qualquer momento',
    landingSupportBilingual: 'Suporte multilíngue',
    landingActiveTeachers: 'professores ativos',
    landingSignIn: 'Entrar',
    landingCreateAccountTab: 'Criar conta',
    landingContinueGoogle: 'Continuar com Google',
    landingOrWithEmail: 'ou com email',
    landingEmail: 'Email',
    landingPassword: 'Senha',
    landingFullNameLabel: 'Nome completo',
    landingCreateFreeAccount: 'Criar conta grátis',
    // Footer
    landingContact: 'Contato',
    landingPrivacyPolicy: 'Política de Privacidade',
    landingHelp: 'Ajuda',
    landingTermsOfUse: 'Termos de Uso',
    landingCookiesPolicy: 'Política de Cookies',
    landingAccessibility: 'Acessibilidade',
    landingAllRightsReserved: 'Todos os direitos reservados.',
    
    // AI Features Section
    landingAdvancedAiFeatures: 'Recursos de IA Avançados',
    landingIntelligentPlanning: 'Planejamento inteligente com IA',
    landingAiPlanningDesc: 'Desde gerar um plano completo até sugestões diárias, a IA ajuda você em cada passo.',
    landingGenerateFullWeek: 'Gerar Semana Completa',
    landingGenerateFullWeekDesc: 'Com um clique, gere atividades para os 5 dias com progressão pedagógica: introdução → prática → domínio → avaliação.',
    landingDays: 'dias',
    landingProgression: 'Progressão',
    landingDailySuggestions: 'Sugestões Diárias',
    landingDailySuggestionsDesc: 'Cada dia mostra sua fase pedagógica (Introdução, Prática, Domínio). Obtenha sugestões específicas para cada etapa.',
    landingMonFri: 'Seg → Sex',
    landingContextual: 'Contextual',
    landingReusableTemplates: 'Modelos Reutilizáveis',
    landingReusableTemplatesDesc: 'Salve seus melhores planos como modelos. Clone e personalize para novos temas com um clique.',
    landingSave: 'Salvar',
    landingClone: 'Clonar',
    landing5StarterTemplates: '5 Modelos Iniciais',
    landing5StarterTemplatesDesc: 'Comece imediatamente com modelos profissionais: Frações, Compreensão de Leitura, Método Científico, Escrita e Multiplicação.',
    landingMath: 'Matemática',
    landingELA: 'Português',
    landingAiCustomize: 'Personalizar com IA',
    landingAiCustomizeDesc: 'Pegue qualquer modelo e adapte para um novo tema. A IA mantém a estrutura bem-sucedida enquanto muda o conteúdo.',
    landingNewTopic: 'Novo tema',
    landingSameStructure: 'Mesma estrutura',
    landingTemplateOfWeek: 'Modelo da Semana',
    landingTemplateOfWeekDesc: 'Cada semana destacamos um modelo diferente no seu painel com dicas de personalização específicas.',
    landingWeeklyRotation: 'Rotação semanal',
    landingTips: 'Dicas',
    landingTryAiFree7Days: 'Experimente a IA grátis por 7 dias',
    
    // AI Presentations Section
    landingNewAiPresentations: 'NOVO! Apresentações com IA',
    landingCreatePresentations: 'Crie apresentações que cativam',
    landingPresentationsDesc: 'Gere apresentações educacionais completas com IA. Escolha temas visuais, adicione imagens, aplique transições e salve para depois.',
    landingAiGenerate: 'Gerar com IA',
    landingAiGenerateDesc: '6+ slides em segundos',
    landing8VisualThemes: '8 Temas Visuais',
    landing8VisualThemesDesc: 'Oceano, Galáxia, Lousa...',
    landingSaveAndLoad: 'Salvar e Carregar',
    landingSaveAndLoadDesc: 'Biblioteca pessoal',
    landingDownloadHtml: 'Baixar HTML',
    landingDownloadHtmlDesc: 'Usar offline',
    landingCreateFirstPresentation: 'Criar minha primeira apresentação',
    landingTheSolarSystem: 'O Sistema Solar',
    landingScienceGrade: 'Ciências - 4º Ano',
    landingTransitionsEnabled: '✨ Transições ativadas',
    
    // Customization Section
    landingFullCustomization: 'Personalização completa',
    landingSchoolBrandingTitle: 'A identidade da sua escola em cada documento',
    landingSchoolBrandingDesc: 'Do seu logo às suas cores, cada PDF e relatório reflete a marca da sua instituição.',
    landingSchoolLogoAll: 'O logo da sua escola em todos os documentos',
    landingCustomColorsMatch: 'Cores personalizadas que combinam com sua marca',
    landingPdfFormats: 'Os formatos PDF exatos da sua escola',
    landingCentralizedAdmin: 'Painel administrativo centralizado',
    landingTeachersPlanning: 'Planejamento do Professor',
    landingYourLogoHere: 'Seu logo aqui',
    landingYourColors: 'Suas cores',
    
    // Integrations Section
    landingConnectFavorite: 'Conecte-se às suas plataformas favoritas',
    landingConnectFavoriteDesc: 'Sincronize com as ferramentas que você já usa para um fluxo de trabalho perfeito.',
    landingAvailable: 'Disponível',
    landingMostPopular: 'Mais popular',
    landingImportClasses: 'Importar Turmas',
    landingImportClassesDesc: 'Traga suas turmas e listas de alunos automaticamente',
    landingSyncGrades: 'Sincronizar Notas',
    landingSyncGradesDesc: 'Envie notas para o Google Classroom com um clique',
    landingSecurePrivate: 'Seguro e Privado',
    landingSecurePrivateDesc: 'Autenticação OAuth segura, seus dados protegidos',
    landingSetupMinutes: 'Configuração em minutos',
    landingNoExtraCost: 'Sem custo adicional',
    landingSupportIncluded: 'Suporte incluído',
    landingComingSoon: 'Em Breve',
    landingNeedIntegration: 'Precisa de outra integração?',
    landingNeedIntegrationDesc: 'Estamos constantemente adicionando novas integrações. Conte-nos quais plataformas você usa e as priorizaremos.',
    landingRequestIntegration: 'Solicitar Integração',
    
    // Homeschool Section
    landingPerfectHomeschool: 'Perfeito para Educadores em Casa',
    landingHomeschoolCompleteDesc: 'Tudo o que você precisa para criar uma experiência de aprendizado personalizada, desde planejamento até acompanhamento de progresso.',
    landingAiPowered: 'Impulsionado por IA',
    landingParentDashboard: 'Painel dos Pais',
    landingParentDashboardDesc: 'Visão completa do progresso, notas e trabalhos pendentes de cada criança em um painel unificado.',
    landingFlexibleCurriculumDesc2: 'Personalize matérias, ritmo e métodos de ensino para combinar com o estilo de aprendizado único de cada criança.',
    landing100Customizable: '100% Personalizável',
    landingDownloadAndLearn: 'Baixar e Aprender',
    landingDownloadAndLearnDesc: 'Exporte lições e materiais como PDFs. Continue aprendendo mesmo offline ou em movimento.',
    landingAudioLearning: 'Aprendizado por Áudio',
    landingTextToSpeechDesc2: 'Texto para fala integrado para todo o conteúdo. Perfeito para aprendizes auditivos ou assistência de leitura.',
    landingSafeContentDesc2: 'Todo o conteúdo é apropriado para a idade e verificado educacionalmente. Controle parental total sobre o que as crianças podem acessar.',
    
    // New Landing Page Copy (Portuguese)
    landingSave5Hours: 'Economize 5+ Horas Toda Semana',
    landingSave5HoursTitle: 'Economize 5+ Horas Toda Semana em Planejamento, Notas e Administração',
    landingSave5HoursSubtitle: 'TeacherHubPro ajuda educadores ocupados a criar planos de aula, gerenciar notas, registrar presença e usar IA para simplificar o trabalho diário — tudo em um só lugar.',
    landingBuiltForBusyTeachers: 'Feito para professores ocupados',
    landingStartFreeNoCreditCard: 'Começar Grátis — Sem Cartão de Crédito',
    landingSeeHowItWorks: 'Veja Como Funciona',
    landingTeachingIsMore: 'Ensinar é Mais do que Ensinar',
    landingTeachingIsMoreDesc: 'Entre planejamento, notas, presença, relatórios e comunicação — a maioria dos professores passa horas toda semana em tarefas que roubam tempo dos alunos e da vida pessoal.',
    landingYouBecameTeacher: 'Você se tornou professor para educar.',
    landingNotToDrown: 'Não para se afogar em papelada.',
    landingEverythingSimple: 'Tudo que Você Precisa. Uma Plataforma Simples.',
    landingLessBusywork: 'Menos trabalho burocrático. Mais ensino.',
    landingGenerateLessons: 'Gere planos de aula estruturados em minutos',
    landingTrackGrades: 'Acompanhe notas com um boletim digital fácil de usar',
    landingRecordAttendance: 'Registre presença rapidamente',
    landingOrganizeData: 'Organize dados da turma em um painel',
    landingUseAiTools: 'Use ferramentas de IA para acelerar a preparação',
    landingWhatTeachersExperience: 'O que os Professores Experimentam',
    landingFasterPlanning: 'Planejamento semanal mais rápido',
    landingLessTimeGrading: 'Menos tempo dando notas',
    landingBetterOrganization: 'Melhor organização',
    landingMoreConfidence: 'Mais confiança em sala de aula',
    landingMorePersonalTime: 'Mais tempo pessoal depois da escola',
    landing3SimpleSteps: 'Comece em 3 Passos Simples',
    landingValueInMinutes: 'Você verá valor em minutos — não horas.',
    landingCreateClasses: 'Crie Suas Turmas',
    landingCreateClassesDesc: 'Configure suas turmas e adicione alunos em segundos',
    landingGenerateWithAi: 'Gere Aulas com IA',
    landingGenerateWithAiDesc: 'Crie planos de aula completos com assistência de IA',
    landingManageGradesAttendance: 'Gerencie Notas e Presença Facilmente',
    landingManageGradesAttendanceDesc: 'Acompanhe tudo em um painel intuitivo',
    landingBuiltForEducators: 'Feito Especialmente para Educadores',
    landingElementaryTeachers: 'Professores do Fundamental',
    landingMiddleSchoolEducators: 'Educadores do Ensino Médio',
    landingHighSchoolTeachers: 'Professores do Ensino Superior',
    landingHomeschoolEducators: 'Educadores em Casa',
    landingInstructionalCoaches: 'Coordenadores Pedagógicos',
    landingJoinThousands: 'Junte-se a Milhares de Professores',
    landingJoinThousandsDesc: 'Educadores em todo o país já estão usando TeacherHubPro para otimizar seu fluxo de trabalho, passar mais tempo com alunos e recuperar suas noites.',
    landingReadyToSimplify: 'Pronto para Simplificar Sua Vida de Professor?',
    landingStartFreeToday: 'Começar Grátis Hoje',
    landingExperienceTimeSaved: 'Comece sua conta grátis hoje e veja quanto tempo você pode economizar esta semana.',
    landingNoCreditCardRequired: 'Sem cartão de crédito',
    landingSetupIn2Minutes: 'Configuração em menos de 2 minutos',
    landingCancelAnytime: 'Cancele quando quiser',
    landingFullAccess: 'Acesso completo a todos os recursos',
    landingTeachersUsing: 'professores já usando TeacherHubPro',
    landingSimplifyingTeaching: 'Simplificando o ensino, uma sala de aula por vez.',
    landingLogIn: 'Entrar',
    landingStartFreeCta: 'Começar Grátis',
    landingBackToHome: 'Voltar ao Início',
    landingWelcomeBack: 'Bem-Vindo de Volta',
    landingSignInAccess: 'Entre para acessar seu painel',
    landingStartFreeTrial: 'Comece Seu Teste Grátis',
    landingJoinThousandsTeachers: 'Junte-se a milhares de professores economizando tempo toda semana',
    landingCreateFreeAccount: 'Criar Conta Grátis',
    landingSignInTab: 'Entrar',
    landingContinueWithGoogle: 'Continuar com Google',
    landingOrWithEmail: 'ou com email',
    landingFullName: 'Nome Completo',
    landingYourName: 'Seu nome',
    landingYourEmail: 'voce@escola.edu',
    landingCreatePassword: 'Crie uma senha',
    landingCreatingAccount: 'Criando conta...',
    landingSigningIn: 'Entrando...',
    
    // Feature Sections (Portuguese)
    landingEngageStudents: 'Engaje Alunos Como Nunca Antes',
    landingEngageStudentsDesc: 'Transforme sua sala de aula com jogos interativos, aprendizado adaptativo e quizzes ao vivo que mantêm os alunos motivados e aprendendo.',
    landingPlayToLearnTitle: 'Aprender Brincando',
    landingPlayToLearnSubtitle: 'Jogos ao Vivo Estilo Kahoot',
    landingPlayToLearnDesc: 'Organize jogos de quiz ao vivo ou atribua atividades no ritmo deles. Os alunos entram com um PIN e competem em tempo real com feedback instantâneo.',
    landingPlayToLearnFeature1: 'Quizzes multiplayer ao vivo',
    landingPlayToLearnFeature2: 'Tarefas no próprio ritmo',
    landingPlayToLearnFeature3: 'Rankings em tempo real',
    landingPlayToLearnFeature4: '9+ modos de jogo',
    landingEducationalGamesTitle: 'Jogos Educativos',
    landingEducationalGamesSubtitle: 'Atividades de Aprendizado Divertidas',
    landingEducationalGamesDesc: 'Crie jogos personalizados para qualquer matéria. Compartilhe com alunos via link ou Google Classroom. Acompanhe engajamento e progresso.',
    landingEducationalGamesFeature1: 'Modelos de jogos personalizáveis',
    landingEducationalGamesFeature2: 'Compartilhe por link ou código QR',
    landingEducationalGamesFeature3: 'Acompanhamento do progresso dos alunos',
    landingEducationalGamesFeature4: 'Funciona em qualquer dispositivo',
    landingAdaptiveLearningTitle: 'Aprendizado Adaptativo',
    landingAdaptiveLearningSubtitle: 'Caminhos de Aprendizado Personalizados',
    landingAdaptiveLearningDesc: 'Caminhos de aprendizado alimentados por IA que se adaptam ao ritmo e nível de cada aluno. Identifique lacunas e forneça prática direcionada.',
    landingAdaptiveLearningFeature1: 'Recomendações alimentadas por IA',
    landingAdaptiveLearningFeature2: 'Prática personalizada',
    landingAdaptiveLearningFeature3: 'Análise de progresso',
    landingAdaptiveLearningFeature4: 'Ajuste automático de dificuldade',
    landingExploreFeature: 'Explorar',
    landingProductLabel: 'Produto',
    landingCompanyLabel: 'Empresa',
    landingLegalLabel: 'Legal',
    landingHelpCenter: 'Central de Ajuda',
    landingPrivacyPolicyLink: 'Política de Privacidade',
    landingTermsOfServiceLink: 'Termos de Uso',
    
    // Platform Integrations (Portuguese)
    landingPlatformIntegrations: 'Integrações de Plataforma',
    landingPlatformIntegrationsDesc: 'Conecte-se com as ferramentas que você já usa',
    landingAiAssistantTitle: 'Assistente IA',
    landingStudentProgressTitle: 'Progresso dos Alunos',
    landingPresentationsTitle: 'Apresentações',
    
    // Google Classroom Section (Portuguese)
    landingConnectPlatforms: 'Conecte Suas Plataformas Favoritas',
    landingConnectPlatformsDesc: 'Integre facilmente com as ferramentas que sua escola já usa',
    landingIntegrated: 'INTEGRADO',
    landingImportClasses: 'Importe turmas e listas automaticamente',
    landingSyncGrades: 'Sincronize notas com Google Classroom',
    landingSecurePrivate: 'Seguro e privado - protegido por OAuth 2.0',
    landingConnectGoogleClassroom: 'Conectar Google Classroom',
    landingComingSoon: 'Em Breve',
    landingComingSoonBadge: 'Em Breve',
    landingMoreIntegrations: 'Mais integrações estão a caminho. Diga-nos o que você precisa!',
    landingTrustedBySchools: 'Confiado por escolas que usam Google Classroom em todo o mundo',
    
    // Homeschool Section (Portuguese)
    landingForHomeschool: 'PARA EDUCAÇÃO EM CASA',
    landingHomeschoolFriendly: 'Ideal para Homeschool',
    landingLearnMoreHomeschool: 'Saiba Mais Sobre Recursos de Homeschool',
    landingHomeschoolAdaptiveDesc: 'Caminhos alimentados por IA que se adaptam ao ritmo único do seu filho',
    landingFlexibleCurriculum: 'Currículo Flexível',
    landingFlexibleCurriculumDesc: 'Personalize matérias e ritmo de acordo com estilos de aprendizagem',
    landingTextToSpeechDesc: 'Texto para fala integrado para aprendizes auditivos',
    landingSafeContent: 'Conteúdo Seguro',
    landingSafeContentDesc: 'Materiais apropriados para a idade e verificados educacionalmente',
    // Pricing Page (Portuguese)
    pricingTitle: 'Escolha Seu Plano',
    pricingSubtitle: 'Comece com 7 dias grátis. Cancele a qualquer momento.',
    pricingBack: 'Voltar',
    pricingYourDigitalClassroom: 'Sua sala de aula digital',
    pricingAllRightsReserved: 'Todos os direitos reservados.',
    pricingIndividualMonthly: 'Mensal Individual',
    pricingIndividualMonthlyDesc: 'Acesso completo para professores individuais',
    pricingIndividualYearly: 'Anual Individual',
    pricingIndividualYearlyDesc: 'Melhor valor para professores individuais',
    pricingSchoolPlanName: 'Plano Escolar',
    pricingSchoolPlanDesc: 'Perfeito para escolas pequenas e médias',
    pricingDistrictPlanName: 'Plano Distrito',
    pricingDistrictPlanDesc: 'Para grandes distritos escolares',
    pricingSave: 'Economize',
    pricingMostPopular: 'MAIS POPULAR',
    pricingMonth: 'mês',
    pricingYear: 'ano',
    pricingPerTeacher: 'por professor',
    pricingBilledAnnually: 'Cobrado anualmente',
    pricingMinimum: 'Mínimo',
    pricingMinTeachers: 'Mínimo 10 professores para Plano Escolar, 100 para Distrito',
    pricingSignUpFree: 'Cadastre-se Grátis',
    pricingFeatureLessonPlanner: 'Planejador de aulas',
    pricingFeatureAttendanceTracker: 'Rastreamento de presença',
    pricingFeatureGradebook: 'Boletim',
    pricingFeaturePdfExport: 'Exportar PDF',
    pricingFeatureTemplates: 'Modelos',
    pricingFeatureEmailSupport: 'Suporte por email',
    pricingFeatureEverythingMonthly: 'Tudo do Mensal',
    pricingFeaturePrioritySupport: 'Suporte prioritário',
    pricingFeature2MonthsFree: '2 meses grátis',
    pricingFeatureEarlyAccess: 'Acesso antecipado',
    pricingFeatureAllIndividual: 'Todas funções individuais',
    pricingFeatureAdminDashboard: 'Painel de admin',
    pricingFeatureSchoolBranding: 'Logo da escola',
    pricingFeatureBulkImport: 'Importação em massa',
    pricingFeatureSchoolReports: 'Relatórios escolares',
    pricingFeatureUserManagement: 'Gestão de usuários',
    pricingFeatureAllSchool: 'Todas funções Escolar',
    pricingFeatureDistrictAnalytics: 'Analytics de distrito',
    pricingFeatureSsoIntegration: 'Integração SSO',
    pricingFeatureDedicatedSupport: 'Suporte dedicado',
    pricingFeatureCustomTraining: 'Treinamento personalizado',
    pricingFeatureApiAccess: 'Acesso API',
    
    // Footer & Navigation (Marketing Pages)
    navFeatures: 'Recursos',
    navPricing: 'Preços',
    navTrust: 'Confiança',
    navContact: 'Contato',
    navLogin: 'Entrar',
    navStartFree: 'Começar Grátis',
    footerProduct: 'Produto',
    footerResources: 'Recursos',
    footerTrust: 'Confiança',
    footerLegal: 'Legal',
    footerFeatures: 'Recursos',
    footerPricing: 'Preços',
    footerIntegrations: 'Integrações',
    footerHelpCenter: 'Central de Ajuda',
    footerContact: 'Contato',
    footerTrustCenter: 'Central de Confiança',
    footerPrivacy: 'Privacidade',
    footerTerms: 'Termos',
    footerCookies: 'Cookies',
    footerAccessibility: 'Acessibilidade',
    footerTagline: 'O espaço de trabalho inteligente para professores.',
    footerSupportNotice: 'Precisa de ajuda? Nossos recursos de suporte estão aqui para ajudá-lo a começar.',
    footerAllRightsReserved: 'Todos os direitos reservados.',
    startFreeTrial: 'Iniciar Teste Grátis',
    viewPricing: 'Ver Preços',
    featuresHeroTitle: 'Ferramentas poderosas criadas para professores',
    featuresHeroSubtitle: 'TeacherHubPro reúne as ferramentas essenciais que os educadores precisam para planejar, organizar, criar e gerenciar o trabalho em sala de aula com mais eficiência.',
    featuresCTATitle: 'Veja como o TeacherHubPro se encaixa no seu dia de ensino',
    featuresCTASubtitle: 'Explore a plataforma e descubra uma maneira melhor de planejar, organizar e ensinar.',
    contactTitle: 'Contato',
    contactSendMessage: 'Envie-nos uma mensagem',
    contactName: 'Nome',
    contactEmail: 'Email',
    contactSubject: 'Assunto',
    contactMessage: 'Mensagem',
    contactEmailSupport: 'Suporte por Email',
    contactHelpCenter: 'Central de Ajuda',
    landingCTATitle: 'Comece a simplificar seu fluxo de trabalho de ensino hoje',
    landingCTASubtitle: 'Junte-se aos educadores que usam TeacherHubPro para economizar tempo, manter-se organizados e trabalhar de forma mais inteligente com IA.',
    
    // Hero Section
    heroTitle: 'O Espaço de Trabalho Inteligente para Professores',
    heroSubtitle: 'Planeje aulas, gerencie notas, acompanhe a presença, crie materiais de aula e economize horas toda semana com uma plataforma de IA feita para professores.',
    seeHowItWorks: 'Veja Como Funciona',
    heroSupportingText: 'Sem taxas extras de IA. Feito para professores, tutores, educadores domésticos e escolas.',
    dashboardLessons: 'Aulas',
    dashboardStudents: 'Alunos',
    dashboardAvgGrade: 'Média',
    hoursSavedPerWeek: 'horas economizadas/semana',
    
    // Trust Bar
    trustBuiltForEducators: 'Feito para educadores modernos',
    trustAIIncluded: 'IA incluída em todos os planos',
    trustSecureEasy: 'Seguro e fácil de usar',
    trustReduceWorkload: 'Projetado para reduzir a carga administrativa',
    
    // Benefits Grid
    benefitSectionTitle: 'Tudo que os professores precisam, em um só lugar',
    benefitSectionSubtitle: 'Pare de alternar entre documentos, planilhas e ferramentas desconectadas. TeacherHubPro reúne seu fluxo de trabalho principal em uma plataforma simples.',
    benefitLessonPlanningTitle: 'Planejamento de Aulas',
    benefitLessonPlanningDesc: 'Crie planos de aula organizados e reutilizáveis em menos tempo com modelos integrados e suporte de IA.',
    benefitGradebookTitle: 'Boletim de Notas',
    benefitGradebookDesc: 'Acompanhe o progresso dos alunos com um boletim limpo e simples feito para uso real em sala de aula.',
    benefitAttendanceTitle: 'Controle de Presença',
    benefitAttendanceDesc: 'Gerencie a presença rapidamente e mantenha os registros de classe organizados sem papelada extra.',
    benefitAIToolsTitle: 'Ferramentas de IA para Ensino',
    benefitAIToolsDesc: 'Gere ideias de aulas, atividades, exercícios de escrita, materiais de aula e mais em segundos.',
    benefitPresentationsTitle: 'Apresentações e Recursos',
    benefitPresentationsDesc: 'Crie materiais de aula mais rápido e mantenha tudo conectado em um único fluxo de trabalho.',
    benefitWorkflowTitle: 'Fluxo de Trabalho para Professores',
    benefitWorkflowDesc: 'Projetado para educadores que precisam de ferramentas práticas, não software complexo.',
    
    // Outcomes Section
    outcomeSectionTitle: 'Menos tempo em administração, mais tempo ensinando',
    outcomePlanFaster: 'Planeje mais rápido',
    outcomePlanFasterDesc: 'Crie planos de aula, atividades de classe e materiais sem começar do zero toda vez.',
    outcomeStayOrganized: 'Mantenha-se organizado',
    outcomeStayOrganizedDesc: 'Mantenha notas, presença e recursos de ensino em um só lugar em vez de espalhados em várias ferramentas.',
    outcomeTeachConfidence: 'Ensine com confiança',
    outcomeTeachConfidenceDesc: 'Use IA e fluxos de trabalho otimizados para reduzir o estresse e manter seu trabalho de sala de aula em movimento.',
    
    // Social Proof Section
    socialProofSectionTitle: 'Construído para apoiar o trabalho real em sala de aula',
    socialProofEducators: 'Projetado especificamente para educadores',
    socialProofSupport: 'Inclui recursos de suporte e ajuda guiada',
    socialProofWorkflows: 'Construído para simplificar fluxos de trabalho comuns de professores',
    socialProofSuitable: 'Adequado para professores individuais, educadores domésticos e uso escolar',
    socialProofHelpCenter: 'Recursos de suporte disponíveis • Central de ajuda incluída',
    
    // Use Cases Section
    useCaseSectionTitle: 'Feito para diferentes ambientes de ensino',
    useCaseClassroomTitle: 'Professores de Sala de Aula',
    useCaseClassroomDesc: 'Gerencie planejamento, notas e organização diária da sala de aula em um só lugar.',
    useCaseHomeschoolTitle: 'Educadores Domésticos',
    useCaseHomeschoolDesc: 'Crie fluxos de trabalho flexíveis para aulas e mantenha os planos de aprendizagem organizados.',
    useCaseTutorsTitle: 'Tutores e Pequenos Programas',
    useCaseTutorsDesc: 'Acompanhe o trabalho dos alunos, planeje sessões e crie recursos de forma eficiente.',
    useCaseSchoolsTitle: 'Escolas e Equipes',
    useCaseSchoolsDesc: 'Apoie educadores com uma plataforma que melhora a consistência e economiza tempo.',
    
    // Feature Detail Pages - Common
    backToFeatures: 'Voltar para Recursos',
    keyBenefits: 'Benefícios Principais',
    howItWorks: 'Como Funciona',
    readyToGetStarted: 'Pronto para começar?',
    featureCTASubtitle: 'Junte-se a milhares de educadores que já estão economizando tempo com TeacherHubPro.',
    exploreOtherFeatures: 'Explorar Outros Recursos',
    learnMore: 'Saiba mais',
    featureLessonPlanningTitle: 'Planejamento de Aulas',
    featureLessonPlanningSubtitle: 'Crie planos organizados em menos tempo com modelos e IA.',
    featureLessonPlanningDescription: 'A ferramenta de planejamento ajuda você a criar planos completos rapidamente.',
    featureLessonPlanningBenefit1: 'Crie planos até 5x mais rápido',
    featureLessonPlanningBenefit2: 'Alinhe aos padrões automaticamente',
    featureLessonPlanningBenefit3: 'Reutilize planos para diferentes turmas',
    featureLessonPlanningBenefit4: 'Compartilhe com colegas em tempo real',
    featureLessonPlanningHowItWorks1: 'Escolha um modelo ou comece do zero',
    featureLessonPlanningHowItWorks2: 'Use sugestões de IA',
    featureLessonPlanningHowItWorks3: 'Salve e reutilize seus planos',
    featureGradebookTitle: 'Boletim de Notas',
    featureGradebookSubtitle: 'Acompanhe o progresso dos alunos facilmente.',
    featureGradebookDescription: 'Nosso boletim é intuitivo e poderoso.',
    featureGradebookBenefit1: 'Cálculos automáticos de notas',
    featureGradebookBenefit2: 'Acompanhamento visual do progresso',
    featureGradebookBenefit3: 'Gere boletins instantaneamente',
    featureGradebookBenefit4: 'Insights de IA para identificar dificuldades',
    featureGradebookHowItWorks1: 'Configure suas turmas',
    featureGradebookHowItWorks2: 'Crie tarefas e insira notas',
    featureGradebookHowItWorks3: 'Revise análises e gere relatórios',
    featureAttendanceTitle: 'Controle de Presença',
    featureAttendanceSubtitle: 'Gerencie presença rapidamente sem papelada.',
    featureAttendanceDescription: 'Adeus às folhas de presença em papel.',
    featureAttendanceBenefit1: 'Faça chamada em menos de 30 segundos',
    featureAttendanceBenefit2: 'Detecção automática de faltas',
    featureAttendanceBenefit3: 'Gere relatórios para administração',
    featureAttendanceBenefit4: 'Notificações opcionais aos pais',
    featureAttendanceHowItWorks1: 'Abra sua turma e marque presença',
    featureAttendanceHowItWorks2: 'Revise padrões de presença',
    featureAttendanceHowItWorks3: 'Exporte ou compartilhe',
    featureAIToolsTitle: 'Ferramentas de IA',
    featureAIToolsSubtitle: 'Gere ideias de aulas e materiais em segundos.',
    featureAIToolsDescription: 'Use o poder da IA para potencializar seu ensino.',
    featureAIToolsBenefit1: 'Gere ideias adaptadas à sua matéria',
    featureAIToolsBenefit2: 'Crie materiais diferenciados',
    featureAIToolsBenefit3: 'Construa avaliações automaticamente',
    featureAIToolsBenefit4: 'Sem taxas extras de IA',
    featureAIToolsHowItWorks1: 'Descreva o que você precisa',
    featureAIToolsHowItWorks2: 'A IA gera opções para seu nível',
    featureAIToolsHowItWorks3: 'Edite e salve na biblioteca',
    featurePresentationsTitle: 'Apresentações e Recursos',
    featurePresentationsSubtitle: 'Crie materiais mais rapidamente.',
    featurePresentationsDescription: 'Crie apresentações e recursos em um só lugar.',
    featurePresentationsBenefit1: 'Modelos educacionais',
    featurePresentationsBenefit2: 'Integra com planos de aula',
    featurePresentationsBenefit3: 'Compartilhe facilmente',
    featurePresentationsBenefit4: 'Importe recursos existentes',
    featurePresentationsHowItWorks1: 'Escolha um modelo',
    featurePresentationsHowItWorks2: 'Adicione conteúdo',
    featurePresentationsHowItWorks3: 'Vincule e compartilhe',
    featureWorkflowTitle: 'Fluxo de Trabalho Amigável',
    featureWorkflowSubtitle: 'Projetado para educadores que precisam de ferramentas práticas.',
    featureWorkflowDescription: 'Construímos para como os professores realmente trabalham.',
    featureWorkflowBenefit1: 'Interface intuitiva',
    featureWorkflowBenefit2: 'Funciona em desktop e mobile',
    featureWorkflowBenefit3: 'Configuração rápida',
    featureWorkflowBenefit4: 'Integra com suas ferramentas',
    featureWorkflowHowItWorks1: 'Cadastre-se e configure',
    featureWorkflowHowItWorks2: 'Acesse tudo de um painel',
    featureWorkflowHowItWorks3: 'Dados sincronizam automaticamente',
    featureClassroomTeachersTitle: 'Para Professores',
    featureClassroomTeachersSubtitle: 'Gerencie planejamento e notas em um lugar.',
    featureClassroomTeachersDescription: 'TeacherHubPro reúne todas suas ferramentas.',
    featureClassroomTeachersBenefit1: 'Planeje para múltiplas turmas',
    featureClassroomTeachersBenefit2: 'Acompanhe notas e presença',
    featureClassroomTeachersBenefit3: 'Gere relatórios para pais',
    featureClassroomTeachersBenefit4: 'Acesse de qualquer lugar',
    featureClassroomTeachersHowItWorks1: 'Configure suas turmas',
    featureClassroomTeachersHowItWorks2: 'Planeje e registre diariamente',
    featureClassroomTeachersHowItWorks3: 'Comunique via portal',
    featureHomeschoolTitle: 'Para Ensino Doméstico',
    featureHomeschoolSubtitle: 'Crie fluxos de aprendizagem flexíveis.',
    featureHomeschoolDescription: 'TeacherHubPro ajuda a criar caminhos personalizados.',
    featureHomeschoolBenefit1: 'Caminhos personalizados para cada criança',
    featureHomeschoolBenefit2: 'Registros organizados',
    featureHomeschoolBenefit3: 'Ideias geradas por IA',
    featureHomeschoolBenefit4: 'Horários flexíveis',
    featureHomeschoolHowItWorks1: 'Crie perfis para cada criança',
    featureHomeschoolHowItWorks2: 'Acompanhe o progresso',
    featureHomeschoolHowItWorks3: 'Gere relatórios',
    featureTutorsTitle: 'Para Tutores',
    featureTutorsSubtitle: 'Acompanhe alunos eficientemente.',
    featureTutorsDescription: 'TeacherHubPro ajuda você a se manter organizado.',
    featureTutorsBenefit1: 'Acompanhe múltiplos alunos',
    featureTutorsBenefit2: 'Planeje e mantenha notas',
    featureTutorsBenefit3: 'Relatórios para pais',
    featureTutorsBenefit4: 'Materiais personalizados',
    featureTutorsHowItWorks1: 'Adicione seus alunos',
    featureTutorsHowItWorks2: 'Planeje e acompanhe',
    featureTutorsHowItWorks3: 'Compartilhe via portal',
    featureSchoolsTitle: 'Para Escolas',
    featureSchoolsSubtitle: 'Apoie educadores com uma plataforma consistente.',
    featureSchoolsDescription: 'TeacherHubPro escala com sua escola.',
    featureSchoolsBenefit1: 'Ferramentas consistentes',
    featureSchoolsBenefit2: 'Biblioteca de recursos compartilhada',
    featureSchoolsBenefit3: 'Painel administrativo',
    featureSchoolsBenefit4: 'Preços por volume',
    featureSchoolsHowItWorks1: 'Entre em contato para preços',
    featureSchoolsHowItWorks2: 'Integre professores',
    featureSchoolsHowItWorks3: 'Monitore e suporte'
  },
  de: {
    absent: "Abwesend",
    activities: "Aktivitäten",
    art: "Kunst",
    assignments: "Aufgaben",
    attendanceReport: "Anwesenheitsbericht",
    book: "Buch",
    brainstorming: "Brainstorming",
    buildingBackground: "Hintergrundwissen aufbauen",
    category: "Kategorie",
    comprehensionQuestions: "Verständnisfragen",
    createAssignment: "Aufgabe erstellen",
    createPlan: "Plan erstellen",
    dayTheme: "Tagesthema",
    description: "Beschreibung",
    dictionary: "Wörterbuch",
    dueDate: "Fälligkeitsdatum",
    duplicateLastWeek: "Letzte Woche duplizieren",
    excused: "Entschuldigt",
    exercisePractice: "Übungspraxis",
    expectations: "Erwartungen",
    exportPdf: "PDF exportieren",
    firstWeek: "Erste Woche",
    foundationalSkills: "Grundfertigkeiten",
    gradeEntry: "Noteneingabe",
    guidedReading: "Geführtes und chorisches Lesen",
    handouts: "Arbeitsblätter",
    health: "Gesundheit",
    homework: "Hausaufgaben",
    language: "Sprache",
    late: "Verspätet",
    level1Memory: "Stufe 1: Gedächtnis",
    level2Processing: "Stufe 2: Verarbeitung",
    level3Strategic: "Stufe 3: Strategisches Denken",
    level4Extended: "Stufe 4: Erweitertes Denken",
    listeningAndSpeaking: "Hören/Sprechen",
    markAllPresent: "Alle als anwesend markieren",
    materials: "Materialien",
    mathematics: "Mathematik",
    missing: "Fehlend",
    notebook: "Heft",
    objectiveOfWeek: "Wochenziel",
    oralQuestions: "Mündliche Fragen",
    other: "Sonstiges",
    physicalEducation: "Sport",
    points: "Punkte",
    present: "Anwesend",
    pricingActiveSubscription: "Aktives Abonnement",
    pricingAdminAccount: "Administratorkonto - Vollzugriff",
    pricingContactUs: "Kontaktieren Sie uns",
    pricingCurrentPlan: "Aktueller Tarif",
    pricingDaysRemaining: "Tage verbleibend",
    pricingDistrictDesc: "Für Schulbezirke mit erweiterter Verwaltung",
    pricingDistrictPlan: "Bezirk",
    pricingFeatureAIAssistant: "KI-Assistent (Claude)",
    pricingFeatureAdvancedAnalytics: "Erweiterte Analysen",
    pricingFeatureAttendance: "Anwesenheitsverfolgung",
    pricingFeatureBasicLessonPlanning: "Grundlegende Unterrichtsplanung",
    pricingFeatureCustomTemplates: "Benutzerdefinierte Vorlagen",
    pricingFeatureEverythingPro: "Alles aus Pro",
    pricingFeatureEverythingSchool: "Alles aus School",
    pricingFeatureEverythingStarter: "Alles aus Starter",
    pricingFeatureMultiSchool: "Verwaltung mehrerer Schulen",
    pricingFeatureSchoolDashboard: "Schul-Dashboard",
    pricingFeatureSharedResources: "Geteilte Ressourcen",
    pricingFeatureTeacherManagement: "Lehrerverwaltung",
    pricingFeatureUnlimitedClasses: "Unbegrenzte Klassen",
    pricingFeatureUnlimitedStudents: "Unbegrenzte Schüler",
    pricingForSchoolPlans: "für Schultarife",
    pricingFreeTrial: "Kostenlose Testphase",
    pricingGetStarted: "Loslegen",
    pricingNumberOfTeachers: "Anzahl der Lehrkräfte",
    pricingProDesc: "Erweiterte Funktionen für Power-User",
    pricingProPlan: "Pro",
    pricingSchoolDesc: "Für ganze Schulen mit Teamfunktionen",
    pricingSchoolDistrict: "Für Schulen & Bezirke",
    pricingSchoolPlan: "Schule",
    pricingStarterDesc: "Perfekt für einzelne Lehrkräfte",
    pricingStarterPlan: "Starter",
    pricingSubscribe: "Abonnieren",
    pricingTeachers: "Lehrkräfte",
    pricingTrialExpired: "Ihre kostenlose Testphase ist abgelaufen. Bitte wählen Sie einen Tarif, um TeacherHubPro weiter zu nutzen.",
    project: "Projekt",
    quiz: "Quiz",
    readPages: "Zu lesende Seiten",
    reading: "Lesen",
    religion: "Religion",
    saveAsTemplate: "Als Vorlage speichern",
    science: "Naturwissenschaften",
    secondWeek: "Zweite Woche",
    skillsOfWeek: "Fähigkeiten der Woche",
    socialStudies: "Sozialkunde",
    spanish: "Spanisch",
    standards: "Standards",
    story: "Geschichte/Titel",
    submitAttendance: "Anwesenheit übermitteln",
    takeAttendance: "Anwesenheit erfassen",
    tardy: "Verspätet",
    teachersGuide: "Lehrerhandbuch",
    test: "Test",
    testQuiz: "Test/Quiz",
    title: "Titel",
    unit: "Einheit",
    vocabularyDevelopment: "Wortschatzentwicklung",
    weekOf: "Woche vom",
    weeklyPlan: "Wochenplan",
    writing: "Schreiben",

    // Navigation
    dashboard: 'Übersicht',
    planner: 'Unterrichtsplaner',
    attendance: 'Anwesenheit',
    gradebook: 'Notenbuch',
    classes: 'Klassen',
    settings: 'Einstellungen',
    logout: 'Abmelden',
    
    // Auth
    login: 'Anmelden',
    register: 'Registrieren',
    email: 'E-Mail',
    password: 'Passwort',
    name: 'Name',
    signInWithGoogle: 'Mit Google anmelden',
    orContinueWith: 'oder weiter mit',
    dontHaveAccount: 'Kein Konto?',
    alreadyHaveAccount: 'Bereits ein Konto?',
    
    // Dashboard
    welcome: 'Willkommen',
    todayOverview: 'Heutige Übersicht',
    quickActions: 'Schnellaktionen',
    attendancePending: 'Anwesenheit ausstehend',
    upcomingAssignments: 'Anstehende Aufgaben',
    recentPlans: 'Aktuelle Pläne',
    totalClasses: 'Gesamtklassen',
    totalStudents: 'Gesamtschüler',
    totalPlans: 'Gesamtpläne',
    
    // Classes
    addClass: 'Klasse hinzufügen',
    className: 'Klassenname',
    grade: 'Klassenstufe',
    section: 'Abschnitt',
    subject: 'Fach',
    yearTerm: 'Jahr/Semester',
    students: 'Schüler',
    addStudent: 'Schüler hinzufügen',
    firstName: 'Vorname',
    lastName: 'Nachname',
    studentNumber: 'Schülernummer',
    parentEmail: 'Eltern-E-Mail',
    notes: 'Notizen',
    accommodations: 'Anpassungen',
    
    // Common
    save: 'Speichern',
    cancel: 'Abbrechen',
    delete: 'Löschen',
    edit: 'Bearbeiten',
    view: 'Ansehen',
    search: 'Suchen',
    filter: 'Filtern',
    loading: 'Laden...',
    noData: 'Keine Daten verfügbar',
    confirmDelete: 'Möchten Sie dies wirklich löschen?',
    success: 'Erfolg',
    error: 'Fehler',
    from: 'Von',
    to: 'Bis',
    date: 'Datum',
    teacher: 'Lehrer',
    selectLanguage: 'Sprache auswählen',
    
    // Days
    monday: 'Montag',
    tuesday: 'Dienstag',
    wednesday: 'Mittwoch',
    thursday: 'Donnerstag',
    friday: 'Freitag',
    
    // Games
    educationalGames: 'Lernspiele',
    myGames: 'Meine Spiele',
    createGame: 'Spiel erstellen',
    play: 'Spielen',
    copyLink: 'Link kopieren',
    shareToClassroom: 'Im Classroom teilen',
    deleteGame: 'Spiel löschen',
    questions: 'Fragen',
    gameType: 'Spieltyp',
    gradeLevel: 'Klassenstufe',
    
    // Landing Page
    landingHeroTitle: 'Ihr komplettes Klassenzimmer in einer Plattform',
    landingHeroSubtitle: 'Unterrichtsplanung, Anwesenheit, Noten und KI-Assistent. Alles was ein Lehrer braucht.',
    landingBuiltForTeachers: 'Entwickelt für Lehrer',
    landingStartFree: 'Kostenlos starten',
    landingViewPricing: 'Preise ansehen',
    landingUsedBySchools: 'Genutzt von Schulen weltweit',
    landingEverythingYouNeed: 'Alles was Sie brauchen',
    landingEverythingDesc: 'Eine komplette Plattform speziell für Lehrer entwickelt.',
    landingFeatures: 'Funktionen',
    landingAiAssistant: 'KI-Assistent',
    landingPoweredBy: 'Entwickelt von Anthropic',
    landingActiveTeachers: 'aktive Lehrer',
    landingHomeschool: 'Heimunterricht',
    landingIntegrations: 'Integrationen',
    landingCustomization: 'Anpassung',
    landingPricing: 'Preise',
    landingGetStarted: 'Starten - Anmelden',
    landingStart: 'Starten',
    landingAiLessonPlan: 'KI-Plan',
    landingPresentations: 'Präsentationen',
    landingGrades: 'Noten',
    landingPlanFaster: 'Schneller planen',
    landingPlanFasterDesc: 'Erstellen Sie wöchentliche Unterrichtspläne in Sekunden.',
    landingWeek1: 'Woche 1',
    landingYourBrand: 'Ihre Marke, Ihr Stil',
    landingYourBrandDesc: 'Personalisieren Sie mit dem Logo und den Farben Ihrer Schule.',
    landingYourSchool: 'Ihre Schule',
    landingCustomBranding: 'Eigenes Branding',
    landingExportPro: 'Professionell exportieren',
    landingExportProDesc: 'Erstellen Sie druckfertige PDFs: Pläne, Zeugnisse, Vertretungsmappen.',
    landingDownloadPdf: 'PDF herunterladen',
    landingAiAssistantTitle: 'KI-Assistent',
    landingAiAssistantDesc: 'Erstellen Sie Unterrichtspläne, Aktivitäten und Fragen mit KI.',
    landingLessonPlans: 'Unterrichtspläne',
    landingQuizzesTests: 'Quiz und Tests',
    landingDigitalClassroom: 'Ihr digitales Klassenzimmer',
    landingTryFree: '30 Tage kostenlos testen',
    landingNoCard: 'Keine Kreditkarte erforderlich',
    landingFullName: 'Vollständiger Name',
    landingCreateAccount: 'Kostenloses Konto erstellen',
    landingOrLoginWith: 'Oder anmelden mit',
    landingHaveAccount: 'Haben Sie ein Konto?',
    landingLoginHere: 'Hier anmelden',
    landingNeedAccount: 'Brauchen Sie ein Konto?',
    landingRegisterHere: 'Hier registrieren',
    // AI Section
    landingAiTeachingAssistant: 'KI-Unterrichtsassistent',
    landingYouAsked: 'Sie fragten:',
    landingAiResponse: 'KI-Antwort:',
    landingAiQuestion: 'Wie kann ich äquivalente Brüche in der 4. Klasse unterrichten?',
    landingAiAnswer: 'Hier ist ein Unterrichtsplan basierend auf Standards mit praktischen Aktivitäten...',
    landingQuizzes: 'Quiz',
    landingWorksheets: 'Arbeitsblätter',
    landingIdeas: 'Ideen',
    landingNewAi: 'Neu: Künstliche Intelligenz',
    landingAiAssistantToTeach: 'Ihr KI-Assistent für besseren Unterricht',
    landingAiAssistantDesc2: 'Erstellen Sie Unterrichtspläne, Quiz, Zusammenfassungen und Aktivitäten basierend auf Standards, sofort.',
    landingAlignedStandards: 'Ausgerichtet an Bildungsstandards',
    landingGenerateSeconds: 'Inhalte in Sekunden erstellen',
    landingBilingual: 'Mehrsprachig',
    landingPoweredBy: 'Betrieben von Claude Sonnet 4.5',
    landingTryItFree: 'Kostenlos testen',
    landingIncludedSubscription: 'Im Abonnement enthalten',
    // Integrations Section
    landingConnectTools: 'Verbinden Sie Ihre Lieblingswerkzeuge',
    landingConnectToolsDesc: 'Integrieren Sie Plattformen, die Sie bereits nutzen.',
    landingCalendarSync: 'Kalender-Sync',
    landingCalendarSyncDesc: 'Exportieren Sie Ihre Pläne zu Google Calendar, Outlook oder Apple Calendar.',
    landingGoogleClassroom: 'Google Classroom',
    landingGoogleClassroomDesc: 'Teilen Sie Aktivitäten und Spiele direkt mit Ihren Schülern.',
    landingPdfExport: 'PDF-Export',
    landingPdfExportDesc: 'Erstellen Sie professionelle PDFs für Pläne, Zeugnisse und mehr.',
    landingEmailNotifications: 'E-Mail-Benachrichtigungen',
    landingEmailNotificationsDesc: 'Senden Sie automatische Berichte an Eltern und Administratoren.',
    landingViewIntegrations: 'Alle Integrationen ansehen',
    // Customization Section
    landingYourBrandYourWay: 'Ihre Marke, auf Ihre Art',
    landingCustomizeDesc: 'Personalisieren Sie alles mit den Farben, dem Logo und den Präferenzen Ihrer Schule.',
    landingSchoolLogo: 'Schullogo',
    landingSchoolLogoDesc: 'Fügen Sie Ihr Logo zu allen Dokumenten und Berichten hinzu.',
    landingColorThemes: 'Farbthemen',
    landingColorThemesDesc: 'Wählen Sie Farben, die zu Ihrer Schulidentität passen.',
    landingCustomTemplates: 'Benutzerdefinierte Vorlagen',
    landingCustomTemplatesDesc: 'Erstellen Sie wiederverwendbare Vorlagen für Ihre Pläne.',
    landingMultiLanguage: 'Mehrsprachig',
    landingMultiLanguageDesc: 'Arbeiten Sie in mehreren Sprachen.',
    // Homeschool Section
    landingHomeschoolTitle: 'Heimunterricht',
    landingHomeschoolSubtitle: 'Ihr kompletter Heimunterricht-Begleiter',
    landingHomeschoolDesc: 'Alles, was Sie für eine erfolgreiche Heimunterricht-Erfahrung brauchen.',
    landingAdaptiveLearning: 'Adaptives Lernen',
    landingAdaptiveLearningDesc: 'Lernpfade, die sich an das Tempo jedes Kindes anpassen.',
    landingFullVisibility: 'Volle Sichtbarkeit',
    landingFullVisibilityDesc: 'Verfolgen Sie den Fortschritt in Echtzeit mit detaillierten Berichten.',
    landingFlexibleCurriculum: 'Flexibler Lehrplan',
    landingFlexibleCurriculumDesc: 'Passen Sie den Lehrplan an die Bedürfnisse Ihrer Familie an.',
    landing100Customizable: '100% Anpassbar',
    landingDownloadLearn: 'Herunterladen und Lernen',
    landingDownloadLearnDesc: 'Exportieren Sie Materialien für Offline-Lernen.',
    landingPdfExportFeature: 'PDF-Export',
    landingTextToSpeech: 'Text-zu-Sprache',
    landingTextToSpeechDesc: 'Audio-Unterstützung für alle Leseinhalte.',
    landingAudioLearning: 'Audio-Lernen',
    landingSafeContent: 'Sicherer Inhalt',
    landingSafeContentDesc: 'Altersgerechte und geprüfte Materialien.',
    landingParentalControl: 'Elternkontrolle',
    landingSubjectModules: 'Fachmodule',
    landingMathematics: 'Mathematik',
    landingLanguageArts: 'Deutsch',
    landingScience: 'Naturwissenschaften',
    landingAstronomy: 'Astronomie',
    landingGeography: 'Geographie',
    landingHistory: 'Geschichte',
    landingArt: 'Kunst',
    landingPhysicalEd: 'Sport',
    landingStartHomeschooling: 'Heimunterricht starten',
    // CTA Section
    landing7DaysFree: '7 Tage kostenlos',
    landingGetStartedToday: 'Starten Sie heute',
    landingStartYourJourney: 'Starten Sie Ihre digitale Klassenzimmer-Reise. Keine Kreditkarte erforderlich.',
    landingNoCreditCard: 'Keine Kreditkarte erforderlich',
    landingSetup2Min: 'Einrichtung in 2 Minuten',
    landingCancelAnytime: 'Jederzeit kündbar',
    landingSupportBilingual: 'Mehrsprachiger Support',
    landingActiveTeachers: 'aktive Lehrer',
    landingSignIn: 'Anmelden',
    landingCreateAccountTab: 'Konto erstellen',
    landingContinueGoogle: 'Mit Google fortfahren',
    landingOrWithEmail: 'oder mit E-Mail',
    landingEmail: 'E-Mail',
    landingPassword: 'Passwort',
    landingFullNameLabel: 'Vollständiger Name',
    landingCreateFreeAccount: 'Kostenloses Konto erstellen',
    // Footer
    landingContact: 'Kontakt',
    landingPrivacyPolicy: 'Datenschutzrichtlinie',
    landingHelp: 'Hilfe',
    landingTermsOfUse: 'Nutzungsbedingungen',
    landingCookiesPolicy: 'Cookie-Richtlinie',
    landingAccessibility: 'Barrierefreiheit',
    landingAllRightsReserved: 'Alle Rechte vorbehalten.',
    
    // AI Features Section
    landingAdvancedAiFeatures: 'Erweiterte KI-Funktionen',
    landingIntelligentPlanning: 'Intelligente Planung mit KI',
    landingAiPlanningDesc: 'Von der Erstellung eines kompletten Plans bis zu täglichen Vorschlägen hilft Ihnen die KI bei jedem Schritt.',
    landingGenerateFullWeek: 'Ganze Woche Generieren',
    landingGenerateFullWeekDesc: 'Mit einem Klick generieren Sie Aktivitäten für alle 5 Tage mit pädagogischer Progression: Einführung → Übung → Beherrschung → Bewertung.',
    landingDays: 'Tage',
    landingProgression: 'Progression',
    landingDailySuggestions: 'Tägliche Vorschläge',
    landingDailySuggestionsDesc: 'Jeder Tag zeigt seine pädagogische Phase (Einführung, Übung, Beherrschung). Erhalten Sie spezifische Vorschläge für jede Stufe.',
    landingMonFri: 'Mo → Fr',
    landingContextual: 'Kontextbezogen',
    landingReusableTemplates: 'Wiederverwendbare Vorlagen',
    landingReusableTemplatesDesc: 'Speichern Sie Ihre besten Pläne als Vorlagen. Klonen und passen Sie sie mit einem Klick für neue Themen an.',
    landingSave: 'Speichern',
    landingClone: 'Klonen',
    landing5StarterTemplates: '5 Startvorlagen',
    landing5StarterTemplatesDesc: 'Starten Sie sofort mit professionellen Vorlagen: Brüche, Leseverständnis, Wissenschaftliche Methode, Schreiben und Multiplikation.',
    landingMath: 'Mathe',
    landingELA: 'Deutsch',
    landingAiCustomize: 'Mit KI Anpassen',
    landingAiCustomizeDesc: 'Nehmen Sie eine beliebige Vorlage und passen Sie sie an ein neues Thema an. Die KI behält die erfolgreiche Struktur bei, während der Inhalt geändert wird.',
    landingNewTopic: 'Neues Thema',
    landingSameStructure: 'Gleiche Struktur',
    landingTemplateOfWeek: 'Vorlage der Woche',
    landingTemplateOfWeekDesc: 'Jede Woche heben wir eine andere Vorlage auf Ihrem Dashboard mit spezifischen Anpassungstipps hervor.',
    landingWeeklyRotation: 'Wöchentliche Rotation',
    landingTips: 'Tipps',
    landingTryAiFree7Days: 'Testen Sie KI 7 Tage kostenlos',
    
    // AI Presentations Section
    landingNewAiPresentations: 'NEU! KI-Präsentationen',
    landingCreatePresentations: 'Erstellen Sie fesselnde Präsentationen',
    landingPresentationsDesc: 'Generieren Sie komplette Bildungspräsentationen mit KI. Wählen Sie visuelle Themen, fügen Sie Bilder hinzu, wenden Sie Übergänge an und speichern Sie.',
    landingAiGenerate: 'Mit KI Generieren',
    landingAiGenerateDesc: '6+ Folien in Sekunden',
    landing8VisualThemes: '8 Visuelle Themen',
    landing8VisualThemesDesc: 'Ozean, Galaxie, Tafel...',
    landingSaveAndLoad: 'Speichern und Laden',
    landingSaveAndLoadDesc: 'Persönliche Bibliothek',
    landingDownloadHtml: 'HTML Herunterladen',
    landingDownloadHtmlDesc: 'Offline verwenden',
    landingCreateFirstPresentation: 'Meine erste Präsentation erstellen',
    landingTheSolarSystem: 'Das Sonnensystem',
    landingScienceGrade: 'Wissenschaft - 4. Klasse',
    landingTransitionsEnabled: '✨ Übergänge aktiviert',
    
    // Customization Section
    landingFullCustomization: 'Vollständige Anpassung',
    landingSchoolBrandingTitle: 'Die Identität Ihrer Schule auf jedem Dokument',
    landingSchoolBrandingDesc: 'Von Ihrem Logo bis zu Ihren Farben spiegelt jedes PDF und jeder Bericht das Branding Ihrer Institution wider.',
    landingSchoolLogoAll: 'Das Logo Ihrer Schule auf allen Dokumenten',
    landingCustomColorsMatch: 'Benutzerdefinierte Farben passend zu Ihrer Marke',
    landingPdfFormats: 'Die genauen PDF-Formate Ihrer Schule',
    landingCentralizedAdmin: 'Zentralisiertes Admin-Dashboard',
    landingTeachersPlanning: 'Lehrerplanung',
    landingYourLogoHere: 'Ihr Logo hier',
    landingYourColors: 'Ihre Farben',
    
    // Integrations Section
    landingConnectFavorite: 'Verbinden Sie sich mit Ihren Lieblingsplattformen',
    landingConnectFavoriteDesc: 'Synchronisieren Sie mit den Tools, die Sie bereits verwenden, für einen nahtlosen Arbeitsablauf.',
    landingAvailable: 'Verfügbar',
    landingMostPopular: 'Am beliebtesten',
    landingImportClasses: 'Klassen Importieren',
    landingImportClassesDesc: 'Bringen Sie Ihre Klassen und Schülerlisten automatisch',
    landingSyncGrades: 'Noten Synchronisieren',
    landingSyncGradesDesc: 'Senden Sie Noten mit einem Klick an Google Classroom',
    landingSecurePrivate: 'Sicher & Privat',
    landingSecurePrivateDesc: 'Sichere OAuth-Authentifizierung, Ihre Daten geschützt',
    landingSetupMinutes: 'Einrichtung in Minuten',
    landingNoExtraCost: 'Keine zusätzlichen Kosten',
    landingSupportIncluded: 'Support inklusive',
    landingComingSoon: 'Demnächst',
    landingNeedIntegration: 'Brauchen Sie eine andere Integration?',
    landingNeedIntegrationDesc: 'Wir fügen ständig neue Integrationen hinzu. Teilen Sie uns mit, welche Plattformen Sie nutzen, und wir werden sie priorisieren.',
    landingRequestIntegration: 'Integration Anfragen',
    
    // Homeschool Section
    landingPerfectHomeschool: 'Perfekt für Heimschul-Pädagogen',
    landingHomeschoolCompleteDesc: 'Alles, was Sie brauchen, um ein personalisiertes Lernerlebnis zu schaffen, von der Planung bis zur Fortschrittsverfolgung.',
    landingAiPowered: 'KI-Gestützt',
    landingParentDashboard: 'Eltern-Dashboard',
    landingParentDashboardDesc: 'Vollständiger Überblick über Fortschritte, Noten und anstehende Arbeiten jedes Kindes in einem einheitlichen Dashboard.',
    landingFlexibleCurriculumDesc2: 'Passen Sie Fächer, Tempo und Lehrmethoden an den einzigartigen Lernstil jedes Kindes an.',
    landing100Customizable: '100% Anpassbar',
    landingDownloadAndLearn: 'Herunterladen und Lernen',
    landingDownloadAndLearnDesc: 'Exportieren Sie Lektionen und Materialien als PDFs. Lernen Sie auch offline oder unterwegs weiter.',
    landingAudioLearning: 'Audio-Lernen',
    landingTextToSpeechDesc2: 'Integrierte Text-zu-Sprache für alle Inhalte. Perfekt für auditive Lerner oder Lesehilfe.',
    landingSafeContentDesc2: 'Alle Inhalte sind altersgerecht und pädagogisch geprüft. Volle elterliche Kontrolle über das, was Kinder zugreifen können.',
    
    // New Landing Page Copy (German)
    landingSave5Hours: 'Sparen Sie 5+ Stunden Jede Woche',
    landingSave5HoursTitle: 'Sparen Sie 5+ Stunden Jede Woche bei Planung, Benotung und Verwaltung',
    landingSave5HoursSubtitle: 'TeacherHubPro hilft beschäftigten Lehrern, Unterrichtspläne zu erstellen, Noten zu verwalten, Anwesenheit zu erfassen und KI zu nutzen, um die tägliche Arbeit zu vereinfachen — alles an einem Ort.',
    landingBuiltForBusyTeachers: 'Für beschäftigte Lehrer entwickelt',
    landingStartFreeNoCreditCard: 'Kostenlos Starten — Keine Kreditkarte',
    landingSeeHowItWorks: 'Sehen Sie, Wie Es Funktioniert',
    landingTeachingIsMore: 'Unterrichten ist Mehr als Unterrichten',
    landingTeachingIsMoreDesc: 'Zwischen Unterrichtsplanung, Benotung, Anwesenheit, Berichten und Kommunikation — die meisten Lehrer verbringen jede Woche Stunden mit Aufgaben, die Zeit von Schülern und Privatleben stehlen.',
    landingYouBecameTeacher: 'Sie wurden Lehrer, um zu erziehen.',
    landingNotToDrown: 'Nicht um in Papierkram zu ertrinken.',
    landingEverythingSimple: 'Alles was Sie Brauchen. Eine Einfache Plattform.',
    landingLessBusywork: 'Weniger Verwaltungsarbeit. Mehr Unterricht.',
    landingGenerateLessons: 'Erstellen Sie strukturierte Unterrichtspläne in Minuten',
    landingTrackGrades: 'Verfolgen Sie Noten mit einem benutzerfreundlichen digitalen Notenbuch',
    landingRecordAttendance: 'Erfassen Sie Anwesenheit schnell',
    landingOrganizeData: 'Organisieren Sie Klassendaten in einem Dashboard',
    landingUseAiTools: 'Nutzen Sie KI-Tools, um die Vorbereitung zu beschleunigen',
    landingWhatTeachersExperience: 'Was Lehrer Erleben',
    landingFasterPlanning: 'Schnellere wöchentliche Planung',
    landingLessTimeGrading: 'Weniger Zeit für Benotung',
    landingBetterOrganization: 'Bessere Organisation',
    landingMoreConfidence: 'Mehr Selbstvertrauen im Unterricht',
    landingMorePersonalTime: 'Mehr persönliche Zeit nach der Schule',
    landing3SimpleSteps: 'Starten Sie in 3 Einfachen Schritten',
    landingValueInMinutes: 'Sie sehen den Wert in Minuten — nicht Stunden.',
    landingCreateClasses: 'Erstellen Sie Ihre Klassen',
    landingCreateClassesDesc: 'Richten Sie Ihre Klassen ein und fügen Sie Schüler in Sekunden hinzu',
    landingGenerateWithAi: 'Generieren Sie Unterricht mit KI',
    landingGenerateWithAiDesc: 'Erstellen Sie umfassende Unterrichtspläne mit KI-Unterstützung',
    landingManageGradesAttendance: 'Verwalten Sie Noten und Anwesenheit Mühelos',
    landingManageGradesAttendanceDesc: 'Verfolgen Sie alles von einem intuitiven Dashboard',
    landingBuiltForEducators: 'Speziell für Pädagogen Entwickelt',
    landingElementaryTeachers: 'Grundschullehrer',
    landingMiddleSchoolEducators: 'Mittelschullehrer',
    landingHighSchoolTeachers: 'Oberschullehrer',
    landingHomeschoolEducators: 'Heimschul-Pädagogen',
    landingInstructionalCoaches: 'Pädagogische Coaches',
    landingJoinThousands: 'Schließen Sie sich Tausenden von Lehrern an',
    landingJoinThousandsDesc: 'Pädagogen im ganzen Land nutzen bereits TeacherHubPro, um ihren Arbeitsablauf zu optimieren, mehr Zeit mit Schülern zu verbringen und ihre Abende zurückzugewinnen.',
    landingReadyToSimplify: 'Bereit, Ihr Lehrerleben zu Vereinfachen?',
    landingStartFreeToday: 'Heute Kostenlos Starten',
    landingExperienceTimeSaved: 'Erstellen Sie noch heute Ihr kostenloses Konto und erleben Sie, wie viel Zeit Sie diese Woche sparen können.',
    landingNoCreditCardRequired: 'Keine Kreditkarte erforderlich',
    landingSetupIn2Minutes: 'Einrichtung in unter 2 Minuten',
    landingCancelAnytime: 'Jederzeit kündbar',
    landingFullAccess: 'Voller Zugang zu allen Funktionen',
    landingTeachersUsing: 'Lehrer nutzen bereits TeacherHubPro',
    landingSimplifyingTeaching: 'Unterricht vereinfachen, ein Klassenzimmer nach dem anderen.',
    landingLogIn: 'Anmelden',
    landingStartFreeCta: 'Kostenlos Starten',
    landingBackToHome: 'Zurück zur Startseite',
    landingWelcomeBack: 'Willkommen Zurück',
    landingSignInAccess: 'Melden Sie sich an, um auf Ihr Dashboard zuzugreifen',
    landingStartFreeTrial: 'Starten Sie Ihre Kostenlose Testversion',
    landingJoinThousandsTeachers: 'Schließen Sie sich Tausenden von Lehrern an, die jede Woche Zeit sparen',
    landingCreateFreeAccount: 'Kostenloses Konto Erstellen',
    landingSignInTab: 'Anmelden',
    landingContinueWithGoogle: 'Mit Google Fortfahren',
    landingOrWithEmail: 'oder mit E-Mail',
    landingFullName: 'Vollständiger Name',
    landingYourName: 'Ihr Name',
    landingYourEmail: 'sie@schule.de',
    landingCreatePassword: 'Erstellen Sie ein Passwort',
    landingCreatingAccount: 'Konto wird erstellt...',
    landingSigningIn: 'Anmeldung...',
    
    // Feature Sections (German)
    landingEngageStudents: 'Schüler Wie Nie Zuvor Einbinden',
    landingEngageStudentsDesc: 'Verwandeln Sie Ihr Klassenzimmer mit interaktiven Spielen, adaptivem Lernen und Live-Quizzen, die Schüler motiviert und lernend halten.',
    landingPlayToLearnTitle: 'Spielend Lernen',
    landingPlayToLearnSubtitle: 'Live-Spiele im Kahoot-Stil',
    landingPlayToLearnDesc: 'Veranstalten Sie Live-Quiz-Spiele oder weisen Sie Aktivitäten im eigenen Tempo zu. Schüler treten mit einer PIN bei und konkurrieren in Echtzeit mit sofortigem Feedback.',
    landingPlayToLearnFeature1: 'Live-Multiplayer-Quizze',
    landingPlayToLearnFeature2: 'Aufgaben im eigenen Tempo',
    landingPlayToLearnFeature3: 'Echtzeit-Ranglisten',
    landingPlayToLearnFeature4: '9+ Spielmodi',
    landingEducationalGamesTitle: 'Lernspiele',
    landingEducationalGamesSubtitle: 'Spaßige Lernaktivitäten',
    landingEducationalGamesDesc: 'Erstellen Sie benutzerdefinierte Spiele für jedes Fach. Teilen Sie mit Schülern per Link oder Google Classroom. Verfolgen Sie Engagement und Fortschritt.',
    landingEducationalGamesFeature1: 'Anpassbare Spielvorlagen',
    landingEducationalGamesFeature2: 'Per Link oder QR-Code teilen',
    landingEducationalGamesFeature3: 'Schülerfortschritt verfolgen',
    landingEducationalGamesFeature4: 'Funktioniert auf jedem Gerät',
    landingAdaptiveLearningTitle: 'Adaptives Lernen',
    landingAdaptiveLearningSubtitle: 'Personalisierte Lernpfade',
    landingAdaptiveLearningDesc: 'KI-gestützte Lernpfade, die sich an das Tempo und Niveau jedes Schülers anpassen. Lücken identifizieren und gezieltes Üben ermöglichen.',
    landingAdaptiveLearningFeature1: 'KI-gestützte Empfehlungen',
    landingAdaptiveLearningFeature2: 'Personalisiertes Üben',
    landingAdaptiveLearningFeature3: 'Fortschrittsanalysen',
    landingAdaptiveLearningFeature4: 'Automatische Schwierigkeitsanpassung',
    landingExploreFeature: 'Erkunden',
    landingProductLabel: 'Produkt',
    landingCompanyLabel: 'Unternehmen',
    landingLegalLabel: 'Rechtliches',
    landingHelpCenter: 'Hilfezentrum',
    landingPrivacyPolicyLink: 'Datenschutzrichtlinie',
    landingTermsOfServiceLink: 'Nutzungsbedingungen',
    
    // Platform Integrations (German)
    landingPlatformIntegrations: 'Plattform-Integrationen',
    landingPlatformIntegrationsDesc: 'Verbinden Sie sich mit den Tools, die Sie bereits verwenden',
    landingAiAssistantTitle: 'KI-Assistent',
    landingStudentProgressTitle: 'Schülerfortschritt',
    landingPresentationsTitle: 'Präsentationen',
    
    // Google Classroom Section (German)
    landingConnectPlatforms: 'Verbinden Sie Ihre Lieblingsplattformen',
    landingConnectPlatformsDesc: 'Integrieren Sie nahtlos die Tools, die Ihre Schule bereits verwendet',
    landingIntegrated: 'INTEGRIERT',
    landingImportClasses: 'Importieren Sie Klassen und Listen automatisch',
    landingSyncGrades: 'Synchronisieren Sie Noten mit Google Classroom',
    landingSecurePrivate: 'Sicher & privat - OAuth 2.0 geschützt',
    landingConnectGoogleClassroom: 'Google Classroom Verbinden',
    landingComingSoon: 'Demnächst',
    landingComingSoonBadge: 'Demnächst',
    landingMoreIntegrations: 'Weitere Integrationen sind auf dem Weg. Sagen Sie uns, was Sie brauchen!',
    landingTrustedBySchools: 'Von Schulen weltweit vertraut, die Google Classroom verwenden',
    
    // Homeschool Section (German)
    landingForHomeschool: 'FÜR HEIMUNTERRICHT',
    landingHomeschoolFriendly: 'Heimschul-freundlich',
    landingLearnMoreHomeschool: 'Mehr über Heimschul-Funktionen erfahren',
    landingHomeschoolAdaptiveDesc: 'KI-gestützte Pfade, die sich dem einzigartigen Tempo Ihres Kindes anpassen',
    landingFlexibleCurriculum: 'Flexibler Lehrplan',
    landingFlexibleCurriculumDesc: 'Passen Sie Fächer und Tempo an Lernstile an',
    landingTextToSpeechDesc: 'Integrierte Sprachausgabe für auditive Lerner',
    landingSafeContent: 'Sichere Inhalte',
    landingSafeContentDesc: 'Altersgerechte, pädagogisch geprüfte Materialien',
    // Pricing Page (German)
    pricingTitle: 'Wählen Sie Ihren Plan',
    pricingSubtitle: 'Beginnen Sie mit 7 Tagen kostenlos. Jederzeit kündbar.',
    pricingBack: 'Zurück',
    pricingYourDigitalClassroom: 'Ihr digitales Klassenzimmer',
    pricingAllRightsReserved: 'Alle Rechte vorbehalten.',
    pricingIndividualMonthly: 'Einzeln Monatlich',
    pricingIndividualMonthlyDesc: 'Voller Zugang für einzelne Lehrer',
    pricingIndividualYearly: 'Einzeln Jährlich',
    pricingIndividualYearlyDesc: 'Bester Wert für einzelne Lehrer',
    pricingSchoolPlanName: 'Schulplan',
    pricingSchoolPlanDesc: 'Perfekt für kleine und mittlere Schulen',
    pricingDistrictPlanName: 'Bezirksplan',
    pricingDistrictPlanDesc: 'Für große Schulbezirke',
    pricingSave: 'Sparen Sie',
    pricingMostPopular: 'AM BELIEBTESTEN',
    pricingMonth: 'Monat',
    pricingYear: 'Jahr',
    pricingPerTeacher: 'pro Lehrer',
    pricingBilledAnnually: 'Jährlich abgerechnet',
    pricingMinimum: 'Minimum',
    pricingMinTeachers: 'Minimum 10 Lehrer für Schulplan, 100 für Bezirk',
    pricingSignUpFree: 'Kostenlos Registrieren',
    pricingFeatureLessonPlanner: 'Unterrichtsplaner',
    pricingFeatureAttendanceTracker: 'Anwesenheitsverfolgung',
    pricingFeatureGradebook: 'Notenbuch',
    pricingFeaturePdfExport: 'PDF-Export',
    pricingFeatureTemplates: 'Vorlagen',
    pricingFeatureEmailSupport: 'E-Mail-Support',
    pricingFeatureEverythingMonthly: 'Alles aus Monatlich',
    pricingFeaturePrioritySupport: 'Prioritäts-Support',
    pricingFeature2MonthsFree: '2 Monate gratis',
    pricingFeatureEarlyAccess: 'Früher Zugang',
    pricingFeatureAllIndividual: 'Alle Einzelfunktionen',
    pricingFeatureAdminDashboard: 'Admin-Dashboard',
    pricingFeatureSchoolBranding: 'Schullogo',
    pricingFeatureBulkImport: 'Massenimport',
    pricingFeatureSchoolReports: 'Schulberichte',
    pricingFeatureUserManagement: 'Benutzerverwaltung',
    pricingFeatureAllSchool: 'Alle Schulfunktionen',
    pricingFeatureDistrictAnalytics: 'Bezirksanalysen',
    pricingFeatureSsoIntegration: 'SSO-Integration',
    pricingFeatureDedicatedSupport: 'Dedizierter Support',
    pricingFeatureCustomTraining: 'Individuelles Training',
    pricingFeatureApiAccess: 'API-Zugang',
    
    // Footer & Navigation (Marketing Pages)
    navFeatures: 'Funktionen',
    navPricing: 'Preise',
    navTrust: 'Vertrauen',
    navContact: 'Kontakt',
    navLogin: 'Anmelden',
    navStartFree: 'Kostenlos Starten',
    footerProduct: 'Produkt',
    footerResources: 'Ressourcen',
    footerTrust: 'Vertrauen',
    footerLegal: 'Rechtliches',
    footerFeatures: 'Funktionen',
    footerPricing: 'Preise',
    footerIntegrations: 'Integrationen',
    footerHelpCenter: 'Hilfecenter',
    footerContact: 'Kontakt',
    footerTrustCenter: 'Vertrauenszentrum',
    footerPrivacy: 'Datenschutz',
    footerTerms: 'Nutzungsbedingungen',
    footerCookies: 'Cookies',
    footerAccessibility: 'Barrierefreiheit',
    footerTagline: 'Der intelligentere Arbeitsplatz für Lehrer.',
    footerSupportNotice: 'Brauchen Sie Hilfe? Unsere Support-Ressourcen helfen Ihnen beim Einstieg.',
    footerAllRightsReserved: 'Alle Rechte vorbehalten.',
    startFreeTrial: 'Kostenlose Testversion Starten',
    viewPricing: 'Preise Ansehen',
    featuresHeroTitle: 'Leistungsstarke Tools für Lehrer entwickelt',
    featuresHeroSubtitle: 'TeacherHubPro vereint die wesentlichen Tools, die Pädagogen brauchen, um Unterricht effizienter zu planen, zu organisieren, zu erstellen und zu verwalten.',
    featuresCTATitle: 'Sehen Sie, wie TeacherHubPro in Ihren Unterrichtsalltag passt',
    featuresCTASubtitle: 'Erkunden Sie die Plattform und entdecken Sie eine bessere Art zu planen, organisieren und unterrichten.',
    contactTitle: 'Kontakt',
    contactSendMessage: 'Senden Sie uns eine Nachricht',
    contactName: 'Name',
    contactEmail: 'E-Mail',
    contactSubject: 'Betreff',
    contactMessage: 'Nachricht',
    contactEmailSupport: 'E-Mail-Support',
    contactHelpCenter: 'Hilfecenter',
    landingCTATitle: 'Vereinfachen Sie noch heute Ihren Unterrichtsablauf',
    landingCTASubtitle: 'Schließen Sie sich Pädagogen an, die TeacherHubPro nutzen, um Zeit zu sparen, organisiert zu bleiben und intelligenter mit KI zu arbeiten.',
    
    // Hero Section
    heroTitle: 'Der Intelligentere Arbeitsplatz für Lehrer',
    heroSubtitle: 'Planen Sie Unterricht, verwalten Sie Noten, verfolgen Sie Anwesenheit, erstellen Sie Unterrichtsmaterialien und sparen Sie jede Woche Stunden mit einer KI-Plattform für Lehrer.',
    seeHowItWorks: 'So Funktioniert Es',
    heroSupportingText: 'Keine zusätzlichen KI-Gebühren. Für Lehrer, Tutoren, Homeschool-Erzieher und Schulen entwickelt.',
    dashboardLessons: 'Lektionen',
    dashboardStudents: 'Schüler',
    dashboardAvgGrade: 'Durchschnitt',
    hoursSavedPerWeek: 'Stunden gespart/Woche',
    
    // Trust Bar
    trustBuiltForEducators: 'Für moderne Pädagogen entwickelt',
    trustAIIncluded: 'KI in jedem Plan enthalten',
    trustSecureEasy: 'Sicher und einfach zu bedienen',
    trustReduceWorkload: 'Entwickelt zur Reduzierung der Verwaltungsarbeit',
    
    // Benefits Grid
    benefitSectionTitle: 'Alles was Lehrer brauchen, an einem Ort',
    benefitSectionSubtitle: 'Hören Sie auf, zwischen Dokumenten, Tabellen und unverbundenen Tools zu wechseln. TeacherHubPro bringt Ihren Kernarbeitsablauf in eine einfache Plattform.',
    benefitLessonPlanningTitle: 'Unterrichtsplanung',
    benefitLessonPlanningDesc: 'Erstellen Sie organisierte, wiederverwendbare Unterrichtspläne in weniger Zeit mit integrierten Vorlagen und KI-Unterstützung.',
    benefitGradebookTitle: 'Notenbuch',
    benefitGradebookDesc: 'Verfolgen Sie den Fortschritt der Schüler mit einem übersichtlichen, einfachen Notenbuch für den echten Unterrichtseinsatz.',
    benefitAttendanceTitle: 'Anwesenheitsverfolgung',
    benefitAttendanceDesc: 'Verwalten Sie die Anwesenheit schnell und halten Sie Klassenaufzeichnungen ohne zusätzlichen Papierkram organisiert.',
    benefitAIToolsTitle: 'KI-Lehrwerkzeuge',
    benefitAIToolsDesc: 'Generieren Sie Unterrichtsideen, Aktivitäten, Schreibübungen, Unterrichtsmaterialien und mehr in Sekunden.',
    benefitPresentationsTitle: 'Präsentationen und Ressourcen',
    benefitPresentationsDesc: 'Erstellen Sie Unterrichtsmaterialien schneller und halten Sie alles in einem Arbeitsablauf verbunden.',
    benefitWorkflowTitle: 'Lehrerfreundlicher Arbeitsablauf',
    benefitWorkflowDesc: 'Für Pädagogen entwickelt, die praktische Werkzeuge brauchen, keine komplexe Software.',
    
    // Outcomes Section
    outcomeSectionTitle: 'Weniger Zeit für Verwaltung, mehr Zeit zum Unterrichten',
    outcomePlanFaster: 'Schneller planen',
    outcomePlanFasterDesc: 'Erstellen Sie Unterrichtspläne, Klassenaktivitäten und Materialien ohne jedes Mal von vorne anzufangen.',
    outcomeStayOrganized: 'Organisiert bleiben',
    outcomeStayOrganizedDesc: 'Halten Sie Noten, Anwesenheit und Lehrressourcen an einem Ort statt über mehrere Tools verstreut.',
    outcomeTeachConfidence: 'Mit Vertrauen unterrichten',
    outcomeTeachConfidenceDesc: 'Nutzen Sie KI und optimierte Arbeitsabläufe, um Stress zu reduzieren und Ihre Unterrichtsarbeit am Laufen zu halten.',
    
    // Social Proof Section
    socialProofSectionTitle: 'Entwickelt zur Unterstützung echter Unterrichtsarbeit',
    socialProofEducators: 'Speziell für Pädagogen entwickelt',
    socialProofSupport: 'Enthält Support-Ressourcen und geführte Hilfe',
    socialProofWorkflows: 'Entwickelt zur Vereinfachung gängiger Lehrerarbeitsabläufe',
    socialProofSuitable: 'Geeignet für Einzellehrer, Homeschool-Erzieher und wachsenden Schuleinsatz',
    socialProofHelpCenter: 'Support-Ressourcen verfügbar • Hilfecenter enthalten',
    
    // Use Cases Section
    useCaseSectionTitle: 'Für verschiedene Lehrumgebungen gemacht',
    useCaseClassroomTitle: 'Klassenlehrer',
    useCaseClassroomDesc: 'Verwalten Sie Planung, Benotung und tägliche Klassenorganisation an einem Ort.',
    useCaseHomeschoolTitle: 'Homeschool-Erzieher',
    useCaseHomeschoolDesc: 'Erstellen Sie flexible Unterrichtsabläufe und halten Sie Lernpläne organisiert.',
    useCaseTutorsTitle: 'Tutoren und kleine Programme',
    useCaseTutorsDesc: 'Verfolgen Sie Schülerarbeit, planen Sie Sitzungen und erstellen Sie Ressourcen effizient.',
    useCaseSchoolsTitle: 'Schulen und Teams',
    useCaseSchoolsDesc: 'Unterstützen Sie Pädagogen mit einer Plattform, die Konsistenz verbessert und Zeit spart.',
    
    // Feature Detail Pages - Common
    backToFeatures: 'Zurück zu Funktionen',
    keyBenefits: 'Hauptvorteile',
    howItWorks: 'So Funktioniert Es',
    readyToGetStarted: 'Bereit loszulegen?',
    featureCTASubtitle: 'Schließen Sie sich Tausenden von Pädagogen an, die bereits Zeit sparen mit TeacherHubPro.',
    exploreOtherFeatures: 'Andere Funktionen Entdecken',
    learnMore: 'Mehr erfahren',
    featureLessonPlanningTitle: 'Unterrichtsplanung',
    featureLessonPlanningSubtitle: 'Erstellen Sie organisierte Pläne schneller mit Vorlagen und KI.',
    featureLessonPlanningDescription: 'Das Planungstool hilft Ihnen, umfassende Pläne schnell zu erstellen.',
    featureLessonPlanningBenefit1: 'Erstellen Sie Pläne bis zu 5x schneller',
    featureLessonPlanningBenefit2: 'Automatische Standards-Ausrichtung',
    featureLessonPlanningBenefit3: 'Pläne für verschiedene Klassen wiederverwenden',
    featureLessonPlanningBenefit4: 'Mit Kollegen in Echtzeit teilen',
    featureLessonPlanningHowItWorks1: 'Wählen Sie eine Vorlage oder starten Sie neu',
    featureLessonPlanningHowItWorks2: 'Nutzen Sie KI-Vorschläge',
    featureLessonPlanningHowItWorks3: 'Speichern und wiederverwenden',
    featureGradebookTitle: 'Notenbuch',
    featureGradebookSubtitle: 'Verfolgen Sie Schülerfortschritte einfach.',
    featureGradebookDescription: 'Unser Notenbuch ist intuitiv und leistungsstark.',
    featureGradebookBenefit1: 'Automatische Notenberechnung',
    featureGradebookBenefit2: 'Visuelles Fortschrittstracking',
    featureGradebookBenefit3: 'Zeugnisse sofort generieren',
    featureGradebookBenefit4: 'KI-Einblicke für Schwierigkeiten',
    featureGradebookHowItWorks1: 'Klassen einrichten',
    featureGradebookHowItWorks2: 'Aufgaben erstellen und Noten eingeben',
    featureGradebookHowItWorks3: 'Analysen und Berichte anzeigen',
    featureAttendanceTitle: 'Anwesenheitsverfolgung',
    featureAttendanceSubtitle: 'Anwesenheit schnell ohne Papierkram verwalten.',
    featureAttendanceDescription: 'Verabschieden Sie sich von Papierlisten.',
    featureAttendanceBenefit1: 'Anwesenheit in unter 30 Sekunden',
    featureAttendanceBenefit2: 'Automatische Fehlzeitenerkennung',
    featureAttendanceBenefit3: 'Berichte für die Verwaltung',
    featureAttendanceBenefit4: 'Optionale Elternbenachrichtigungen',
    featureAttendanceHowItWorks1: 'Klasse öffnen und markieren',
    featureAttendanceHowItWorks2: 'Anwesenheitsmuster überprüfen',
    featureAttendanceHowItWorks3: 'Exportieren oder teilen',
    featureAIToolsTitle: 'KI-Lehrwerkzeuge',
    featureAIToolsSubtitle: 'Generieren Sie Ideen und Materialien in Sekunden.',
    featureAIToolsDescription: 'Nutzen Sie die Kraft der KI für Ihren Unterricht.',
    featureAIToolsBenefit1: 'Generieren Sie angepasste Ideen',
    featureAIToolsBenefit2: 'Differenzierte Materialien erstellen',
    featureAIToolsBenefit3: 'Bewertungen automatisch erstellen',
    featureAIToolsBenefit4: 'Keine zusätzlichen KI-Gebühren',
    featureAIToolsHowItWorks1: 'Beschreiben Sie Ihren Bedarf',
    featureAIToolsHowItWorks2: 'KI generiert Optionen',
    featureAIToolsHowItWorks3: 'Bearbeiten und speichern',
    featurePresentationsTitle: 'Präsentationen & Ressourcen',
    featurePresentationsSubtitle: 'Materialien schneller erstellen.',
    featurePresentationsDescription: 'Erstellen Sie alles an einem Ort.',
    featurePresentationsBenefit1: 'Bildungsvorlagen',
    featurePresentationsBenefit2: 'Integration mit Unterrichtsplänen',
    featurePresentationsBenefit3: 'Einfaches Teilen',
    featurePresentationsBenefit4: 'Vorhandene Ressourcen importieren',
    featurePresentationsHowItWorks1: 'Vorlage wählen',
    featurePresentationsHowItWorks2: 'Inhalte hinzufügen',
    featurePresentationsHowItWorks3: 'Verknüpfen und teilen',
    featureWorkflowTitle: 'Lehrerfreundlicher Workflow',
    featureWorkflowSubtitle: 'Für Pädagogen, die praktische Werkzeuge brauchen.',
    featureWorkflowDescription: 'Entwickelt für echte Lehrerarbeit.',
    featureWorkflowBenefit1: 'Intuitive Oberfläche',
    featureWorkflowBenefit2: 'Desktop und Mobile',
    featureWorkflowBenefit3: 'Schnelle Einrichtung',
    featureWorkflowBenefit4: 'Integration mit Ihren Tools',
    featureWorkflowHowItWorks1: 'Anmelden und einrichten',
    featureWorkflowHowItWorks2: 'Alles von einem Dashboard',
    featureWorkflowHowItWorks3: 'Automatische Synchronisation',
    featureClassroomTeachersTitle: 'Für Klassenlehrer',
    featureClassroomTeachersSubtitle: 'Planung und Noten an einem Ort.',
    featureClassroomTeachersDescription: 'TeacherHubPro vereint alle Werkzeuge.',
    featureClassroomTeachersBenefit1: 'Für mehrere Klassen planen',
    featureClassroomTeachersBenefit2: 'Noten und Anwesenheit verfolgen',
    featureClassroomTeachersBenefit3: 'Elternberichte generieren',
    featureClassroomTeachersBenefit4: 'Von überall zugreifen',
    featureClassroomTeachersHowItWorks1: 'Klassen einrichten',
    featureClassroomTeachersHowItWorks2: 'Täglich planen und erfassen',
    featureClassroomTeachersHowItWorks3: 'Über Portal kommunizieren',
    featureHomeschoolTitle: 'Für Homeschool',
    featureHomeschoolSubtitle: 'Flexible Lernpfade erstellen.',
    featureHomeschoolDescription: 'TeacherHubPro hilft bei personalisierten Pfaden.',
    featureHomeschoolBenefit1: 'Personalisierte Pfade pro Kind',
    featureHomeschoolBenefit2: 'Organisierte Aufzeichnungen',
    featureHomeschoolBenefit3: 'KI-generierte Ideen',
    featureHomeschoolBenefit4: 'Flexible Zeitpläne',
    featureHomeschoolHowItWorks1: 'Profile pro Kind erstellen',
    featureHomeschoolHowItWorks2: 'Fortschritt verfolgen',
    featureHomeschoolHowItWorks3: 'Berichte generieren',
    featureTutorsTitle: 'Für Tutoren',
    featureTutorsSubtitle: 'Schülerarbeit effizient verfolgen.',
    featureTutorsDescription: 'TeacherHubPro hält Sie organisiert.',
    featureTutorsBenefit1: 'Mehrere Schüler verfolgen',
    featureTutorsBenefit2: 'Sitzungen planen',
    featureTutorsBenefit3: 'Fortschrittsberichte für Eltern',
    featureTutorsBenefit4: 'Personalisierte Materialien',
    featureTutorsHowItWorks1: 'Schüler hinzufügen',
    featureTutorsHowItWorks2: 'Planen und verfolgen',
    featureTutorsHowItWorks3: 'Über Portal teilen',
    featureSchoolsTitle: 'Für Schulen',
    featureSchoolsSubtitle: 'Konsistente Plattform für Pädagogen.',
    featureSchoolsDescription: 'TeacherHubPro skaliert mit Ihrer Schule.',
    featureSchoolsBenefit1: 'Konsistente Werkzeuge',
    featureSchoolsBenefit2: 'Geteilte Ressourcenbibliothek',
    featureSchoolsBenefit3: 'Admin-Dashboard',
    featureSchoolsBenefit4: 'Volumenpreise',
    featureSchoolsHowItWorks1: 'Kontaktieren Sie uns für Preise',
    featureSchoolsHowItWorks2: 'Lehrer einbinden',
    featureSchoolsHowItWorks3: 'Überwachen und unterstützen'
  },
  it: {
    absent: "Assente",
    activities: "Attività",
    art: "Arte",
    assignments: "Compiti",
    attendanceReport: "Rapporto presenze",
    book: "Libro",
    brainstorming: "Brainstorming",
    buildingBackground: "Costruzione del contesto",
    category: "Categoria",
    comprehensionQuestions: "Domande di comprensione",
    createAssignment: "Crea compito",
    createPlan: "Crea piano",
    dayTheme: "Tema del giorno",
    description: "Descrizione",
    dictionary: "Dizionario",
    dueDate: "Data di consegna",
    duplicateLastWeek: "Duplica settimana scorsa",
    excused: "Giustificato",
    exercisePractice: "Esercitazione",
    expectations: "Aspettative",
    exportPdf: "Esporta PDF",
    firstWeek: "Prima settimana",
    foundationalSkills: "Competenze di base",
    gradeEntry: "Inserimento voti",
    guidedReading: "Lettura guidata e corale",
    handouts: "Dispense",
    health: "Salute",
    homework: "Compiti a casa",
    language: "Lingua",
    late: "In ritardo",
    level1Memory: "Livello 1: Memoria",
    level2Processing: "Livello 2: Elaborazione",
    level3Strategic: "Livello 3: Pensiero strategico",
    level4Extended: "Livello 4: Pensiero esteso",
    listeningAndSpeaking: "Ascolto/Parlato",
    markAllPresent: "Segna tutti presenti",
    materials: "Materiali",
    mathematics: "Matematica",
    missing: "Mancante",
    notebook: "Quaderno",
    objectiveOfWeek: "Obiettivo della settimana",
    oralQuestions: "Domande orali",
    other: "Altro",
    physicalEducation: "Educazione fisica",
    points: "Punti",
    present: "Presente",
    pricingActiveSubscription: "Abbonamento attivo",
    pricingAdminAccount: "Account amministratore - Accesso completo",
    pricingContactUs: "Contattaci",
    pricingCurrentPlan: "Piano attuale",
    pricingDaysRemaining: "giorni rimanenti",
    pricingDistrictDesc: "Per distretti scolastici con amministrazione avanzata",
    pricingDistrictPlan: "Distretto",
    pricingFeatureAIAssistant: "Assistente IA (Claude)",
    pricingFeatureAdvancedAnalytics: "Analisi avanzate",
    pricingFeatureAttendance: "Monitoraggio presenze",
    pricingFeatureBasicLessonPlanning: "Pianificazione lezioni di base",
    pricingFeatureCustomTemplates: "Modelli personalizzati",
    pricingFeatureEverythingPro: "Tutto di Pro",
    pricingFeatureEverythingSchool: "Tutto di School",
    pricingFeatureEverythingStarter: "Tutto di Starter",
    pricingFeatureMultiSchool: "Gestione multi-scuola",
    pricingFeatureSchoolDashboard: "Dashboard della scuola",
    pricingFeatureSharedResources: "Risorse condivise",
    pricingFeatureTeacherManagement: "Gestione insegnanti",
    pricingFeatureUnlimitedClasses: "Classi illimitate",
    pricingFeatureUnlimitedStudents: "Studenti illimitati",
    pricingForSchoolPlans: "per i piani scolastici",
    pricingFreeTrial: "Prova gratuita",
    pricingGetStarted: "Inizia",
    pricingNumberOfTeachers: "Numero di insegnanti",
    pricingProDesc: "Funzionalità avanzate per utenti esperti",
    pricingProPlan: "Pro",
    pricingSchoolDesc: "Per intere scuole con funzionalità di gruppo",
    pricingSchoolDistrict: "Per scuole e distretti",
    pricingSchoolPlan: "Scuola",
    pricingStarterDesc: "Perfetto per insegnanti individuali",
    pricingStarterPlan: "Starter",
    pricingSubscribe: "Abbonati",
    pricingTeachers: "insegnanti",
    pricingTrialExpired: "La tua prova gratuita è scaduta. Seleziona un piano per continuare a usare TeacherHubPro.",
    project: "Progetto",
    quiz: "Quiz",
    readPages: "Pagine da leggere",
    reading: "Lettura",
    religion: "Religione",
    saveAsTemplate: "Salva come modello",
    science: "Scienze",
    secondWeek: "Seconda settimana",
    skillsOfWeek: "Competenze della settimana",
    socialStudies: "Studi sociali",
    spanish: "Spagnolo",
    standards: "Standard",
    story: "Storia/Titolo",
    submitAttendance: "Invia presenze",
    takeAttendance: "Fai l'appello",
    tardy: "In ritardo",
    teachersGuide: "Guida dell'insegnante",
    test: "Test",
    testQuiz: "Test/Quiz",
    title: "Titolo",
    unit: "Unità",
    vocabularyDevelopment: "Sviluppo del vocabolario",
    weekOf: "Settimana del",
    weeklyPlan: "Piano settimanale",
    writing: "Scrittura",

    // Navigation
    dashboard: 'Pannello',
    planner: 'Pianificatore',
    attendance: 'Presenze',
    gradebook: 'Registro Voti',
    classes: 'Classi',
    settings: 'Impostazioni',
    logout: 'Esci',
    
    // Auth
    login: 'Accedi',
    register: 'Registrati',
    email: 'Email',
    password: 'Password',
    name: 'Nome',
    signInWithGoogle: 'Accedi con Google',
    orContinueWith: 'o continua con',
    dontHaveAccount: 'Non hai un account?',
    alreadyHaveAccount: 'Hai già un account?',
    
    // Dashboard
    welcome: 'Benvenuto',
    todayOverview: 'Panoramica di Oggi',
    quickActions: 'Azioni Rapide',
    attendancePending: 'Presenze in Sospeso',
    upcomingAssignments: 'Compiti in Arrivo',
    recentPlans: 'Piani Recenti',
    totalClasses: 'Totale Classi',
    totalStudents: 'Totale Studenti',
    totalPlans: 'Totale Piani',
    
    // Classes
    addClass: 'Aggiungi Classe',
    className: 'Nome Classe',
    grade: 'Anno',
    section: 'Sezione',
    subject: 'Materia',
    yearTerm: 'Anno/Periodo',
    students: 'Studenti',
    addStudent: 'Aggiungi Studente',
    firstName: 'Nome',
    lastName: 'Cognome',
    studentNumber: 'Numero Studente',
    parentEmail: 'Email Genitore',
    notes: 'Note',
    accommodations: 'Adattamenti',
    
    // Common
    save: 'Salva',
    cancel: 'Annulla',
    delete: 'Elimina',
    edit: 'Modifica',
    view: 'Visualizza',
    search: 'Cerca',
    filter: 'Filtra',
    loading: 'Caricamento...',
    noData: 'Nessun dato disponibile',
    confirmDelete: 'Sei sicuro di voler eliminare?',
    success: 'Successo',
    error: 'Errore',
    from: 'Da',
    to: 'A',
    date: 'Data',
    teacher: 'Insegnante',
    selectLanguage: 'Seleziona Lingua',
    
    // Days
    monday: 'Lunedì',
    tuesday: 'Martedì',
    wednesday: 'Mercoledì',
    thursday: 'Giovedì',
    friday: 'Venerdì',
    
    // Games
    educationalGames: 'Giochi Educativi',
    myGames: 'I Miei Giochi',
    createGame: 'Crea Gioco',
    play: 'Gioca',
    copyLink: 'Copia Link',
    shareToClassroom: 'Condividi su Classroom',
    deleteGame: 'Elimina Gioco',
    questions: 'Domande',
    gameType: 'Tipo di Gioco',
    gradeLevel: 'Livello Scolastico',
    
    // Landing Page
    landingHeroTitle: 'La tua classe completa in una piattaforma',
    landingHeroSubtitle: 'Pianificazione, presenze, voti e assistente IA. Tutto ciò di cui un insegnante ha bisogno.',
    landingBuiltForTeachers: 'Progettato per insegnanti',
    landingStartFree: 'Inizia gratis',
    landingViewPricing: 'Vedi prezzi',
    landingUsedBySchools: 'Usato da scuole in tutto il mondo',
    landingEverythingYouNeed: 'Tutto ciò di cui hai bisogno',
    landingEverythingDesc: 'Una piattaforma completa progettata appositamente per gli insegnanti.',
    landingFeatures: 'Funzionalità',
    landingAiAssistant: 'Assistente IA',
    landingPoweredBy: 'Sviluppato da Anthropic',
    landingActiveTeachers: 'insegnanti attivi',
    landingHomeschool: 'Homeschooling',
    landingIntegrations: 'Integrazioni',
    landingCustomization: 'Personalizzazione',
    landingPricing: 'Prezzi',
    landingGetStarted: 'Inizia - Accedi',
    landingStart: 'Inizia',
    landingAiLessonPlan: 'Piano IA',
    landingPresentations: 'Presentazioni',
    landingGrades: 'Voti',
    landingPlanFaster: 'Pianifica più veloce',
    landingPlanFasterDesc: 'Crea piani di lezione settimanali in pochi secondi.',
    landingWeek1: 'Settimana 1',
    landingYourBrand: 'Il tuo marchio, il tuo stile',
    landingYourBrandDesc: 'Personalizza con il logo e i colori della tua scuola.',
    landingYourSchool: 'La Tua Scuola',
    landingCustomBranding: 'Marchio personalizzato',
    landingExportPro: 'Esporta professionalmente',
    landingExportProDesc: 'Genera PDF pronti per la stampa: piani, pagelle, dossier.',
    landingDownloadPdf: 'Scarica PDF',
    landingAiAssistantTitle: 'Assistente IA',
    landingAiAssistantDesc: 'Genera piani di lezione, attività e domande con l\'IA.',
    landingLessonPlans: 'Piani di lezione',
    landingQuizzesTests: 'Quiz e test',
    landingDigitalClassroom: 'La tua classe digitale',
    landingTryFree: 'Prova gratuita di 30 giorni',
    landingNoCard: 'Nessuna carta di credito richiesta',
    landingFullName: 'Nome Completo',
    landingCreateAccount: 'Crea Account Gratuito',
    landingOrLoginWith: 'O accedi con',
    landingHaveAccount: 'Hai un account?',
    landingLoginHere: 'Accedi qui',
    landingNeedAccount: 'Hai bisogno di un account?',
    landingRegisterHere: 'Registrati qui',
    // AI Section
    landingAiTeachingAssistant: 'Assistente IA per l\'Insegnamento',
    landingYouAsked: 'Hai chiesto:',
    landingAiResponse: 'Risposta IA:',
    landingAiQuestion: 'Come posso insegnare le frazioni equivalenti alla 4a classe?',
    landingAiAnswer: 'Ecco un piano di lezione basato sugli standard con attività pratiche...',
    landingQuizzes: 'Quiz',
    landingWorksheets: 'Schede di Lavoro',
    landingIdeas: 'Idee',
    landingNewAi: 'Nuovo: Intelligenza Artificiale',
    landingAiAssistantToTeach: 'Il tuo assistente IA per insegnare meglio',
    landingAiAssistantDesc2: 'Genera piani di lezione, quiz, riassunti e attività basate sugli standard, istantaneamente.',
    landingAlignedStandards: 'Allineato agli standard educativi',
    landingGenerateSeconds: 'Genera contenuti in secondi',
    landingBilingual: 'Multilingue',
    landingPoweredBy: 'Alimentato da Claude Sonnet 4.5',
    landingTryItFree: 'Prova gratis',
    landingIncludedSubscription: 'Incluso nell\'abbonamento',
    // Integrations Section
    landingConnectTools: 'Connetti i tuoi strumenti preferiti',
    landingConnectToolsDesc: 'Integra le piattaforme che già usi.',
    landingCalendarSync: 'Sincronizzazione Calendario',
    landingCalendarSyncDesc: 'Esporta i tuoi piani su Google Calendar, Outlook o Apple Calendar.',
    landingGoogleClassroom: 'Google Classroom',
    landingGoogleClassroomDesc: 'Condividi attività e giochi direttamente con i tuoi studenti.',
    landingPdfExport: 'Esporta PDF',
    landingPdfExportDesc: 'Genera PDF professionali per piani, pagelle e altro.',
    landingEmailNotifications: 'Notifiche Email',
    landingEmailNotificationsDesc: 'Invia rapporti automatici a genitori e amministratori.',
    landingViewIntegrations: 'Vedi tutte le integrazioni',
    // Customization Section
    landingYourBrandYourWay: 'Il tuo marchio, a modo tuo',
    landingCustomizeDesc: 'Personalizza tutto con colori, logo e preferenze della tua scuola.',
    landingSchoolLogo: 'Logo della Scuola',
    landingSchoolLogoDesc: 'Aggiungi il tuo logo a tutti i documenti e rapporti.',
    landingColorThemes: 'Temi di Colore',
    landingColorThemesDesc: 'Scegli colori che corrispondono all\'identità della tua scuola.',
    landingCustomTemplates: 'Modelli Personalizzati',
    landingCustomTemplatesDesc: 'Crea modelli riutilizzabili per i tuoi piani.',
    landingMultiLanguage: 'Multi-Lingua',
    landingMultiLanguageDesc: 'Lavora in più lingue.',
    // Homeschool Section
    landingHomeschoolTitle: 'Homeschooling',
    landingHomeschoolSubtitle: 'Il tuo compagno completo per l\'homeschooling',
    landingHomeschoolDesc: 'Tutto ciò di cui hai bisogno per un\'esperienza di homeschooling di successo.',
    landingAdaptiveLearning: 'Apprendimento Adattivo',
    landingAdaptiveLearningDesc: 'Percorsi di apprendimento che si adattano al ritmo di ogni bambino.',
    landingFullVisibility: 'Visibilità Totale',
    landingFullVisibilityDesc: 'Monitora i progressi in tempo reale con rapporti dettagliati.',
    landingFlexibleCurriculum: 'Curriculum Flessibile',
    landingFlexibleCurriculumDesc: 'Personalizza il curriculum in base alle esigenze della tua famiglia.',
    landing100Customizable: '100% Personalizzabile',
    landingDownloadLearn: 'Scarica e Impara',
    landingDownloadLearnDesc: 'Esporta materiali per l\'apprendimento offline.',
    landingPdfExportFeature: 'Esporta PDF',
    landingTextToSpeech: 'Testo in Voce',
    landingTextToSpeechDesc: 'Supporto audio per tutti i contenuti di lettura.',
    landingAudioLearning: 'Apprendimento Audio',
    landingSafeContent: 'Contenuto Sicuro',
    landingSafeContentDesc: 'Materiali appropriati all\'età e verificati.',
    landingParentalControl: 'Controllo Genitoriale',
    landingSubjectModules: 'Moduli per Materia',
    landingMathematics: 'Matematica',
    landingLanguageArts: 'Italiano',
    landingScience: 'Scienze',
    landingAstronomy: 'Astronomia',
    landingGeography: 'Geografia',
    landingHistory: 'Storia',
    landingArt: 'Arte',
    landingPhysicalEd: 'Educazione Fisica',
    landingStartHomeschooling: 'Inizia l\'Homeschooling',
    // CTA Section
    landing7DaysFree: '7 giorni gratis',
    landingGetStartedToday: 'Inizia oggi',
    landingStartYourJourney: 'Inizia il tuo percorso di classe digitale. Nessuna carta di credito richiesta.',
    landingNoCreditCard: 'Nessuna carta di credito richiesta',
    landingSetup2Min: 'Configurazione in 2 minuti',
    landingCancelAnytime: 'Annulla in qualsiasi momento',
    landingSupportBilingual: 'Supporto multilingue',
    landingActiveTeachers: 'insegnanti attivi',
    landingSignIn: 'Accedi',
    landingCreateAccountTab: 'Crea account',
    landingContinueGoogle: 'Continua con Google',
    landingOrWithEmail: 'o con email',
    landingEmail: 'Email',
    landingPassword: 'Password',
    landingFullNameLabel: 'Nome completo',
    landingCreateFreeAccount: 'Crea account gratuito',
    // Footer
    landingContact: 'Contatto',
    landingPrivacyPolicy: 'Informativa sulla Privacy',
    landingHelp: 'Aiuto',
    landingTermsOfUse: 'Termini di Utilizzo',
    landingCookiesPolicy: 'Informativa sui Cookie',
    landingAccessibility: 'Accessibilità',
    landingAllRightsReserved: 'Tutti i diritti riservati.',
    
    // AI Features Section
    landingAdvancedAiFeatures: 'Funzionalità IA Avanzate',
    landingIntelligentPlanning: 'Pianificazione intelligente con IA',
    landingAiPlanningDesc: 'Dalla generazione di un piano completo ai suggerimenti giornalieri, l\'IA ti aiuta in ogni fase.',
    landingGenerateFullWeek: 'Genera Settimana Completa',
    landingGenerateFullWeekDesc: 'Con un clic, genera attività per tutti i 5 giorni con progressione pedagogica: introduzione → pratica → padronanza → valutazione.',
    landingDays: 'giorni',
    landingProgression: 'Progressione',
    landingDailySuggestions: 'Suggerimenti Giornalieri',
    landingDailySuggestionsDesc: 'Ogni giorno mostra la sua fase pedagogica (Introduzione, Pratica, Padronanza). Ottieni suggerimenti specifici per ogni fase.',
    landingMonFri: 'Lun → Ven',
    landingContextual: 'Contestuale',
    landingReusableTemplates: 'Modelli Riutilizzabili',
    landingReusableTemplatesDesc: 'Salva i tuoi migliori piani come modelli. Clona e personalizza per nuovi argomenti con un clic.',
    landingSave: 'Salva',
    landingClone: 'Clona',
    landing5StarterTemplates: '5 Modelli Iniziali',
    landing5StarterTemplatesDesc: 'Inizia subito con modelli professionali: Frazioni, Comprensione della Lettura, Metodo Scientifico, Scrittura e Moltiplicazione.',
    landingMath: 'Matematica',
    landingELA: 'Italiano',
    landingAiCustomize: 'Personalizza con IA',
    landingAiCustomizeDesc: 'Prendi qualsiasi modello e adattalo a un nuovo argomento. L\'IA mantiene la struttura di successo cambiando il contenuto.',
    landingNewTopic: 'Nuovo argomento',
    landingSameStructure: 'Stessa struttura',
    landingTemplateOfWeek: 'Modello della Settimana',
    landingTemplateOfWeekDesc: 'Ogni settimana mettiamo in evidenza un modello diverso sulla tua dashboard con suggerimenti di personalizzazione specifici.',
    landingWeeklyRotation: 'Rotazione settimanale',
    landingTips: 'Suggerimenti',
    landingTryAiFree7Days: 'Prova l\'IA gratis per 7 giorni',
    
    // AI Presentations Section
    landingNewAiPresentations: 'NUOVO! Presentazioni IA',
    landingCreatePresentations: 'Crea presentazioni che affascinano',
    landingPresentationsDesc: 'Genera presentazioni educative complete con l\'IA. Scegli temi visivi, aggiungi immagini, applica transizioni e salva per dopo.',
    landingAiGenerate: 'Genera con IA',
    landingAiGenerateDesc: '6+ slide in secondi',
    landing8VisualThemes: '8 Temi Visivi',
    landing8VisualThemesDesc: 'Oceano, Galassia, Lavagna...',
    landingSaveAndLoad: 'Salva e Carica',
    landingSaveAndLoadDesc: 'Libreria personale',
    landingDownloadHtml: 'Scarica HTML',
    landingDownloadHtmlDesc: 'Usa offline',
    landingCreateFirstPresentation: 'Crea la mia prima presentazione',
    landingTheSolarSystem: 'Il Sistema Solare',
    landingScienceGrade: 'Scienze - 4a Classe',
    landingTransitionsEnabled: '✨ Transizioni attivate',
    
    // Customization Section
    landingFullCustomization: 'Personalizzazione completa',
    landingSchoolBrandingTitle: 'L\'identità della tua scuola su ogni documento',
    landingSchoolBrandingDesc: 'Dal tuo logo ai tuoi colori, ogni PDF e report riflette il branding della tua istituzione.',
    landingSchoolLogoAll: 'Il logo della tua scuola su tutti i documenti',
    landingCustomColorsMatch: 'Colori personalizzati che corrispondono al tuo marchio',
    landingPdfFormats: 'I formati PDF esatti della tua scuola',
    landingCentralizedAdmin: 'Dashboard amministrativo centralizzato',
    landingTeachersPlanning: 'Pianificazione dell\'Insegnante',
    landingYourLogoHere: 'Il tuo logo qui',
    landingYourColors: 'I tuoi colori',
    
    // Integrations Section
    landingConnectFavorite: 'Connettiti con le tue piattaforme preferite',
    landingConnectFavoriteDesc: 'Sincronizza con gli strumenti che già usi per un flusso di lavoro perfetto.',
    landingAvailable: 'Disponibile',
    landingMostPopular: 'Più popolare',
    landingImportClasses: 'Importa Classi',
    landingImportClassesDesc: 'Porta le tue classi e gli elenchi degli studenti automaticamente',
    landingSyncGrades: 'Sincronizza Voti',
    landingSyncGradesDesc: 'Invia voti a Google Classroom con un clic',
    landingSecurePrivate: 'Sicuro e Privato',
    landingSecurePrivateDesc: 'Autenticazione OAuth sicura, i tuoi dati protetti',
    landingSetupMinutes: 'Configurazione in minuti',
    landingNoExtraCost: 'Nessun costo aggiuntivo',
    landingSupportIncluded: 'Supporto incluso',
    landingComingSoon: 'Prossimamente',
    landingNeedIntegration: 'Hai bisogno di un\'altra integrazione?',
    landingNeedIntegrationDesc: 'Stiamo costantemente aggiungendo nuove integrazioni. Dicci quali piattaforme usi e le daremo priorità.',
    landingRequestIntegration: 'Richiedi Integrazione',
    
    // Homeschool Section
    landingPerfectHomeschool: 'Perfetto per Educatori Homeschool',
    landingHomeschoolCompleteDesc: 'Tutto ciò di cui hai bisogno per creare un\'esperienza di apprendimento personalizzata, dalla pianificazione al monitoraggio dei progressi.',
    landingAiPowered: 'Alimentato dall\'IA',
    landingParentDashboard: 'Dashboard Genitori',
    landingParentDashboardDesc: 'Panoramica completa dei progressi, voti e lavori imminenti di ogni bambino in una dashboard unificata.',
    landingFlexibleCurriculumDesc2: 'Personalizza materie, ritmo e metodi di insegnamento per adattarsi allo stile di apprendimento unico di ogni bambino.',
    landing100Customizable: '100% Personalizzabile',
    landingDownloadAndLearn: 'Scarica e Impara',
    landingDownloadAndLearnDesc: 'Esporta lezioni e materiali come PDF. Continua a imparare anche offline o in movimento.',
    landingAudioLearning: 'Apprendimento Audio',
    landingTextToSpeechDesc2: 'Sintesi vocale integrata per tutti i contenuti. Perfetto per apprendenti uditivi o assistenza alla lettura.',
    landingSafeContentDesc2: 'Tutti i contenuti sono appropriati all\'età e verificati educativamente. Controllo parentale totale su ciò che i bambini possono accedere.',
    
    // New Landing Page Copy (Italian)
    landingSave5Hours: 'Risparmia 5+ Ore Ogni Settimana',
    landingSave5HoursTitle: 'Risparmia 5+ Ore Ogni Settimana su Pianificazione, Valutazione e Amministrazione',
    landingSave5HoursSubtitle: 'TeacherHubPro aiuta gli educatori impegnati a creare piani di lezione, gestire i voti, registrare le presenze e usare l\'IA per semplificare il lavoro quotidiano — tutto in un unico posto.',
    landingBuiltForBusyTeachers: 'Costruito per insegnanti impegnati',
    landingStartFreeNoCreditCard: 'Inizia Gratis — Nessuna Carta di Credito',
    landingSeeHowItWorks: 'Guarda Come Funziona',
    landingTeachingIsMore: 'Insegnare è Più che Insegnare',
    landingTeachingIsMoreDesc: 'Tra pianificazione delle lezioni, valutazione, presenze, report e comunicazione — la maggior parte degli insegnanti trascorre ore ogni settimana in compiti che rubano tempo agli studenti e alla vita personale.',
    landingYouBecameTeacher: 'Sei diventato insegnante per educare.',
    landingNotToDrown: 'Non per affogare nelle scartoffie.',
    landingEverythingSimple: 'Tutto Ciò di Cui Hai Bisogno. Una Piattaforma Semplice.',
    landingLessBusywork: 'Meno lavoro burocratico. Più insegnamento.',
    landingGenerateLessons: 'Genera piani di lezione strutturati in minuti',
    landingTrackGrades: 'Monitora i voti con un registro digitale facile da usare',
    landingRecordAttendance: 'Registra le presenze rapidamente',
    landingOrganizeData: 'Organizza i dati della classe in una dashboard',
    landingUseAiTools: 'Usa strumenti IA per accelerare la preparazione',
    landingWhatTeachersExperience: 'Cosa Sperimentano gli Insegnanti',
    landingFasterPlanning: 'Pianificazione settimanale più veloce',
    landingLessTimeGrading: 'Meno tempo per la valutazione',
    landingBetterOrganization: 'Migliore organizzazione',
    landingMoreConfidence: 'Più sicurezza in classe',
    landingMorePersonalTime: 'Più tempo personale dopo la scuola',
    landing3SimpleSteps: 'Inizia in 3 Semplici Passaggi',
    landingValueInMinutes: 'Vedrai il valore in minuti — non ore.',
    landingCreateClasses: 'Crea le Tue Classi',
    landingCreateClassesDesc: 'Configura le tue classi e aggiungi studenti in secondi',
    landingGenerateWithAi: 'Genera Lezioni con l\'IA',
    landingGenerateWithAiDesc: 'Crea piani di lezione completi con l\'assistenza dell\'IA',
    landingManageGradesAttendance: 'Gestisci Voti e Presenze Senza Sforzo',
    landingManageGradesAttendanceDesc: 'Monitora tutto da una dashboard intuitiva',
    landingBuiltForEducators: 'Costruito Specificamente per gli Educatori',
    landingElementaryTeachers: 'Insegnanti delle Elementari',
    landingMiddleSchoolEducators: 'Educatori delle Medie',
    landingHighSchoolTeachers: 'Insegnanti delle Superiori',
    landingHomeschoolEducators: 'Educatori Homeschool',
    landingInstructionalCoaches: 'Coach Pedagogici',
    landingJoinThousands: 'Unisciti a Migliaia di Insegnanti',
    landingJoinThousandsDesc: 'Educatori in tutto il paese stanno già usando TeacherHubPro per ottimizzare il loro flusso di lavoro, trascorrere più tempo con gli studenti e riconquistare le loro serate.',
    landingReadyToSimplify: 'Pronto a Semplificare la Tua Vita da Insegnante?',
    landingStartFreeToday: 'Inizia Gratis Oggi',
    landingExperienceTimeSaved: 'Inizia il tuo account gratuito oggi e scopri quanto tempo puoi risparmiare questa settimana.',
    landingNoCreditCardRequired: 'Nessuna carta di credito richiesta',
    landingSetupIn2Minutes: 'Configurazione in meno di 2 minuti',
    landingCancelAnytime: 'Annulla quando vuoi',
    landingFullAccess: 'Accesso completo a tutte le funzionalità',
    landingTeachersUsing: 'insegnanti stanno già usando TeacherHubPro',
    landingSimplifyingTeaching: 'Semplificare l\'insegnamento, una classe alla volta.',
    landingLogIn: 'Accedi',
    landingStartFreeCta: 'Inizia Gratis',
    landingBackToHome: 'Torna alla Home',
    landingWelcomeBack: 'Bentornato',
    landingSignInAccess: 'Accedi per accedere alla tua dashboard',
    landingStartFreeTrial: 'Inizia la Tua Prova Gratuita',
    landingJoinThousandsTeachers: 'Unisciti a migliaia di insegnanti che risparmiano tempo ogni settimana',
    landingCreateFreeAccount: 'Crea Account Gratuito',
    landingSignInTab: 'Accedi',
    landingContinueWithGoogle: 'Continua con Google',
    landingOrWithEmail: 'o con email',
    landingFullName: 'Nome Completo',
    landingYourName: 'Il tuo nome',
    landingYourEmail: 'tu@scuola.it',
    landingCreatePassword: 'Crea una password',
    landingCreatingAccount: 'Creazione account...',
    landingSigningIn: 'Accesso...',
    
    // Feature Sections (Italian)
    landingEngageStudents: 'Coinvolgi gli Studenti Come Mai Prima',
    landingEngageStudentsDesc: 'Trasforma la tua classe con giochi interattivi, apprendimento adattivo e quiz dal vivo che mantengono gli studenti motivati e che imparano.',
    landingPlayToLearnTitle: 'Giocare per Imparare',
    landingPlayToLearnSubtitle: 'Giochi dal Vivo Stile Kahoot',
    landingPlayToLearnDesc: 'Organizza giochi quiz dal vivo o assegna attività al proprio ritmo. Gli studenti si uniscono con un PIN e competono in tempo reale con feedback istantaneo.',
    landingPlayToLearnFeature1: 'Quiz multiplayer dal vivo',
    landingPlayToLearnFeature2: 'Compiti al proprio ritmo',
    landingPlayToLearnFeature3: 'Classifiche in tempo reale',
    landingPlayToLearnFeature4: '9+ modalità di gioco',
    landingEducationalGamesTitle: 'Giochi Educativi',
    landingEducationalGamesSubtitle: 'Attività di Apprendimento Divertenti',
    landingEducationalGamesDesc: 'Crea giochi personalizzati per qualsiasi materia. Condividi con gli studenti tramite link o Google Classroom. Monitora coinvolgimento e progressi.',
    landingEducationalGamesFeature1: 'Modelli di giochi personalizzabili',
    landingEducationalGamesFeature2: 'Condividi tramite link o codice QR',
    landingEducationalGamesFeature3: 'Monitoraggio dei progressi degli studenti',
    landingEducationalGamesFeature4: 'Funziona su qualsiasi dispositivo',
    landingAdaptiveLearningTitle: 'Apprendimento Adattivo',
    landingAdaptiveLearningSubtitle: 'Percorsi di Apprendimento Personalizzati',
    landingAdaptiveLearningDesc: 'Percorsi di apprendimento alimentati dall\'IA che si adattano al ritmo e al livello di ogni studente. Identifica le lacune e fornisci pratica mirata.',
    landingAdaptiveLearningFeature1: 'Raccomandazioni alimentate dall\'IA',
    landingAdaptiveLearningFeature2: 'Pratica personalizzata',
    landingAdaptiveLearningFeature3: 'Analisi dei progressi',
    landingAdaptiveLearningFeature4: 'Regolazione automatica della difficoltà',
    landingExploreFeature: 'Esplora',
    landingProductLabel: 'Prodotto',
    landingCompanyLabel: 'Azienda',
    landingLegalLabel: 'Legale',
    landingHelpCenter: 'Centro Assistenza',
    landingPrivacyPolicyLink: 'Informativa sulla Privacy',
    landingTermsOfServiceLink: 'Termini di Servizio',
    
    // Platform Integrations (Italian)
    landingPlatformIntegrations: 'Integrazioni Piattaforma',
    landingPlatformIntegrationsDesc: 'Connettiti con gli strumenti che già usi',
    landingAiAssistantTitle: 'Assistente IA',
    landingStudentProgressTitle: 'Progressi Studenti',
    landingPresentationsTitle: 'Presentazioni',
    
    // Google Classroom Section (Italian)
    landingConnectPlatforms: 'Connetti le Tue Piattaforme Preferite',
    landingConnectPlatformsDesc: 'Integra facilmente gli strumenti che la tua scuola già usa',
    landingIntegrated: 'INTEGRATO',
    landingImportClasses: 'Importa classi e elenchi automaticamente',
    landingSyncGrades: 'Sincronizza i voti con Google Classroom',
    landingSecurePrivate: 'Sicuro e privato - protetto da OAuth 2.0',
    landingConnectGoogleClassroom: 'Connetti Google Classroom',
    landingComingSoon: 'Prossimamente',
    landingComingSoonBadge: 'Prossimamente',
    landingMoreIntegrations: 'Altre integrazioni sono in arrivo. Facci sapere di cosa hai bisogno!',
    landingTrustedBySchools: 'Affidato da scuole che usano Google Classroom in tutto il mondo',
    
    // Homeschool Section (Italian)
    landingForHomeschool: 'PER HOMESCHOOL',
    landingHomeschoolFriendly: 'Adatto per Homeschool',
    landingLearnMoreHomeschool: 'Scopri di Più sulle Funzionalità Homeschool',
    landingHomeschoolAdaptiveDesc: 'Percorsi alimentati dall\'IA che si adattano al ritmo unico del tuo bambino',
    landingFlexibleCurriculum: 'Curriculum Flessibile',
    landingFlexibleCurriculumDesc: 'Personalizza materie e ritmo secondo gli stili di apprendimento',
    landingTextToSpeechDesc: 'Sintesi vocale integrata per apprendenti uditivi',
    landingSafeContent: 'Contenuto Sicuro',
    landingSafeContentDesc: 'Materiali adatti all\'età e verificati educativamente',
    // Pricing Page (Italian)
    pricingTitle: 'Scegli il Tuo Piano',
    pricingSubtitle: 'Inizia con 7 giorni gratuiti. Cancella in qualsiasi momento.',
    pricingBack: 'Indietro',
    pricingYourDigitalClassroom: 'La tua classe digitale',
    pricingAllRightsReserved: 'Tutti i diritti riservati.',
    pricingIndividualMonthly: 'Mensile Individuale',
    pricingIndividualMonthlyDesc: 'Accesso completo per insegnanti individuali',
    pricingIndividualYearly: 'Annuale Individuale',
    pricingIndividualYearlyDesc: 'Miglior valore per insegnanti individuali',
    pricingSchoolPlanName: 'Piano Scuola',
    pricingSchoolPlanDesc: 'Perfetto per scuole piccole e medie',
    pricingDistrictPlanName: 'Piano Distretto',
    pricingDistrictPlanDesc: 'Per grandi distretti scolastici',
    pricingSave: 'Risparmia',
    pricingMostPopular: 'PIÙ POPOLARE',
    pricingMonth: 'mese',
    pricingYear: 'anno',
    pricingPerTeacher: 'per insegnante',
    pricingBilledAnnually: 'Fatturato annualmente',
    pricingMinimum: 'Minimo',
    pricingMinTeachers: 'Minimo 10 insegnanti per Piano Scuola, 100 per Distretto',
    pricingSignUpFree: 'Registrati Gratis',
    pricingFeatureLessonPlanner: 'Pianificatore lezioni',
    pricingFeatureAttendanceTracker: 'Tracciamento presenze',
    pricingFeatureGradebook: 'Registro voti',
    pricingFeaturePdfExport: 'Esporta PDF',
    pricingFeatureTemplates: 'Modelli',
    pricingFeatureEmailSupport: 'Supporto email',
    pricingFeatureEverythingMonthly: 'Tutto del Mensile',
    pricingFeaturePrioritySupport: 'Supporto prioritario',
    pricingFeature2MonthsFree: '2 mesi gratis',
    pricingFeatureEarlyAccess: 'Accesso anticipato',
    pricingFeatureAllIndividual: 'Tutte le funzioni individuali',
    pricingFeatureAdminDashboard: 'Dashboard admin',
    pricingFeatureSchoolBranding: 'Logo scuola',
    pricingFeatureBulkImport: 'Importazione massiva',
    pricingFeatureSchoolReports: 'Report scolastici',
    pricingFeatureUserManagement: 'Gestione utenti',
    pricingFeatureAllSchool: 'Tutte le funzioni Scuola',
    pricingFeatureDistrictAnalytics: 'Analytics distretto',
    pricingFeatureSsoIntegration: 'Integrazione SSO',
    pricingFeatureDedicatedSupport: 'Supporto dedicato',
    pricingFeatureCustomTraining: 'Formazione personalizzata',
    pricingFeatureApiAccess: 'Accesso API',
    
    // Footer & Navigation (Marketing Pages)
    navFeatures: 'Funzionalità',
    navPricing: 'Prezzi',
    navTrust: 'Fiducia',
    navContact: 'Contatto',
    navLogin: 'Accedi',
    navStartFree: 'Inizia Gratis',
    footerProduct: 'Prodotto',
    footerResources: 'Risorse',
    footerTrust: 'Fiducia',
    footerLegal: 'Legale',
    footerFeatures: 'Funzionalità',
    footerPricing: 'Prezzi',
    footerIntegrations: 'Integrazioni',
    footerHelpCenter: 'Centro Assistenza',
    footerContact: 'Contatto',
    footerTrustCenter: 'Centro Fiducia',
    footerPrivacy: 'Privacy',
    footerTerms: 'Termini',
    footerCookies: 'Cookies',
    footerAccessibility: 'Accessibilità',
    footerTagline: 'Lo spazio di lavoro intelligente per insegnanti.',
    footerSupportNotice: 'Hai bisogno di aiuto? Le nostre risorse di supporto sono qui per aiutarti.',
    footerAllRightsReserved: 'Tutti i diritti riservati.',
    startFreeTrial: 'Inizia la Prova Gratuita',
    viewPricing: 'Vedi Prezzi',
    featuresHeroTitle: 'Strumenti potenti progettati per gli insegnanti',
    featuresHeroSubtitle: 'TeacherHubPro riunisce gli strumenti essenziali di cui gli educatori hanno bisogno per pianificare, organizzare, creare e gestire il lavoro in classe in modo più efficiente.',
    featuresCTATitle: 'Scopri come TeacherHubPro si adatta alla tua giornata di insegnamento',
    featuresCTASubtitle: 'Esplora la piattaforma e scopri un modo migliore per pianificare, organizzare e insegnare.',
    contactTitle: 'Contattaci',
    contactSendMessage: 'Inviaci un messaggio',
    contactName: 'Nome',
    contactEmail: 'Email',
    contactSubject: 'Oggetto',
    contactMessage: 'Messaggio',
    contactEmailSupport: 'Supporto Email',
    contactHelpCenter: 'Centro Assistenza',
    landingCTATitle: 'Inizia a semplificare il tuo flusso di lavoro didattico oggi',
    landingCTASubtitle: 'Unisciti agli educatori che usano TeacherHubPro per risparmiare tempo, rimanere organizzati e lavorare in modo più intelligente con l\'IA.',
    
    // Hero Section
    heroTitle: 'Lo Spazio di Lavoro Intelligente per Insegnanti',
    heroSubtitle: 'Pianifica lezioni, gestisci voti, monitora presenze, crea materiali didattici e risparmia ore ogni settimana con una piattaforma IA creata per insegnanti.',
    seeHowItWorks: 'Scopri Come Funziona',
    heroSupportingText: 'Nessun costo IA aggiuntivo. Creato per insegnanti, tutor, educatori domestici e scuole.',
    dashboardLessons: 'Lezioni',
    dashboardStudents: 'Studenti',
    dashboardAvgGrade: 'Media',
    hoursSavedPerWeek: 'ore risparmiate/settimana',
    
    // Trust Bar
    trustBuiltForEducators: 'Creato per educatori moderni',
    trustAIIncluded: 'IA inclusa in ogni piano',
    trustSecureEasy: 'Sicuro e facile da usare',
    trustReduceWorkload: 'Progettato per ridurre il carico amministrativo',
    
    // Benefits Grid
    benefitSectionTitle: 'Tutto ciò di cui gli insegnanti hanno bisogno, in un unico posto',
    benefitSectionSubtitle: 'Smetti di passare tra documenti, fogli di calcolo e strumenti scollegati. TeacherHubPro riunisce il tuo flusso di lavoro principale in una piattaforma semplice.',
    benefitLessonPlanningTitle: 'Pianificazione Lezioni',
    benefitLessonPlanningDesc: 'Crea piani di lezione organizzati e riutilizzabili in meno tempo con modelli integrati e supporto IA.',
    benefitGradebookTitle: 'Registro Voti',
    benefitGradebookDesc: 'Monitora i progressi degli studenti con un registro voti pulito e semplice fatto per l\'uso reale in classe.',
    benefitAttendanceTitle: 'Monitoraggio Presenze',
    benefitAttendanceDesc: 'Gestisci le presenze rapidamente e mantieni i registri di classe organizzati senza documentazione extra.',
    benefitAIToolsTitle: 'Strumenti IA per l\'Insegnamento',
    benefitAIToolsDesc: 'Genera idee per lezioni, attività, esercizi di scrittura, materiali didattici e altro in pochi secondi.',
    benefitPresentationsTitle: 'Presentazioni e Risorse',
    benefitPresentationsDesc: 'Crea materiali per la classe più velocemente e mantieni tutto connesso in un unico flusso di lavoro.',
    benefitWorkflowTitle: 'Flusso di Lavoro per Insegnanti',
    benefitWorkflowDesc: 'Progettato per educatori che hanno bisogno di strumenti pratici, non software complessi.',
    
    // Outcomes Section
    outcomeSectionTitle: 'Meno tempo in amministrazione, più tempo a insegnare',
    outcomePlanFaster: 'Pianifica più velocemente',
    outcomePlanFasterDesc: 'Crea piani di lezione, attività di classe e materiali senza partire da zero ogni volta.',
    outcomeStayOrganized: 'Rimani organizzato',
    outcomeStayOrganizedDesc: 'Mantieni voti, presenze e risorse didattiche in un unico posto invece che sparse in più strumenti.',
    outcomeTeachConfidence: 'Insegna con fiducia',
    outcomeTeachConfidenceDesc: 'Usa IA e flussi di lavoro ottimizzati per ridurre lo stress e mantenere il tuo lavoro in classe in movimento.',
    
    // Social Proof Section
    socialProofSectionTitle: 'Costruito per supportare il vero lavoro in classe',
    socialProofEducators: 'Progettato specificamente per educatori',
    socialProofSupport: 'Include risorse di supporto e aiuto guidato',
    socialProofWorkflows: 'Costruito per semplificare i flussi di lavoro comuni degli insegnanti',
    socialProofSuitable: 'Adatto a insegnanti individuali, educatori domestici e uso scolastico',
    socialProofHelpCenter: 'Risorse di supporto disponibili • Centro assistenza incluso',
    
    // Use Cases Section
    useCaseSectionTitle: 'Fatto per diversi ambienti di insegnamento',
    useCaseClassroomTitle: 'Insegnanti di Classe',
    useCaseClassroomDesc: 'Gestisci pianificazione, valutazione e organizzazione quotidiana della classe in un unico posto.',
    useCaseHomeschoolTitle: 'Educatori Domestici',
    useCaseHomeschoolDesc: 'Crea flussi di lavoro flessibili per le lezioni e mantieni i piani di apprendimento organizzati.',
    useCaseTutorsTitle: 'Tutor e Piccoli Programmi',
    useCaseTutorsDesc: 'Monitora il lavoro degli studenti, pianifica le sessioni e crea risorse in modo efficiente.',
    useCaseSchoolsTitle: 'Scuole e Team',
    useCaseSchoolsDesc: 'Supporta gli educatori con una piattaforma che migliora la coerenza e fa risparmiare tempo.',
    
    // Feature Detail Pages - Common
    backToFeatures: 'Torna alle Funzionalità',
    keyBenefits: 'Vantaggi Principali',
    howItWorks: 'Come Funziona',
    readyToGetStarted: 'Pronto per iniziare?',
    featureCTASubtitle: 'Unisciti a migliaia di educatori che stanno già risparmiando tempo con TeacherHubPro.',
    exploreOtherFeatures: 'Esplora Altre Funzionalità',
    learnMore: 'Scopri di più',
    featureLessonPlanningTitle: 'Pianificazione Lezioni',
    featureLessonPlanningSubtitle: 'Crea piani organizzati più velocemente con modelli e IA.',
    featureLessonPlanningDescription: 'Lo strumento di pianificazione ti aiuta a creare piani completi rapidamente.',
    featureLessonPlanningBenefit1: 'Crea piani fino a 5x più velocemente',
    featureLessonPlanningBenefit2: 'Allineamento automatico agli standard',
    featureLessonPlanningBenefit3: 'Riutilizza piani per diverse classi',
    featureLessonPlanningBenefit4: 'Condividi con colleghi in tempo reale',
    featureLessonPlanningHowItWorks1: 'Scegli un modello o inizia da zero',
    featureLessonPlanningHowItWorks2: 'Usa suggerimenti IA',
    featureLessonPlanningHowItWorks3: 'Salva e riutilizza',
    featureGradebookTitle: 'Registro Voti',
    featureGradebookSubtitle: 'Monitora i progressi degli studenti facilmente.',
    featureGradebookDescription: 'Il nostro registro è intuitivo e potente.',
    featureGradebookBenefit1: 'Calcoli automatici dei voti',
    featureGradebookBenefit2: 'Monitoraggio visivo dei progressi',
    featureGradebookBenefit3: 'Genera pagelle istantaneamente',
    featureGradebookBenefit4: 'Insights IA per identificare difficoltà',
    featureGradebookHowItWorks1: 'Configura le tue classi',
    featureGradebookHowItWorks2: 'Crea compiti e inserisci voti',
    featureGradebookHowItWorks3: 'Consulta analisi e genera report',
    featureAttendanceTitle: 'Monitoraggio Presenze',
    featureAttendanceSubtitle: 'Gestisci le presenze rapidamente senza documentazione.',
    featureAttendanceDescription: 'Addio ai fogli di presenza cartacei.',
    featureAttendanceBenefit1: 'Presenze in meno di 30 secondi',
    featureAttendanceBenefit2: 'Rilevamento automatico assenze',
    featureAttendanceBenefit3: 'Report per l\'amministrazione',
    featureAttendanceBenefit4: 'Notifiche opzionali ai genitori',
    featureAttendanceHowItWorks1: 'Apri la classe e segna',
    featureAttendanceHowItWorks2: 'Rivedi i pattern di presenza',
    featureAttendanceHowItWorks3: 'Esporta o condividi',
    featureAIToolsTitle: 'Strumenti IA',
    featureAIToolsSubtitle: 'Genera idee e materiali in secondi.',
    featureAIToolsDescription: 'Sfrutta la potenza dell\'IA per il tuo insegnamento.',
    featureAIToolsBenefit1: 'Genera idee adatte alla tua materia',
    featureAIToolsBenefit2: 'Crea materiali differenziati',
    featureAIToolsBenefit3: 'Costruisci valutazioni automaticamente',
    featureAIToolsBenefit4: 'Nessun costo IA aggiuntivo',
    featureAIToolsHowItWorks1: 'Descrivi cosa ti serve',
    featureAIToolsHowItWorks2: 'L\'IA genera opzioni',
    featureAIToolsHowItWorks3: 'Modifica e salva',
    featurePresentationsTitle: 'Presentazioni e Risorse',
    featurePresentationsSubtitle: 'Crea materiali più velocemente.',
    featurePresentationsDescription: 'Crea tutto in un unico posto.',
    featurePresentationsBenefit1: 'Modelli educativi',
    featurePresentationsBenefit2: 'Integrazione con piani di lezione',
    featurePresentationsBenefit3: 'Condivisione facile',
    featurePresentationsBenefit4: 'Importa risorse esistenti',
    featurePresentationsHowItWorks1: 'Scegli un modello',
    featurePresentationsHowItWorks2: 'Aggiungi contenuti',
    featurePresentationsHowItWorks3: 'Collega e condividi',
    featureWorkflowTitle: 'Flusso di Lavoro Amichevole',
    featureWorkflowSubtitle: 'Per educatori che necessitano strumenti pratici.',
    featureWorkflowDescription: 'Costruito per come lavorano davvero gli insegnanti.',
    featureWorkflowBenefit1: 'Interfaccia intuitiva',
    featureWorkflowBenefit2: 'Desktop e mobile',
    featureWorkflowBenefit3: 'Configurazione rapida',
    featureWorkflowBenefit4: 'Integrazione con i tuoi strumenti',
    featureWorkflowHowItWorks1: 'Registrati e configura',
    featureWorkflowHowItWorks2: 'Tutto da una dashboard',
    featureWorkflowHowItWorks3: 'Sincronizzazione automatica',
    featureClassroomTeachersTitle: 'Per Insegnanti',
    featureClassroomTeachersSubtitle: 'Pianificazione e voti in un unico posto.',
    featureClassroomTeachersDescription: 'TeacherHubPro riunisce tutti i tuoi strumenti.',
    featureClassroomTeachersBenefit1: 'Pianifica per più classi',
    featureClassroomTeachersBenefit2: 'Monitora voti e presenze',
    featureClassroomTeachersBenefit3: 'Genera report per i genitori',
    featureClassroomTeachersBenefit4: 'Accedi da ovunque',
    featureClassroomTeachersHowItWorks1: 'Configura le tue classi',
    featureClassroomTeachersHowItWorks2: 'Pianifica e registra quotidianamente',
    featureClassroomTeachersHowItWorks3: 'Comunica via portale',
    featureHomeschoolTitle: 'Per Homeschool',
    featureHomeschoolSubtitle: 'Crea percorsi di apprendimento flessibili.',
    featureHomeschoolDescription: 'TeacherHubPro aiuta a creare percorsi personalizzati.',
    featureHomeschoolBenefit1: 'Percorsi personalizzati per ogni bambino',
    featureHomeschoolBenefit2: 'Registri organizzati',
    featureHomeschoolBenefit3: 'Idee generate dall\'IA',
    featureHomeschoolBenefit4: 'Orari flessibili',
    featureHomeschoolHowItWorks1: 'Crea profili per ogni bambino',
    featureHomeschoolHowItWorks2: 'Monitora i progressi',
    featureHomeschoolHowItWorks3: 'Genera report',
    featureTutorsTitle: 'Per Tutor',
    featureTutorsSubtitle: 'Monitora il lavoro degli studenti efficientemente.',
    featureTutorsDescription: 'TeacherHubPro ti tiene organizzato.',
    featureTutorsBenefit1: 'Monitora più studenti',
    featureTutorsBenefit2: 'Pianifica sessioni',
    featureTutorsBenefit3: 'Report di progresso per genitori',
    featureTutorsBenefit4: 'Materiali personalizzati',
    featureTutorsHowItWorks1: 'Aggiungi i tuoi studenti',
    featureTutorsHowItWorks2: 'Pianifica e monitora',
    featureTutorsHowItWorks3: 'Condividi via portale',
    featureSchoolsTitle: 'Per Scuole',
    featureSchoolsSubtitle: 'Piattaforma coerente per educatori.',
    featureSchoolsDescription: 'TeacherHubPro scala con la tua scuola.',
    featureSchoolsBenefit1: 'Strumenti coerenti',
    featureSchoolsBenefit2: 'Libreria risorse condivisa',
    featureSchoolsBenefit3: 'Dashboard amministratore',
    featureSchoolsBenefit4: 'Prezzi volume',
    featureSchoolsHowItWorks1: 'Contattaci per i prezzi',
    featureSchoolsHowItWorks2: 'Integra gli insegnanti',
    featureSchoolsHowItWorks3: 'Monitora e supporta'
  },
  zh: {
    absent: "缺勤",
    activities: "活动",
    art: "美术",
    assignments: "作业",
    attendanceReport: "考勤报告",
    book: "课本",
    brainstorming: "头脑风暴",
    buildingBackground: "背景铺垫",
    category: "类别",
    comprehensionQuestions: "理解题",
    createAssignment: "创建作业",
    createPlan: "创建教案",
    dayTheme: "当日主题",
    description: "描述",
    dictionary: "词典",
    dueDate: "截止日期",
    duplicateLastWeek: "复制上周",
    excused: "请假",
    exercisePractice: "练习",
    expectations: "预期目标",
    exportPdf: "导出PDF",
    firstWeek: "第一周",
    foundationalSkills: "基础技能",
    gradeEntry: "成绩录入",
    guidedReading: "引导与齐读",
    handouts: "讲义",
    health: "健康",
    homework: "家庭作业",
    language: "语言",
    late: "迟到",
    level1Memory: "层级1：记忆",
    level2Processing: "层级2：加工",
    level3Strategic: "层级3：策略思维",
    level4Extended: "层级4：拓展思维",
    listeningAndSpeaking: "听/说",
    markAllPresent: "全部标记出勤",
    materials: "材料",
    mathematics: "数学",
    missing: "缺交",
    notebook: "笔记本",
    objectiveOfWeek: "本周目标",
    oralQuestions: "口头提问",
    other: "其他",
    physicalEducation: "体育",
    points: "分数",
    present: "出勤",
    pricingActiveSubscription: "订阅生效中",
    pricingAdminAccount: "管理员账户 - 完全访问",
    pricingContactUs: "联系我们",
    pricingCurrentPlan: "当前套餐",
    pricingDaysRemaining: "天剩余",
    pricingDistrictDesc: "适用于具有高级管理功能的学区",
    pricingDistrictPlan: "学区版",
    pricingFeatureAIAssistant: "AI助手（Claude）",
    pricingFeatureAdvancedAnalytics: "高级分析",
    pricingFeatureAttendance: "考勤管理",
    pricingFeatureBasicLessonPlanning: "基础备课",
    pricingFeatureCustomTemplates: "自定义模板",
    pricingFeatureEverythingPro: "包含专业版全部功能",
    pricingFeatureEverythingSchool: "包含学校版全部功能",
    pricingFeatureEverythingStarter: "包含入门版全部功能",
    pricingFeatureMultiSchool: "多校管理",
    pricingFeatureSchoolDashboard: "学校仪表盘",
    pricingFeatureSharedResources: "共享资源",
    pricingFeatureTeacherManagement: "教师管理",
    pricingFeatureUnlimitedClasses: "班级不限",
    pricingFeatureUnlimitedStudents: "学生不限",
    pricingForSchoolPlans: "适用于学校套餐",
    pricingFreeTrial: "免费试用",
    pricingGetStarted: "开始使用",
    pricingNumberOfTeachers: "教师数量",
    pricingProDesc: "为高级用户提供的高级功能",
    pricingProPlan: "专业版",
    pricingSchoolDesc: "适用于整所学校，含团队功能",
    pricingSchoolDistrict: "适用于学校和学区",
    pricingSchoolPlan: "学校版",
    pricingStarterDesc: "适合个人教师",
    pricingStarterPlan: "入门版",
    pricingSubscribe: "订阅",
    pricingTeachers: "名教师",
    pricingTrialExpired: "您的免费试用已到期。请选择一个套餐以继续使用 TeacherHubPro。",
    project: "项目",
    quiz: "测验",
    readPages: "阅读页数",
    reading: "阅读",
    religion: "宗教",
    saveAsTemplate: "保存为模板",
    science: "科学",
    secondWeek: "第二周",
    skillsOfWeek: "本周技能",
    socialStudies: "社会研究",
    spanish: "西班牙语",
    standards: "课程标准",
    story: "故事/标题",
    submitAttendance: "提交考勤",
    takeAttendance: "点名考勤",
    tardy: "迟到",
    teachersGuide: "教师用书",
    test: "考试",
    testQuiz: "考试/测验",
    title: "标题",
    unit: "单元",
    vocabularyDevelopment: "词汇发展",
    weekOf: "周",
    weeklyPlan: "周计划",
    writing: "写作",

    // Navigation
    dashboard: '仪表板',
    planner: '课程计划',
    attendance: '考勤',
    gradebook: '成绩册',
    classes: '班级',
    settings: '设置',
    logout: '退出',
    
    // Auth
    login: '登录',
    register: '注册',
    email: '邮箱',
    password: '密码',
    name: '姓名',
    signInWithGoogle: '使用Google登录',
    orContinueWith: '或使用',
    dontHaveAccount: '没有账户？',
    alreadyHaveAccount: '已有账户？',
    
    // Dashboard
    welcome: '欢迎',
    todayOverview: '今日概览',
    quickActions: '快捷操作',
    attendancePending: '待处理考勤',
    upcomingAssignments: '即将到期作业',
    recentPlans: '最近计划',
    totalClasses: '班级总数',
    totalStudents: '学生总数',
    totalPlans: '计划总数',
    
    // Classes
    addClass: '添加班级',
    className: '班级名称',
    grade: '年级',
    section: '班组',
    subject: '科目',
    yearTerm: '学年/学期',
    students: '学生',
    addStudent: '添加学生',
    firstName: '名',
    lastName: '姓',
    studentNumber: '学号',
    parentEmail: '家长邮箱',
    notes: '备注',
    accommodations: '特殊安排',
    
    // Common
    save: '保存',
    cancel: '取消',
    delete: '删除',
    edit: '编辑',
    view: '查看',
    search: '搜索',
    filter: '筛选',
    loading: '加载中...',
    noData: '暂无数据',
    confirmDelete: '确定要删除吗？',
    success: '成功',
    error: '错误',
    from: '从',
    to: '到',
    date: '日期',
    teacher: '教师',
    selectLanguage: '选择语言',
    
    // Days
    monday: '星期一',
    tuesday: '星期二',
    wednesday: '星期三',
    thursday: '星期四',
    friday: '星期五',
    
    // Games
    educationalGames: '教育游戏',
    myGames: '我的游戏',
    createGame: '创建游戏',
    play: '开始',
    copyLink: '复制链接',
    shareToClassroom: '分享到Classroom',
    deleteGame: '删除游戏',
    questions: '问题',
    gameType: '游戏类型',
    gradeLevel: '年级水平',
    
    // Landing Page
    landingHeroTitle: '一个平台，完整的教室',
    landingHeroSubtitle: '课程计划、考勤、成绩和AI助手。教师所需的一切。',
    landingBuiltForTeachers: '专为教师打造',
    landingStartFree: '免费开始',
    landingViewPricing: '查看价格',
    landingUsedBySchools: '全球学校使用',
    landingEverythingYouNeed: '您需要的一切',
    landingEverythingDesc: '专为教师设计的完整平台。',
    landingFeatures: '功能',
    landingAiAssistant: 'AI助手',
    landingPoweredBy: '由Anthropic提供支持',
    landingActiveTeachers: '活跃教师',
    landingHomeschool: '家庭教育',
    landingIntegrations: '集成',
    landingCustomization: '定制',
    landingPricing: '价格',
    landingGetStarted: '开始 - 登录',
    landingStart: '开始',
    landingAiLessonPlan: 'AI计划',
    landingPresentations: '演示文稿',
    landingGrades: '成绩',
    landingPlanFaster: '更快地规划',
    landingPlanFasterDesc: '用我们直观的格式在几秒钟内创建每周课程计划。',
    landingWeek1: '第1周',
    landingYourBrand: '您的品牌，您的风格',
    landingYourBrandDesc: '使用您学校的标志和颜色进行个性化定制。',
    landingYourSchool: '您的学校',
    landingCustomBranding: '自定义品牌',
    landingExportPro: '专业导出',
    landingExportProDesc: '生成可打印的PDF：计划、成绩单、代课包。',
    landingDownloadPdf: '下载PDF',
    landingAiAssistantTitle: 'AI助手',
    landingAiAssistantDesc: '使用AI生成课程计划、活动和问题。',
    landingLessonPlans: '课程计划',
    landingQuizzesTests: '测验和考试',
    landingDigitalClassroom: '您的数字教室',
    landingTryFree: '免费试用30天',
    landingNoCard: '无需信用卡',
    landingFullName: '全名',
    landingCreateAccount: '创建免费账户',
    landingOrLoginWith: '或使用以下方式登录',
    landingHaveAccount: '已有账户？',
    landingLoginHere: '在此登录',
    landingNeedAccount: '需要账户？',
    landingRegisterHere: '在此注册',
    // AI Section
    landingAiTeachingAssistant: 'AI教学助手',
    landingYouAsked: '您问道：',
    landingAiResponse: 'AI回答：',
    landingAiQuestion: '如何向四年级学生教授等值分数？',
    landingAiAnswer: '这是一个基于标准的课程计划，包含实践活动...',
    landingQuizzes: '测验',
    landingWorksheets: '练习题',
    landingIdeas: '创意',
    landingNewAi: '新功能：人工智能',
    landingAiAssistantToTeach: '您的AI助手，助您更好地教学',
    landingAiAssistantDesc2: '即时生成课程计划、测验、摘要和基于标准的活动。',
    landingAlignedStandards: '符合教育标准',
    landingGenerateSeconds: '几秒钟内生成内容',
    landingBilingual: '多语言支持',
    landingPoweredBy: '由Claude Sonnet 4.5驱动',
    landingTryItFree: '免费试用',
    landingIncludedSubscription: '订阅包含',
    // Integrations Section
    landingConnectTools: '连接您喜爱的工具',
    landingConnectToolsDesc: '整合您已经使用的平台。',
    landingCalendarSync: '日历同步',
    landingCalendarSyncDesc: '将您的计划导出到Google日历、Outlook或Apple日历。',
    landingGoogleClassroom: 'Google Classroom',
    landingGoogleClassroomDesc: '直接与学生分享活动和游戏。',
    landingPdfExport: 'PDF导出',
    landingPdfExportDesc: '生成专业的PDF文件：计划、成绩单等。',
    landingEmailNotifications: '邮件通知',
    landingEmailNotificationsDesc: '向家长和管理员发送自动报告。',
    landingViewIntegrations: '查看所有集成',
    // Customization Section
    landingYourBrandYourWay: '您的品牌，您的方式',
    landingCustomizeDesc: '使用学校的颜色、标志和偏好进行个性化定制。',
    landingSchoolLogo: '学校标志',
    landingSchoolLogoDesc: '在所有文档和报告中添加您的标志。',
    landingColorThemes: '颜色主题',
    landingColorThemesDesc: '选择与学校形象相匹配的颜色。',
    landingCustomTemplates: '自定义模板',
    landingCustomTemplatesDesc: '为您的计划创建可重复使用的模板。',
    landingMultiLanguage: '多语言',
    landingMultiLanguageDesc: '使用多种语言工作。',
    // Homeschool Section
    landingHomeschoolTitle: '家庭教育',
    landingHomeschoolSubtitle: '您完整的家庭教育伴侣',
    landingHomeschoolDesc: '成功家庭教育体验所需的一切。',
    landingAdaptiveLearning: '自适应学习',
    landingAdaptiveLearningDesc: '根据每个孩子的节奏调整学习路径。',
    landingFullVisibility: '全面可见性',
    landingFullVisibilityDesc: '通过详细报告实时跟踪进度。',
    landingFlexibleCurriculum: '灵活课程',
    landingFlexibleCurriculumDesc: '根据家庭需求定制课程。',
    landing100Customizable: '100%可定制',
    landingDownloadLearn: '下载学习',
    landingDownloadLearnDesc: '导出材料进行离线学习。',
    landingPdfExportFeature: 'PDF导出',
    landingTextToSpeech: '文字转语音',
    landingTextToSpeechDesc: '所有阅读内容的音频支持。',
    landingAudioLearning: '音频学习',
    landingSafeContent: '安全内容',
    landingSafeContentDesc: '适合年龄并经过审核的材料。',
    landingParentalControl: '家长控制',
    landingSubjectModules: '学科模块',
    landingMathematics: '数学',
    landingLanguageArts: '语文',
    landingScience: '科学',
    landingAstronomy: '天文学',
    landingGeography: '地理',
    landingHistory: '历史',
    landingArt: '艺术',
    landingPhysicalEd: '体育',
    landingStartHomeschooling: '开始家庭教育',
    // CTA Section
    landing7DaysFree: '7天免费',
    landingGetStartedToday: '立即开始',
    landingStartYourJourney: '开始您的数字教室之旅。无需信用卡。',
    landingNoCreditCard: '无需信用卡',
    landingSetup2Min: '2分钟设置',
    landingCancelAnytime: '随时取消',
    landingSupportBilingual: '多语言支持',
    landingActiveTeachers: '活跃教师',
    landingSignIn: '登录',
    landingCreateAccountTab: '创建账户',
    landingContinueGoogle: '使用Google继续',
    landingOrWithEmail: '或使用邮箱',
    landingEmail: '邮箱',
    landingPassword: '密码',
    landingFullNameLabel: '全名',
    landingCreateFreeAccount: '创建免费账户',
    // Footer
    landingContact: '联系我们',
    landingPrivacyPolicy: '隐私政策',
    landingHelp: '帮助',
    landingTermsOfUse: '使用条款',
    landingCookiesPolicy: 'Cookie政策',
    landingAccessibility: '无障碍',
    landingAllRightsReserved: '版权所有。',
    
    // AI Features Section
    landingAdvancedAiFeatures: '高级AI功能',
    landingIntelligentPlanning: '智能AI规划',
    landingAiPlanningDesc: '从生成完整计划到每日建议，AI帮助您的每一步。',
    landingGenerateFullWeek: '生成完整一周',
    landingGenerateFullWeekDesc: '一键生成5天的活动，具有教学进度：介绍→练习→掌握→评估。',
    landingDays: '天',
    landingProgression: '进度',
    landingDailySuggestions: '每日建议',
    landingDailySuggestionsDesc: '每天显示其教学阶段（介绍、练习、掌握）。获取每个阶段的具体建议。',
    landingMonFri: '周一→周五',
    landingContextual: '情境化',
    landingReusableTemplates: '可重用模板',
    landingReusableTemplatesDesc: '将您最好的计划保存为模板。一键克隆和自定义新主题。',
    landingSave: '保存',
    landingClone: '克隆',
    landing5StarterTemplates: '5个入门模板',
    landing5StarterTemplatesDesc: '立即开始使用专业模板：分数、阅读理解、科学方法、写作和乘法。',
    landingMath: '数学',
    landingELA: '语文',
    landingAiCustomize: 'AI自定义',
    landingAiCustomizeDesc: '采用任何模板并将其适应新主题。AI保持成功的结构同时更改内容。',
    landingNewTopic: '新主题',
    landingSameStructure: '相同结构',
    landingTemplateOfWeek: '本周模板',
    landingTemplateOfWeekDesc: '每周我们在您的仪表板上展示一个不同的模板，并提供具体的自定义提示。',
    landingWeeklyRotation: '每周轮换',
    landingTips: '提示',
    landingTryAiFree7Days: '免费试用AI 7天',
    
    // AI Presentations Section
    landingNewAiPresentations: '新功能！AI演示文稿',
    landingCreatePresentations: '创建引人入胜的演示文稿',
    landingPresentationsDesc: '使用AI生成完整的教育演示文稿。选择视觉主题、添加图像、应用过渡效果并保存以备后用。',
    landingAiGenerate: 'AI生成',
    landingAiGenerateDesc: '几秒内生成6+张幻灯片',
    landing8VisualThemes: '8种视觉主题',
    landing8VisualThemesDesc: '海洋、银河、黑板...',
    landingSaveAndLoad: '保存和加载',
    landingSaveAndLoadDesc: '个人图书馆',
    landingDownloadHtml: '下载HTML',
    landingDownloadHtmlDesc: '离线使用',
    landingCreateFirstPresentation: '创建我的第一个演示文稿',
    landingTheSolarSystem: '太阳系',
    landingScienceGrade: '科学 - 四年级',
    landingTransitionsEnabled: '✨ 已启用过渡效果',
    
    // Customization Section
    landingFullCustomization: '完全自定义',
    landingSchoolBrandingTitle: '您学校的身份体现在每一份文档上',
    landingSchoolBrandingDesc: '从您的标志到颜色，每份PDF和报告都反映您机构的品牌。',
    landingSchoolLogoAll: '您学校的标志出现在所有文档上',
    landingCustomColorsMatch: '与您品牌匹配的自定义颜色',
    landingPdfFormats: '您学校的精确PDF格式',
    landingCentralizedAdmin: '集中式管理仪表板',
    landingTeachersPlanning: '教师规划',
    landingYourLogoHere: '您的标志在这里',
    landingYourColors: '您的颜色',
    
    // Integrations Section
    landingConnectFavorite: '连接您喜爱的平台',
    landingConnectFavoriteDesc: '与您已使用的工具同步，实现无缝工作流程。',
    landingAvailable: '可用',
    landingMostPopular: '最受欢迎',
    landingImportClasses: '导入班级',
    landingImportClassesDesc: '自动导入您的班级和学生名单',
    landingSyncGrades: '同步成绩',
    landingSyncGradesDesc: '一键将成绩发送到Google Classroom',
    landingSecurePrivate: '安全且私密',
    landingSecurePrivateDesc: '安全的OAuth认证，您的数据受到保护',
    landingSetupMinutes: '几分钟内设置',
    landingNoExtraCost: '无额外费用',
    landingSupportIncluded: '包含支持',
    landingComingSoon: '即将推出',
    landingNeedIntegration: '需要其他集成？',
    landingNeedIntegrationDesc: '我们不断添加新的集成。告诉我们您使用的平台，我们会优先处理。',
    landingRequestIntegration: '请求集成',
    
    // Homeschool Section
    landingPerfectHomeschool: '家庭教育者的完美选择',
    landingHomeschoolCompleteDesc: '创建个性化学习体验所需的一切，从课程规划到进度跟踪。',
    landingAiPowered: 'AI驱动',
    landingParentDashboard: '家长仪表板',
    landingParentDashboardDesc: '在统一仪表板中全面了解每个孩子的进度、成绩和即将到来的作业。',
    landingFlexibleCurriculumDesc2: '自定义科目、进度和教学方法，以匹配每个孩子独特的学习风格。',
    landing100Customizable: '100%可自定义',
    landingDownloadAndLearn: '下载和学习',
    landingDownloadAndLearnDesc: '将课程和材料导出为PDF。即使离线或在旅途中也能继续学习。',
    landingAudioLearning: '音频学习',
    landingTextToSpeechDesc2: '所有内容都有内置文本转语音功能。非常适合听觉学习者或阅读辅助。',
    landingSafeContentDesc2: '所有内容都适合年龄且经过教育审核。家长对孩子可访问的内容拥有完全控制权.',
    
    // New Landing Page Copy (Chinese)
    landingSave5Hours: '每周节省5+小时',
    landingSave5HoursTitle: '每周在备课、批改和行政管理上节省5+小时',
    landingSave5HoursSubtitle: 'TeacherHubPro帮助忙碌的教育工作者创建课程计划、管理成绩、记录考勤，并使用AI简化日常工作——一站式解决。',
    landingBuiltForBusyTeachers: '专为忙碌的教师打造',
    landingStartFreeNoCreditCard: '免费开始——无需信用卡',
    landingSeeHowItWorks: '查看如何使用',
    landingTeachingIsMore: '教学不仅仅是教学',
    landingTeachingIsMoreDesc: '在课程规划、批改、考勤、报告和沟通之间——大多数教师每周花费数小时在占用学生和个人生活时间的任务上。',
    landingYouBecameTeacher: '你成为教师是为了教育。',
    landingNotToDrown: '而不是淹没在文书工作中。',
    landingEverythingSimple: '您需要的一切。一个简单的平台。',
    landingLessBusywork: '更少的行政工作。更多的教学。',
    landingGenerateLessons: '几分钟内生成结构化的课程计划',
    landingTrackGrades: '使用易用的数字成绩簿跟踪成绩',
    landingRecordAttendance: '快速记录考勤',
    landingOrganizeData: '在一个仪表板中组织班级数据',
    landingUseAiTools: '使用AI工具加速备课',
    landingWhatTeachersExperience: '教师的体验',
    landingFasterPlanning: '更快的每周规划',
    landingLessTimeGrading: '更少的批改时间',
    landingBetterOrganization: '更好的组织',
    landingMoreConfidence: '更多的课堂自信',
    landingMorePersonalTime: '放学后更多个人时间',
    landing3SimpleSteps: '3个简单步骤开始',
    landingValueInMinutes: '几分钟内就能看到价值——而不是几小时。',
    landingCreateClasses: '创建您的班级',
    landingCreateClassesDesc: '几秒钟内设置班级并添加学生',
    landingGenerateWithAi: '使用AI生成课程',
    landingGenerateWithAiDesc: '借助AI创建全面的课程计划',
    landingManageGradesAttendance: '轻松管理成绩和考勤',
    landingManageGradesAttendanceDesc: '从直观的仪表板跟踪一切',
    landingBuiltForEducators: '专为教育工作者打造',
    landingElementaryTeachers: '小学教师',
    landingMiddleSchoolEducators: '初中教育者',
    landingHighSchoolTeachers: '高中教师',
    landingHomeschoolEducators: '家庭教育者',
    landingInstructionalCoaches: '教学教练',
    landingJoinThousands: '加入数千名教师',
    landingJoinThousandsDesc: '全国各地的教育工作者已经在使用TeacherHubPro来优化工作流程，与学生共度更多时间，并重获晚间时光。',
    landingReadyToSimplify: '准备好简化您的教学生活了吗？',
    landingStartFreeToday: '今天免费开始',
    landingExperienceTimeSaved: '今天创建您的免费账户，体验这周您能节省多少时间。',
    landingNoCreditCardRequired: '无需信用卡',
    landingSetupIn2Minutes: '2分钟内完成设置',
    landingCancelAnytime: '随时取消',
    landingFullAccess: '完全访问所有功能',
    landingTeachersUsing: '名教师已在使用TeacherHubPro',
    landingSimplifyingTeaching: '简化教学，一个教室一个教室地改变。',
    landingLogIn: '登录',
    landingStartFreeCta: '免费开始',
    landingBackToHome: '返回首页',
    landingWelcomeBack: '欢迎回来',
    landingSignInAccess: '登录以访问您的仪表板',
    landingStartFreeTrial: '开始您的免费试用',
    landingJoinThousandsTeachers: '加入数千名每周节省时间的教师',
    landingCreateFreeAccount: '创建免费账户',
    landingSignInTab: '登录',
    landingContinueWithGoogle: '使用Google继续',
    landingOrWithEmail: '或使用邮箱',
    landingFullName: '全名',
    landingYourName: '您的姓名',
    landingYourEmail: 'you@school.edu',
    landingCreatePassword: '创建密码',
    landingCreatingAccount: '正在创建账户...',
    landingSigningIn: '正在登录...',
    
    // Feature Sections (Chinese)
    landingEngageStudents: '前所未有地吸引学生',
    landingEngageStudentsDesc: '用互动游戏、自适应学习和实时测验改变您的课堂，让学生保持动力和学习。',
    landingPlayToLearnTitle: '游戏中学习',
    landingPlayToLearnSubtitle: 'Kahoot风格的实时游戏',
    landingPlayToLearnDesc: '主持实时问答游戏或布置自主学习活动。学生使用PIN码加入并实时竞争，获得即时反馈。',
    landingPlayToLearnFeature1: '实时多人测验',
    landingPlayToLearnFeature2: '自主学习作业',
    landingPlayToLearnFeature3: '实时排行榜',
    landingPlayToLearnFeature4: '9+种游戏模式',
    landingEducationalGamesTitle: '教育游戏',
    landingEducationalGamesSubtitle: '有趣的学习活动',
    landingEducationalGamesDesc: '为任何科目创建自定义游戏。通过链接或Google Classroom与学生分享。跟踪参与度和进度。',
    landingEducationalGamesFeature1: '可自定义的游戏模板',
    landingEducationalGamesFeature2: '通过链接或二维码分享',
    landingEducationalGamesFeature3: '学生进度跟踪',
    landingEducationalGamesFeature4: '在任何设备上运行',
    landingAdaptiveLearningTitle: '自适应学习',
    landingAdaptiveLearningSubtitle: '个性化学习路径',
    landingAdaptiveLearningDesc: 'AI驱动的学习路径，适应每个学生的节奏和水平。识别差距并提供针对性练习。',
    landingAdaptiveLearningFeature1: 'AI驱动的推荐',
    landingAdaptiveLearningFeature2: '个性化练习',
    landingAdaptiveLearningFeature3: '进度分析',
    landingAdaptiveLearningFeature4: '自动难度调整',
    landingExploreFeature: '探索',
    landingProductLabel: '产品',
    landingCompanyLabel: '公司',
    landingLegalLabel: '法律',
    landingHelpCenter: '帮助中心',
    landingPrivacyPolicyLink: '隐私政策',
    landingTermsOfServiceLink: '服务条款',
    
    // Platform Integrations (Chinese)
    landingPlatformIntegrations: '平台集成',
    landingPlatformIntegrationsDesc: '连接您已经使用的工具',
    landingAiAssistantTitle: 'AI助手',
    landingStudentProgressTitle: '学生进度',
    landingPresentationsTitle: '演示文稿',
    
    // Google Classroom Section (Chinese)
    landingConnectPlatforms: '连接您喜爱的平台',
    landingConnectPlatformsDesc: '无缝集成学校已使用的工具',
    landingIntegrated: '已集成',
    landingImportClasses: '自动导入班级和名册',
    landingSyncGrades: '与Google Classroom同步成绩',
    landingSecurePrivate: '安全私密 - OAuth 2.0保护',
    landingConnectGoogleClassroom: '连接 Google Classroom',
    landingComingSoon: '即将推出',
    landingComingSoonBadge: '即将推出',
    landingMoreIntegrations: '更多集成即将推出。告诉我们您需要什么！',
    landingTrustedBySchools: '受到全球使用Google Classroom的学校信赖',
    
    // Homeschool Section (Chinese)
    landingForHomeschool: '家庭教育',
    landingHomeschoolFriendly: '适合家庭教育',
    landingLearnMoreHomeschool: '了解更多家庭教育功能',
    landingHomeschoolAdaptiveDesc: 'AI驱动的路径，适应孩子独特的学习节奏',
    landingFlexibleCurriculum: '灵活课程',
    landingFlexibleCurriculumDesc: '根据学习风格自定义科目和节奏',
    landingTextToSpeechDesc: '内置文字转语音，适合听觉学习者',
    landingSafeContent: '安全内容',
    landingSafeContentDesc: '适龄且经过教育审核的材料',
    // Pricing Page (Chinese)
    pricingTitle: '选择您的计划',
    pricingSubtitle: '7天免费试用。随时取消。',
    pricingBack: '返回',
    pricingYourDigitalClassroom: '您的数字教室',
    pricingAllRightsReserved: '保留所有权利。',
    pricingIndividualMonthly: '个人月付',
    pricingIndividualMonthlyDesc: '个人教师的完整访问权限',
    pricingIndividualYearly: '个人年付',
    pricingIndividualYearlyDesc: '个人教师的最佳价值',
    pricingSchoolPlanName: '学校计划',
    pricingSchoolPlanDesc: '适合中小型学校',
    pricingDistrictPlanName: '学区计划',
    pricingDistrictPlanDesc: '适合大型学区',
    pricingSave: '节省',
    pricingMostPopular: '最受欢迎',
    pricingMonth: '月',
    pricingYear: '年',
    pricingPerTeacher: '每位教师',
    pricingBilledAnnually: '按年计费',
    pricingMinimum: '最少',
    pricingMinTeachers: '学校计划最少10名教师，学区最少100名',
    pricingSignUpFree: '免费注册',
    pricingFeatureLessonPlanner: '课程计划器',
    pricingFeatureAttendanceTracker: '考勤追踪',
    pricingFeatureGradebook: '成绩册',
    pricingFeaturePdfExport: 'PDF导出',
    pricingFeatureTemplates: '模板',
    pricingFeatureEmailSupport: '邮件支持',
    pricingFeatureEverythingMonthly: '月付的所有功能',
    pricingFeaturePrioritySupport: '优先支持',
    pricingFeature2MonthsFree: '2个月免费',
    pricingFeatureEarlyAccess: '抢先体验',
    pricingFeatureAllIndividual: '所有个人功能',
    pricingFeatureAdminDashboard: '管理员仪表板',
    pricingFeatureSchoolBranding: '学校标识',
    pricingFeatureBulkImport: '批量导入',
    pricingFeatureSchoolReports: '学校报告',
    pricingFeatureUserManagement: '用户管理',
    pricingFeatureAllSchool: '所有学校功能',
    pricingFeatureDistrictAnalytics: '学区分析',
    pricingFeatureSsoIntegration: 'SSO集成',
    pricingFeatureDedicatedSupport: '专属支持',
    pricingFeatureCustomTraining: '定制培训',
    pricingFeatureApiAccess: 'API访问',
    
    // Footer & Navigation (Marketing Pages)
    navFeatures: '功能',
    navPricing: '价格',
    navTrust: '信任',
    navContact: '联系',
    navLogin: '登录',
    navStartFree: '免费开始',
    footerProduct: '产品',
    footerResources: '资源',
    footerTrust: '信任',
    footerLegal: '法律',
    footerFeatures: '功能',
    footerPricing: '价格',
    footerIntegrations: '集成',
    footerHelpCenter: '帮助中心',
    footerContact: '联系',
    footerTrustCenter: '信任中心',
    footerPrivacy: '隐私',
    footerTerms: '条款',
    footerCookies: 'Cookies',
    footerAccessibility: '无障碍',
    footerTagline: '教师的智能工作空间。',
    footerSupportNotice: '需要帮助？我们的支持资源在这里帮助您入门。',
    footerAllRightsReserved: '保留所有权利。',
    startFreeTrial: '开始免费试用',
    viewPricing: '查看价格',
    featuresHeroTitle: '为教师打造的强大工具',
    featuresHeroSubtitle: 'TeacherHubPro汇集了教育工作者需要的基本工具，帮助他们更高效地计划、组织、创建和管理课堂工作。',
    featuresCTATitle: '了解TeacherHubPro如何融入您的教学日常',
    featuresCTASubtitle: '探索平台，发现更好的计划、组织和教学方式。',
    contactTitle: '联系我们',
    contactSendMessage: '给我们留言',
    contactName: '姓名',
    contactEmail: '邮箱',
    contactSubject: '主题',
    contactMessage: '消息',
    contactEmailSupport: '邮件支持',
    contactHelpCenter: '帮助中心',
    landingCTATitle: '今天就开始简化您的教学工作流程',
    landingCTASubtitle: '加入使用TeacherHubPro的教育工作者行列，节省时间，保持有序，借助AI更智能地工作。',
    
    // Hero Section
    heroTitle: '教师的智能工作空间',
    heroSubtitle: '规划课程、管理成绩、追踪考勤、创建课堂材料，通过一个为教师打造的AI平台每周节省数小时。',
    seeHowItWorks: '了解如何运作',
    heroSupportingText: '无额外AI费用。为教师、导师、家庭教育者和学校而建。',
    dashboardLessons: '课程',
    dashboardStudents: '学生',
    dashboardAvgGrade: '平均分',
    hoursSavedPerWeek: '小时节省/周',
    
    // Trust Bar
    trustBuiltForEducators: '为现代教育工作者打造',
    trustAIIncluded: '每个计划都包含AI',
    trustSecureEasy: '安全且易于使用',
    trustReduceWorkload: '旨在减少规划和管理工作量',
    
    // Benefits Grid
    benefitSectionTitle: '教师需要的一切，尽在一处',
    benefitSectionSubtitle: '停止在文档、电子表格和断开连接的工具之间切换。TeacherHubPro将您的核心教学工作流程整合到一个简单的平台中。',
    benefitLessonPlanningTitle: '课程规划',
    benefitLessonPlanningDesc: '通过内置模板和AI支持，更快地创建有组织、可重复使用的课程计划。',
    benefitGradebookTitle: '成绩簿',
    benefitGradebookDesc: '使用为真实课堂使用而设计的简洁成绩簿跟踪学生进度。',
    benefitAttendanceTitle: '考勤追踪',
    benefitAttendanceDesc: '快速管理考勤，无需额外文书工作即可保持班级记录有序。',
    benefitAIToolsTitle: 'AI教学工具',
    benefitAIToolsDesc: '在几秒钟内生成课程创意、活动、写作练习、课堂材料等。',
    benefitPresentationsTitle: '演示文稿和资源',
    benefitPresentationsDesc: '更快地构建课堂材料，并在一个工作流程中保持一切连接。',
    benefitWorkflowTitle: '教师友好的工作流程',
    benefitWorkflowDesc: '为需要实用工具而非复杂软件的教育工作者而设计。',
    
    // Outcomes Section
    outcomeSectionTitle: '减少行政时间，增加教学时间',
    outcomePlanFaster: '更快规划',
    outcomePlanFasterDesc: '创建课程计划、课堂活动和材料，无需每次从头开始。',
    outcomeStayOrganized: '保持有序',
    outcomeStayOrganizedDesc: '将成绩、考勤和教学资源保存在一处，而非分散在多个工具中。',
    outcomeTeachConfidence: '自信教学',
    outcomeTeachConfidenceDesc: '使用AI和优化的工作流程减轻压力，保持课堂工作顺利进行。',
    
    // Social Proof Section
    socialProofSectionTitle: '为支持真实的课堂工作而构建',
    socialProofEducators: '专门为教育工作者设计',
    socialProofSupport: '包括支持资源和引导帮助',
    socialProofWorkflows: '旨在简化常见的教师工作流程',
    socialProofSuitable: '适合个人教师、家庭教育者和学校使用',
    socialProofHelpCenter: '支持资源可用 • 包含帮助中心',
    
    // Use Cases Section
    useCaseSectionTitle: '为不同的教学环境而制作',
    useCaseClassroomTitle: '课堂教师',
    useCaseClassroomDesc: '在一处管理规划、评分和日常课堂组织。',
    useCaseHomeschoolTitle: '家庭教育者',
    useCaseHomeschoolDesc: '创建灵活的课程工作流程，保持学习计划有序。',
    useCaseTutorsTitle: '导师和小型学习项目',
    useCaseTutorsDesc: '跟踪学生工作，规划课程，高效创建资源。',
    useCaseSchoolsTitle: '学校和团队',
    useCaseSchoolsDesc: '通过提高一致性和节省时间的平台支持教育工作者。',
    
    // Feature Detail Pages - Common
    backToFeatures: '返回功能',
    keyBenefits: '主要优势',
    howItWorks: '如何运作',
    readyToGetStarted: '准备开始了吗？',
    featureCTASubtitle: '加入成千上万已经在用TeacherHubPro节省时间的教育工作者。',
    exploreOtherFeatures: '探索其他功能',
    learnMore: '了解更多',
    featureLessonPlanningTitle: '课程规划',
    featureLessonPlanningSubtitle: '使用模板和AI更快地创建有组织的计划。',
    featureLessonPlanningDescription: '规划工具帮助您快速创建全面的计划。',
    featureLessonPlanningBenefit1: '创建计划速度快5倍',
    featureLessonPlanningBenefit2: '自动对齐标准',
    featureLessonPlanningBenefit3: '为不同班级重复使用计划',
    featureLessonPlanningBenefit4: '与同事实时共享',
    featureLessonPlanningHowItWorks1: '选择模板或从头开始',
    featureLessonPlanningHowItWorks2: '使用AI建议',
    featureLessonPlanningHowItWorks3: '保存并重复使用',
    featureGradebookTitle: '成绩簿',
    featureGradebookSubtitle: '轻松跟踪学生进度。',
    featureGradebookDescription: '我们的成绩簿直观而强大。',
    featureGradebookBenefit1: '自动计算成绩',
    featureGradebookBenefit2: '可视化进度跟踪',
    featureGradebookBenefit3: '即时生成成绩单',
    featureGradebookBenefit4: 'AI洞察识别困难学生',
    featureGradebookHowItWorks1: '设置您的班级',
    featureGradebookHowItWorks2: '创建作业并输入成绩',
    featureGradebookHowItWorks3: '查看分析和生成报告',
    featureAttendanceTitle: '考勤追踪',
    featureAttendanceSubtitle: '快速管理考勤，无需额外文书工作。',
    featureAttendanceDescription: '告别纸质考勤表。',
    featureAttendanceBenefit1: '30秒内完成点名',
    featureAttendanceBenefit2: '自动检测缺勤模式',
    featureAttendanceBenefit3: '为管理层生成报告',
    featureAttendanceBenefit4: '可选家长通知',
    featureAttendanceHowItWorks1: '打开班级并标记',
    featureAttendanceHowItWorks2: '查看考勤模式',
    featureAttendanceHowItWorks3: '导出或分享',
    featureAIToolsTitle: 'AI教学工具',
    featureAIToolsSubtitle: '在几秒钟内生成想法和材料。',
    featureAIToolsDescription: '利用AI的力量增强您的教学。',
    featureAIToolsBenefit1: '生成适合您学科的想法',
    featureAIToolsBenefit2: '创建差异化材料',
    featureAIToolsBenefit3: '自动构建评估',
    featureAIToolsBenefit4: '无额外AI费用',
    featureAIToolsHowItWorks1: '描述您需要什么',
    featureAIToolsHowItWorks2: 'AI生成选项',
    featureAIToolsHowItWorks3: '编辑并保存',
    featurePresentationsTitle: '演示文稿和资源',
    featurePresentationsSubtitle: '更快地创建材料。',
    featurePresentationsDescription: '在一个地方创建所有内容。',
    featurePresentationsBenefit1: '教育模板',
    featurePresentationsBenefit2: '与课程计划集成',
    featurePresentationsBenefit3: '轻松分享',
    featurePresentationsBenefit4: '导入现有资源',
    featurePresentationsHowItWorks1: '选择模板',
    featurePresentationsHowItWorks2: '添加内容',
    featurePresentationsHowItWorks3: '链接并分享',
    featureWorkflowTitle: '教师友好的工作流程',
    featureWorkflowSubtitle: '为需要实用工具的教育者设计。',
    featureWorkflowDescription: '为教师的实际工作方式而构建。',
    featureWorkflowBenefit1: '直观的界面',
    featureWorkflowBenefit2: '桌面和移动端',
    featureWorkflowBenefit3: '快速设置',
    featureWorkflowBenefit4: '与您的工具集成',
    featureWorkflowHowItWorks1: '注册并设置',
    featureWorkflowHowItWorks2: '从一个仪表板访问所有内容',
    featureWorkflowHowItWorks3: '自动同步',
    featureClassroomTeachersTitle: '为课堂教师',
    featureClassroomTeachersSubtitle: '在一处管理规划和成绩。',
    featureClassroomTeachersDescription: 'TeacherHubPro整合所有工具。',
    featureClassroomTeachersBenefit1: '为多个班级规划',
    featureClassroomTeachersBenefit2: '跟踪成绩和考勤',
    featureClassroomTeachersBenefit3: '为家长生成报告',
    featureClassroomTeachersBenefit4: '随时随地访问',
    featureClassroomTeachersHowItWorks1: '设置您的班级',
    featureClassroomTeachersHowItWorks2: '每天规划和记录',
    featureClassroomTeachersHowItWorks3: '通过门户沟通',
    featureHomeschoolTitle: '为家庭教育',
    featureHomeschoolSubtitle: '创建灵活的学习路径。',
    featureHomeschoolDescription: 'TeacherHubPro帮助创建个性化路径。',
    featureHomeschoolBenefit1: '为每个孩子创建个性化路径',
    featureHomeschoolBenefit2: '有组织的记录',
    featureHomeschoolBenefit3: 'AI生成的想法',
    featureHomeschoolBenefit4: '灵活的时间表',
    featureHomeschoolHowItWorks1: '为每个孩子创建档案',
    featureHomeschoolHowItWorks2: '跟踪进度',
    featureHomeschoolHowItWorks3: '生成报告',
    featureTutorsTitle: '为导师',
    featureTutorsSubtitle: '高效跟踪学生工作。',
    featureTutorsDescription: 'TeacherHubPro帮助您保持有序。',
    featureTutorsBenefit1: '跟踪多个学生',
    featureTutorsBenefit2: '规划会议',
    featureTutorsBenefit3: '为家长提供进度报告',
    featureTutorsBenefit4: '个性化材料',
    featureTutorsHowItWorks1: '添加您的学生',
    featureTutorsHowItWorks2: '规划和跟踪',
    featureTutorsHowItWorks3: '通过门户分享',
    featureSchoolsTitle: '为学校',
    featureSchoolsSubtitle: '为教育者提供一致的平台。',
    featureSchoolsDescription: 'TeacherHubPro随您的学校扩展。',
    featureSchoolsBenefit1: '一致的工具',
    featureSchoolsBenefit2: '共享资源库',
    featureSchoolsBenefit3: '管理员仪表板',
    featureSchoolsBenefit4: '批量价格',
    featureSchoolsHowItWorks1: '联系我们获取价格',
    featureSchoolsHowItWorks2: '入职教师',
    featureSchoolsHowItWorks3: '监控和支持'
  }
};

const LanguageContext = createContext();

export const LanguageProvider = ({ children }) => {
  const [language, setLanguage] = useState(() => {
    // Check URL param first (for SEO/sitemap hreflang links), then localStorage, default to English
    const urlParams = new URLSearchParams(window.location.search);
    const urlLang = urlParams.get('lang');
    if (urlLang && ['en', 'es', 'fr', 'pt', 'de', 'it', 'zh'].includes(urlLang)) {
      return urlLang;
    }
    return localStorage.getItem('teacherhub_language') || 'en';
  });

  useEffect(() => {
    localStorage.setItem('teacherhub_language', language);
    document.documentElement.lang = language;
  }, [language]);

  const t = (key) => {
    return translations[language]?.[key] || translations.en[key] || key;
  };

  const tr = (en, es) => {
    if (language === 'es') return es;
    if (language === 'en') return en;
    return extraTranslations[language]?.[en] || en;
  };

  const toggleLanguage = () => {
    // Cycle through languages
    const codes = availableLanguages.map(l => l.code);
    const currentIndex = codes.indexOf(language);
    const nextIndex = (currentIndex + 1) % codes.length;
    setLanguage(codes[nextIndex]);
  };

  const getCurrentLanguage = () => {
    return availableLanguages.find(l => l.code === language) || availableLanguages[0];
  };

  return (
    <LanguageContext.Provider value={{ 
      language, 
      setLanguage, 
      t, 
      tr,
      toggleLanguage, 
      getCurrentLanguage,
      availableLanguages 
    }}>
      {children}
    </LanguageContext.Provider>
  );
};

export const useLanguage = () => {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error('useLanguage must be used within a LanguageProvider');
  }
  return context;
};
